<template>
  <div class="config-container" :class="{ 'view-mode': isViewMode }">
    <!-- 加载遮罩 -->
    <div v-if="loading" class="loading-overlay">
      <div class="loading-spinner">
        <div class="spinner"></div>
        <p>加载配置中...</p>
      </div>
    </div>
    <!-- Added config name input and save button with Apple-style header -->
    <div class="config-header">
      <div class="header-content">
        <div class="header-left">
          <h2>{{ isEditMode ? '编辑配置 - 尤弥尔传奇国际服' : '创建配置 - 尤弥尔传奇国际服' }}</h2>
          <div class="header-controls">
            <div class="config-name-group">
              <label>配置名称</label>
              <input
                  v-model="configForm.name"
                  type="text"
                  class="config-name-input"
                  placeholder="输入配置名称"
                  :disabled="isEditMode && isDefaultConfig"
                  :title="isEditMode && isDefaultConfig ? '默认配置不允许修改名称' : ''"
              />
            </div>
            <button
                v-if="!isSharedConfig && !isViewMode"
                @click="saveConfig"
                :disabled="saving"
                class="save-btn"
            >
              {{ saving ? '保存中...' : (isEditMode ? '更新配置' : '保存配置') }}
            </button>
            <span v-else-if="isViewMode" class="shared-config-notice">
              查看模式，不允许修改
            </span>
            <span v-else-if="isSharedConfig" class="shared-config-notice">
              这是共享配置，只能查看不能修改
            </span>
            <div class="config-description-group">
              <label>配置描述</label>
              <input
                  v-model="configForm.description"
                  type="text"
                  class="config-description-input"
                  placeholder="输入配置描述（可选）"
              />
            </div>
          </div>
        </div>
        <div class="header-notice">
          <div class="notice-content-compact">
            <p>
              <a
                  href="https://www.yuque.com/liuce1996/yeya/gvrk7rv34x21gdp8?singleDoc#"
                  target="_blank"
                  class="tutorial-text-link"
                  title="查看AutoRun脚本设置教程"
              >
                📖 脚本设置教程
              </a>
            </p>
            <p>• 配置名称创建后不允许改名和删除</p>
            <p>• 每个账号都必须有一个<strong>默认配置</strong>，新创建的账号将自动使用默认配置</p>
          </div>
        </div>
      </div>
    </div>

    <div class="config-content">
      <!-- Tab导航 -->
      <div class="tab-container">
        <div class="tab-nav">
          <button
              class="tab-button"
              :class="{ active: activeTab === 'basic' }"
              @click="activeTab = 'basic'"
          >
            基础配置
          </button>
          <button
              class="tab-button"
              :class="{ active: activeTab === 'items' }"
              @click="activeTab = 'items'"
          >
            道具配置
          </button>
          <button
              class="tab-button"
              :class="{ active: activeTab === 'shout' }"
              @click="activeTab = 'shout'"
          >
            自动喊话
          </button>
          <button
              class="tab-button"
              :class="{ active: activeTab === 'extraFeatures' }"
              @click="activeTab = 'extraFeatures'"
          >
            额外功能
          </button>
        </div>

        <!-- Tab内容 -->
        <div class="tab-content">
          <!-- 基础配置Tab -->
          <div v-if="activeTab === 'basic'" class="tab-pane">
            <!-- Three column layout with all sections in one row -->
            <div class="main-content-grid">
              <!-- First column: Basic settings -->
              <div class="form-section grouped-section">
                <div class="grouped-content">
                  <!-- 角色模式 -->
                  <div class="section-divider">
                    <h4 class="compact-title">角色模式</h4>
                    <div class="compact-note">20级前均为起号模式以下配置不会生效</div>
                    <div class="form-row">
                      <el-radio-group v-model="configContent.characterMode" size="small">
                        <el-radio label="single">单角色模式</el-radio>
                        <el-radio label="roll2">滚2角色挖琥珀</el-radio>
                        <el-radio label="rollAccount">滚账号挖琥珀</el-radio>
                        <el-radio label="placeholder">占坑模式（比单角色模式增加自动创建角色功能）</el-radio>
                      </el-radio-group>
                    </div>
                    <div class="form-row">
                      <label>职业（占坑模式）：</label>
                      <el-select v-model="configContent.createCharacter.profession" size="small" style="width: 120px;">
                        <el-option label="战士" value="战士"></el-option>
                        <el-option label="奶妈" value="奶妈"></el-option>
                        <el-option label="法师" value="法师"></el-option>
                        <el-option label="双枪" value="双枪"></el-option>
                        <el-option label="弓箭" value="弓箭"></el-option>
                      </el-select>
                    </div>
                    <div class="form-row">
                      <label>选区（占坑模式）：</label>
                      <div style="display: flex; align-items: center; gap: 8px;">
                        <el-select
                            v-if="!useManualInput"
                            v-model="configContent.placeholderZone"
                            size="small"
                            style="width: 180px;"
                            filterable
                            placeholder="选择服务器">
                          <!-- 搭档服务器 -->
                          <el-option label="--- 搭档服务器 ---" :value="null" disabled></el-option>
                          <el-option label="HOF1" value="HOF1"></el-option>
                          <el-option label="DaddyKim" value="DaddyKim"></el-option>
                          <el-option label="Kings" value="Kings"></el-option>
                          <el-option label="HOF2" value="HOF2"></el-option>
                          <el-option label="DaddyVerse" value="DaddyVerse"></el-option>
                          <el-option label="xaou" value="xaou"></el-option>
                          <el-option label="ISEKAI" value="ISEKAI"></el-option>
                          <el-option label="Chess" value="Chess"></el-option>
                          <el-option label="PROSGARD" value="PROSGARD"></el-option>
                          <el-option label="ManiTW" value="ManiTW"></el-option>
                          <el-option label="8888" value="8888"></el-option>
                          <el-option label="HOF3" value="HOF3"></el-option>
                          <el-option label="HOF4" value="HOF4"></el-option>
                          <el-option label="HOF5" value="HOF5"></el-option>
                          <el-option label="HOF6" value="HOF6"></el-option>
                          <el-option label="Kings2" value="Kings2"></el-option>
                          <el-option label="Kings3" value="Kings3"></el-option>
                          <el-option label="Kings4" value="Kings4"></el-option>
                          <el-option label="Sky" value="Sky"></el-option>
                          <el-option label="DaddyWorld" value="DaddyWorld"></el-option>
                          <el-option label="MEGA" value="MEGA"></el-option>
                          <el-option label="HERO" value="HERO"></el-option>
                          <el-option label="Bitcoin" value="Bitcoin"></el-option>
                          <el-option label="X" value="X"></el-option>
                          <el-option label="破军" value="破军"></el-option>
                          <el-option label="Tokyo" value="Tokyo"></el-option>
                          <el-option label="Taipei" value="Taipei"></el-option>
                          <el-option label="Bangkok" value="Bangkok"></el-option>

                          <!-- 普通服务器 -->
                          <el-option label="--- 普通服务器 ---" :value="null" disabled></el-option>
                          <el-option label="ASIA014" value="ASIA014"></el-option>
                          <el-option label="ASIA015" value="ASIA015"></el-option>
                          <el-option label="ASIA016" value="ASIA016"></el-option>
                          <el-option label="ASIA017" value="ASIA017"></el-option>
                          <el-option label="ASIA018" value="ASIA018"></el-option>
                          <el-option label="ASIA019" value="ASIA019"></el-option>
                          <el-option label="ASIA020" value="ASIA020"></el-option>
                          <el-option label="ASIA021" value="ASIA021"></el-option>
                          <el-option label="ASIA022" value="ASIA022"></el-option>
                          <el-option label="ASIA023" value="ASIA023"></el-option>
                          <el-option label="ASIA024" value="ASIA024"></el-option>
                          <el-option label="ASIA025" value="ASIA025"></el-option>
                          <el-option label="ASIA026" value="ASIA026"></el-option>
                          <el-option label="ASIA027" value="ASIA027"></el-option>
                          <el-option label="ASIA028" value="ASIA028"></el-option>
                          <el-option label="ASIA029" value="ASIA029"></el-option>
                          <el-option label="ASIA030" value="ASIA030"></el-option>
                          <el-option label="ASIA031" value="ASIA031"></el-option>
                          <el-option label="ASIA032" value="ASIA032"></el-option>
                          <el-option label="ASIA033" value="ASIA033"></el-option>
                          <el-option label="ASIA034" value="ASIA034"></el-option>
                          <el-option label="ASIA035" value="ASIA035"></el-option>
                          <el-option label="ASIA036" value="ASIA036"></el-option>
                          <el-option label="ASIA037" value="ASIA037"></el-option>
                          <el-option label="ASIA038" value="ASIA038"></el-option>
                          <el-option label="ASIA039" value="ASIA039"></el-option>
                          <el-option label="ASIA040" value="ASIA040"></el-option>
                          <el-option label="ASIA041" value="ASIA041"></el-option>
                          <el-option label="ASIA042" value="ASIA042"></el-option>
                          <el-option label="ASIA043" value="ASIA043"></el-option>
                        </el-select>
                        <input
                            v-else
                            v-model="configContent.placeholderZone"
                            type="text"
                            class="simple-text-input"
                            placeholder="手动输入服务器名称"
                        />
                        <el-button
                            size="small"
                            type="text"
                            @click="useManualInput = !useManualInput"
                            style="padding: 0; color: #409eff; font-size: 12px;"
                        >
                          {{ useManualInput ? '选择' : '手动输入' }}
                        </el-button>
                      </div>
                    </div>
                    <div class="form-row">
                      <label>主角色位置：</label>
                      <el-select v-model="configContent.mainCharacterPosition" size="small" style="width: 120px;">
                        <el-option label="第一个" value="第一个"></el-option>
                        <el-option label="第二个" value="第二个"></el-option>
                        <el-option label="第三个" value="第三个"></el-option>
                        <el-option label="第四个" value="第四个"></el-option>
                      </el-select>
                    </div>
                    <div class="form-row">
                      <label>账号大区：</label>
                      <el-select v-model="configContent.accountRegion" size="small" style="width: 120px;">
                        <el-option label="亚服" value="亚服"></el-option>
                        <el-option label="NU欧服" value="NU欧服"></el-option>
                      </el-select>
                    </div>
                    <div class="compact-note" style="margin-top: 8px;">
                      <i class="el-icon-info" style="color: #409EFF; margin-right: 6px;"></i>
                      <span style="color: #666; font-size: 12px;">备注：不同大区世界boss时间有所不同</span>
                    </div>
                  </div>

                  <!-- 游戏更新 -->
                  <div class="section-divider">
                    <h4 class="compact-title">游戏更新</h4>
                    <div class="form-row">
                      <el-select v-model="configContent.gameUpdate" size="small" style="width: 150px;">
                        <el-option label="gametoday" value="gametoday"></el-option>
                        <el-option label="apkpure" value="apkpure"></el-option>
                        <el-option label="谷歌" value="google"></el-option>
                        <el-option label="ACE云手机" value="ace"></el-option>
                      </el-select>
                      <el-checkbox v-model="configContent.autoUpdate" size="small">自动更新</el-checkbox>
                    </div>
                  </div>

                  <!-- 代理软件 -->
                  <div class="section-divider">
                    <h4 class="compact-title">代理软件</h4>
                    <div class="form-row">
                      <el-select v-model="configContent.proxyType" size="small" style="width: 120px;">
                        <el-option label="Kit代理" value="kit"></el-option>
                        <el-option label="nekobox" value="nekobox"></el-option>
                      </el-select>
                      <el-checkbox v-model="configContent.enableProxy" size="small">启用代理</el-checkbox>
                    </div>
                  </div>

                  <!-- 主线区域 -->
                  <div class="section-divider">
                    <h4 class="compact-title">主线区域</h4>
                    <div class="form-row">
                      <el-checkbox v-model="configContent.mainQuest.enabled" size="small">是否主线</el-checkbox>
                    </div>
                    <div class="form-row">
                      <label>一直做到</label>
                      <input
                          type="number"
                          v-model.number="configContent.mainQuest.level"
                          :min="1"
                          :max="100"
                          class="simple-number-input"
                      />
                      <span class="form-label">级</span>
                    </div>
                    <div class="form-row">
                      <label>或做到</label>
                      <input
                          type="number"
                          v-model.number="configContent.mainQuest.chapterMajor"
                          :min="8"
                          :max="50"
                          class="simple-number-input"
                          style="width: 60px;"
                      />
                      <span class="form-label">-</span>
                      <input
                          type="number"
                          v-model.number="configContent.mainQuest.chapterMinor"
                          :min="1"
                          :max="99"
                          class="simple-number-input"
                          style="width: 60px;"
                      />
                      <span class="form-label">章节</span>
                    </div>
                    <div class="form-row">
                      <span class="small-text">死亡</span>
                      <input
                          type="number"
                          v-model.number="configContent.mainQuest.deathLimit"
                          :min="1"
                          :max="20"
                          class="simple-number-input"
                      />
                      <span class="form-label">次后</span>
                      <input
                          type="number"
                          v-model.number="configContent.mainQuest.cooldownHours"
                          :min="1"
                          :max="72"
                          class="simple-number-input"
                      />
                      <span class="form-label">小时内不再进行主线</span>
                    </div>
                    <div class="compact-note">提示：18级之前默认会一直做主线</div>
                  </div>

                  <!-- 每日课题区域 -->
                  <div class="section-divider">
                    <h4 class="compact-title">每日课题区域</h4>
                    <div class="form-row">
                      <el-checkbox v-model="configContent.dailyQuest.enabled" size="small">启用每日课题</el-checkbox>
                    </div>
                    <div class="form-row">
                      <el-checkbox v-model="configContent.dailyQuest.repeatChallenge.enabled" size="small" :disabled="!configContent.dailyQuest.enabled">每日10/5次重复挑战</el-checkbox>
                      <el-select 
                        v-model="configContent.dailyQuest.repeatChallenge.count" 
                        size="small" 
                        style="width: 80px; margin-left: 10px;"
                        :disabled="!configContent.dailyQuest.enabled || !configContent.dailyQuest.repeatChallenge.enabled"
                      >
                        <el-option label="10次" value="10次"></el-option>
                        <el-option label="5次" value="5次"></el-option>
                      </el-select>
                      <el-select 
                        v-model="configContent.dailyQuest.repeatChallenge.type" 
                        size="small" 
                        style="width: 100px; margin-left: 10px;"
                        :disabled="!configContent.dailyQuest.enabled || !configContent.dailyQuest.repeatChallenge.enabled"
                      >
                        <el-option label="野外" value="野外"></el-option>
                        <el-option label="精锐" value="精锐"></el-option>
                        <el-option label="迷宫" value="迷宫"></el-option>
                        <el-option label="峡谷" value="峡谷"></el-option>
                      </el-select>
                    </div>
                    <div class="form-row">
                      <el-checkbox v-model="configContent.dailyQuest.collectionTask" size="small" :disabled="!configContent.dailyQuest.enabled">每日采集任务</el-checkbox>
                    </div>
                  </div>

                  <!-- 故事&成就&黑色女武神 -->
                  <div class="section-divider">
                    <h4 class="compact-title">传奇任务 & 成就</h4>
                    <div style="margin-bottom: 12px;">
                      <div style="font-weight: 600; margin-bottom: 8px; color: #409EFF;">传奇任务</div>
                      <div class="checkbox-grid-2x2">
                        <el-checkbox v-model="configContent.storyAchievement.exchangeTask" size="small">交易所任务（20级）</el-checkbox>
                        <el-checkbox v-model="configContent.storyAchievement.ultimateTask" size="small">大招任务（20级）</el-checkbox>
                        <el-checkbox v-model="configContent.storyAchievement.utgardShadow" size="small">乌坦加德之影（40级）</el-checkbox>
                        <el-checkbox v-model="configContent.storyAchievement.worldTreeDisir" size="small">世界树迪西尔（42级）</el-checkbox>
                      </div>
                    </div>
                    <div>
                      <div style="font-weight: 600; margin-bottom: 8px; color: #409EFF;">成就</div>
                      <div class="checkbox-grid-2x2">
                        <el-checkbox v-model="configContent.storyAchievement.blueBird" size="small">蓝鸟</el-checkbox>
                        <el-checkbox v-model="configContent.storyAchievement.purpleBird" size="small">紫鸟</el-checkbox>
                        <el-checkbox v-model="configContent.storyAchievement.sigrenTrial" size="small">希格露恩试炼</el-checkbox>
                      </div>
                    </div>
                  </div>

                  <!-- 系统设置 -->
                  <div class="section-divider">
                    <h4 class="compact-title">系统设置</h4>
                    <div class="form-row">
                      <el-checkbox v-model="configContent.systemSettings.autoStart" size="small">开机自启动</el-checkbox>
                      <el-checkbox v-model="configContent.systemSettings.ailinSystem" size="small">艾琳系统</el-checkbox>
                      <el-checkbox v-model="configContent.systemSettings.scheduledScreenshot" size="small">定时截屏</el-checkbox>
                    </div>
                    <div class="form-row">
                      <el-checkbox v-model="configContent.systemSettings.randomDelayStart" size="small">随机延时启动</el-checkbox>
                      <el-checkbox v-model="configContent.systemSettings.autoUpdateScript" size="small">自动更新脚本（运行中不会更新，启动会强制更新）</el-checkbox>
                    </div>
                    <div class="form-row">
                      <el-checkbox v-model="configContent.systemSettings.killProxyAndGameOnStart" size="small">启动脚本杀代理和游戏</el-checkbox>
                      <el-checkbox v-model="configContent.systemSettings.killProxyAndGameOnUpdate" size="small">更新脚本杀代理和游戏</el-checkbox>
                    </div>
                    <div class="form-row">
                      <el-checkbox v-model="configContent.systemSettings.openProxyBeforeStart" size="small">每次启动游戏前都开一次代理</el-checkbox>
                    </div>
                    <div class="form-row">
                      <el-checkbox v-model="configContent.systemSettings.android11InputMethod" size="small">安卓11输入法</el-checkbox>
                      <span class="setting-hint" style="margin-left: 8px; color: #909399; font-size: 12px;">解决安卓11无法输出内容的问题</span>
                    </div>
                    <div class="form-row">
                      <el-checkbox v-model="configContent.systemSettings.autoRestartOnIdle" size="small">系统画面长时间不动自动重启游戏</el-checkbox>
                    </div>
                    <div class="form-row">
                      <label class="setting-label">登录选项：</label>
                      <el-radio-group v-model="configContent.systemSettings.loginType" size="small">
                        <el-radio value="苹果">苹果</el-radio>
                        <el-radio value="谷歌">谷歌</el-radio>
                      </el-radio-group>
                    </div>
                    <div class="form-row" v-if="configContent.systemSettings.loginType === '谷歌'">
                      <label class="setting-label">谷歌账号类型：</label>
                      <el-radio-group v-model="configContent.systemSettings.googleAccountType" size="small">
                        <el-radio value="单谷歌号">单谷歌号</el-radio>
                        <el-radio value="双谷歌号1位置">双谷歌号1位置</el-radio>
                        <el-radio value="双谷歌号2位置">双谷歌号2位置</el-radio>
                      </el-radio-group>
                    </div>
                    <div class="form-row">
                      <label class="setting-label">防掉账号缓存</label>
                    </div>
                    <div class="form-row">
                      <el-radio-group v-model="configContent.systemSettings['谷歌登录界面选择']" size="small">
                        <el-radio value="直接登录">直接登录</el-radio>
                        <el-radio value="重启登录">重启登录</el-radio>
                        <el-radio value="停止脚本">停止脚本</el-radio>
                      </el-radio-group>
                    </div>
                    <div class="form-row">
                      <span class="setting-label">连续</span>
                      <input
                          type="number"
                          v-model.number="configContent.systemSettings.gameEnterFailStopThreshold"
                          :min="0"
                          :max="99"
                          class="simple-number-input"
                          style="width: 56px;"
                      />
                      <span class="form-label">次进入游戏不成功后关闭脚本</span>
                      <span class="setting-hint" style="margin-left: 8px; color: #909399; font-size: 12px;">填 0 表示不启用</span>
                    </div>
                    <div class="form-row">
                      <label>读秒倒计时：</label>
                      <input
                          type="number"
                          v-model.number="configContent.systemSettings.countdownSeconds"
                          :min="3"
                          :max="200"
                          class="simple-number-input"
                          style="width: 60px;"
                      />
                      <span class="form-label">秒（3～200秒）</span>
                    </div>
                    <div class="google-login-tip">
                      <div class="tip-content">
                        <span class="tip-icon">💡</span>
                        <span class="tip-text">出现停权的号可以使用停止登录防止掉授权，苹果建议使用重启防止掉授权再次登录</span>
                      </div>
                    </div>
                  </div>

                  <!-- 游戏商城（每天自动购买） - 独立框 -->
                  <div class="section-divider">
                    <h4 class="compact-title">游戏商城（每天自动购买）</h4>
                    <div class="compact-note">提示：小于50万银币时不再购买</div>
                    <div class="shop-rows">
                      <div class="checkbox-row">
                        <el-checkbox v-model="configContent.gameShop.autoBuyMana" size="small">自动商场买蓝</el-checkbox>
                      </div>
                      <div class="checkbox-row">
                        <el-checkbox v-model="configContent.gameShop.weaponStone" size="small">武器强化石</el-checkbox>
                        <el-checkbox v-model="configContent.gameShop.weaponStoneBox" size="small">武器强化石箱子</el-checkbox>
                      </div>
                      <div class="checkbox-row">
                        <el-checkbox v-model="configContent.gameShop.clothesStone" size="small">服饰强化石</el-checkbox>
                        <el-checkbox v-model="configContent.gameShop.clothesStoneBox" size="small">服饰强化石箱子</el-checkbox>
                      </div>
                      <div class="checkbox-row">
                        <el-checkbox v-model="configContent.gameShop.accessoryStone" size="small">饰品强化石</el-checkbox>
                        <el-checkbox v-model="configContent.gameShop.accessoryStoneBox" size="small">饰品强化石箱子</el-checkbox>
                      </div>
                      <div class="checkbox-row">
                        <el-checkbox v-model="configContent.gameShop.hammerStone" size="small">镐锤强化石</el-checkbox>
                        <el-checkbox v-model="configContent.gameShop.hammerStoneBox" size="small">镐锤强化石箱子</el-checkbox>
                      </div>
                      <div class="checkbox-row">
                        <el-checkbox v-model="configContent.gameShop.valkyrieDraw" size="small">瓦尔基里抽卡</el-checkbox>
                        <el-checkbox v-model="configContent.gameShop.dixierDraw" size="small">迪西尔抽卡</el-checkbox>
                      </div>
                    <div class="checkbox-row">
                      <el-checkbox v-model="configContent.gameShop.restPoints" size="small">休息点数</el-checkbox>
                    </div>
                    <div class="checkbox-row" v-if="configContent.gameShop.restPoints">
                      <el-checkbox v-model="configContent.gameShop.restPointItems.amberPocket" size="small">琥珀口袋</el-checkbox>
                      <el-checkbox v-model="configContent.gameShop.restPointItems.sealEnhanceStone" size="small">印章强化石</el-checkbox>
                    </div>
                    <div class="checkbox-row" v-if="configContent.gameShop.restPoints">
                      <el-checkbox v-model="configContent.gameShop.restPointItems.manaRegenPotion" size="small">法力再生药水</el-checkbox>
                      <el-checkbox v-model="configContent.gameShop.restPointItems.enhanceStoneBox" size="small">强化石选择箱子</el-checkbox>
                    </div>
                    </div>
                  </div>

                  <!-- 荣誉商人（每天自动购买） - 独立框 -->
                  <div class="section-divider" style="margin-top: 20px;">
                    <h4 class="compact-title">荣誉商人（每天自动购买）</h4>
                    <div class="compact-note">提示：使用荣誉信物购买</div>
                    <div class="shop-rows">
                      <div class="checkbox-row">
                        <el-checkbox v-model="configContent.gameShop.honorMerchant.enabled" size="small">荣誉商人</el-checkbox>
                      </div>
                      <div class="checkbox-row" v-if="configContent.gameShop.honorMerchant.enabled">
                        <el-checkbox v-model="configContent.gameShop.honorMerchant.heroSoul" size="small">英雄灵魂</el-checkbox>
                        <el-checkbox v-model="configContent.gameShop.honorMerchant.duskStone" size="small">黄昏石</el-checkbox>
                      </div>
                      <div class="checkbox-row" v-if="configContent.gameShop.honorMerchant.enabled">
                        <el-checkbox v-model="configContent.gameShop.honorMerchant.victorySealPack" size="small">胜利印章礼包</el-checkbox>
                        <el-checkbox v-model="configContent.gameShop.honorMerchant.sealEnhanceStone" size="small">印章强化石</el-checkbox>
                      </div>
                      <div class="checkbox-row" v-if="configContent.gameShop.honorMerchant.enabled">
                        <el-checkbox v-model="configContent.gameShop.honorMerchant.gloriousEnhanceBox" size="small">光耀的强化石选择箱子</el-checkbox>
                        <el-checkbox v-model="configContent.gameShop.honorMerchant.darkEnhanceBox" size="small">黑暗强化石选择箱子</el-checkbox>
                      </div>
                      <div class="checkbox-row" v-if="configContent.gameShop.honorMerchant.enabled">
                        <el-checkbox v-model="configContent.gameShop.honorMerchant.patternBox" size="small">图案选择箱子</el-checkbox>
                        <el-checkbox v-model="configContent.gameShop.honorMerchant.guildSkillBookBox" size="small">公会技能书箱子</el-checkbox>
                      </div>
                      <div class="checkbox-row" v-if="configContent.gameShop.honorMerchant.enabled">
                        <el-checkbox v-model="configContent.gameShop.honorMerchant.midMaterialBox" size="small">中级制作材料选择箱子</el-checkbox>
                        <el-checkbox v-model="configContent.gameShop.honorMerchant.lowMaterialBox" size="small">低级制作材料选择箱子</el-checkbox>
                      </div>
                      <div class="checkbox-row" v-if="configContent.gameShop.honorMerchant.enabled">
                        <el-checkbox v-model="configContent.gameShop.honorMerchant.manaRegenPotion" size="small">法力再生药水</el-checkbox>
                        <el-checkbox v-model="configContent.gameShop.honorMerchant.swiftMead" size="small">迅速蜜酒</el-checkbox>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Second column: Combined dungeon area and inventory management -->
              <div class="form-section dungeon-inventory-section">
                <h3 class="section-title">副本区域</h3>

                <!-- 日本-女武神的殿堂西侧 -->
                <div class="dungeon-item">
                  <el-checkbox v-model="configContent.dungeons.valkyrie.enabled" size="small">日本-瓦尔基里殿堂</el-checkbox>
                  <div class="dungeon-settings">
                    <div class="form-row">
                      <label>层数</label>
                      <el-select v-model="configContent.dungeons.valkyrie.floor" size="small" style="width: 120px;">
                        <el-option label="一层-25级" value="1"></el-option>
                        <el-option label="二层-35级" value="2"></el-option>
                        <el-option label="三层-45级" value="3"></el-option>
                        <el-option label="四层-55级" value="4"></el-option>
                        <el-option label="五层-65级" value="5"></el-option>
                      </el-select>
                      <label v-if="configContent.dungeons.valkyrie.floor === '1'">等级</label>
                      <el-select v-if="configContent.dungeons.valkyrie.floor === '1'" v-model="configContent.dungeons.valkyrie.floorLevel" size="small" style="width: 80px;">
                        <el-option label="随便" value="-1"></el-option>
                        <el-option label="25级" value="25"></el-option>
                        <el-option label="27级" value="27"></el-option>
                        <el-option label="30级" value="30"></el-option>
                      </el-select>
                      <label v-if="configContent.dungeons.valkyrie.floor === '2'">等级</label>
                      <el-select v-if="configContent.dungeons.valkyrie.floor === '2'" v-model="configContent.dungeons.valkyrie.floorLevel" size="small" style="width: 80px;">
                        <el-option label="随便" value="-1"></el-option>
                        <el-option label="35级" value="35"></el-option>
                        <el-option label="37级" value="37"></el-option>
                        <el-option label="40级" value="40"></el-option>
                      </el-select>
                      <label v-if="configContent.dungeons.valkyrie.floor === '3'">等级</label>
                      <el-select v-if="configContent.dungeons.valkyrie.floor === '3'" v-model="configContent.dungeons.valkyrie.floorLevel" size="small" style="width: 80px;">
                        <el-option label="随便" value="-1"></el-option>
                        <el-option label="45级" value="45"></el-option>
                        <el-option label="47级" value="47"></el-option>
                        <el-option label="50级" value="50"></el-option>
                      </el-select>
                      <label v-if="configContent.dungeons.valkyrie.floor === '4'">等级</label>
                      <el-select v-if="configContent.dungeons.valkyrie.floor === '4'" v-model="configContent.dungeons.valkyrie.floorLevel" size="small" style="width: 80px;">
                        <el-option label="随便" value="-1"></el-option>
                        <el-option label="55级" value="55"></el-option>
                        <el-option label="57级" value="57"></el-option>
                        <el-option label="60级" value="60"></el-option>
                      </el-select>
                      <label v-if="configContent.dungeons.valkyrie.floor === '5'">等级</label>
                      <el-select v-if="configContent.dungeons.valkyrie.floor === '5'" v-model="configContent.dungeons.valkyrie.floorLevel" size="small" style="width: 80px;">
                        <el-option label="随便" value="-1"></el-option>
                        <el-option label="65级" value="65"></el-option>
                        <el-option label="67级" value="67"></el-option>
                        <el-option label="70级" value="70"></el-option>
                      </el-select>
                      <label>跨服</label>
                      <el-select v-model="configContent.dungeons.valkyrie.difficulty" size="small" style="width: 80px;">
                        <el-option label="普通" value="普通"></el-option>
                        <el-option label="跨服" value="PVP"></el-option>
                      </el-select>
                    </div>
                    <div class="form-row">
                      <label>难度类型</label>
                      <el-select v-model="configContent.dungeons.valkyrie.difficultyType" size="small" style="width: 80px;">
                        <el-option label="随便" value="随便"></el-option>
                        <el-option label="普通" value="普通"></el-option>
                        <el-option label="精锐" value="精锐"></el-option>
                      </el-select>
                      <label>类型</label>
                      <el-select v-model="configContent.dungeons.valkyrie.type" size="small" style="width: 80px;">
                        <el-option label="经验屋" value="经验屋"></el-option>
                        <el-option label="琥珀屋" value="琥珀屋"></el-option>
                        <el-option label="财富屋" value="财富屋"></el-option>
                      </el-select>
                    </div>
                  </div>
                  <div class="form-row" style="margin-top: 10px;">
                    <span class="small-text">失败</span>
                    <input
                        type="number"
                        v-model.number="configContent.dungeons.valkyrie.failLimit"
                        :min="1"
                        :max="20"
                        class="simple-number-input"
                    />
                    <span class="form-label">次后</span>
                    <input
                        type="number"
                        v-model.number="configContent.dungeons.valkyrie.failCooldownHours"
                        :min="1"
                        :max="72"
                        class="simple-number-input"
                    />
                    <span class="form-label">小时内不再执行</span>
                  </div>
                  <!-- 时间设置 -->
                  <div class="time-settings" style="margin-top: 10px;">
                    <el-checkbox v-model="configContent.dungeons.valkyrie.timeLimit.enabled" size="small">指定时间执行（包括开始和结束的小时）</el-checkbox>
                    <div v-if="configContent.dungeons.valkyrie.timeLimit.enabled" class="time-range">
                      <el-select
                          v-model="configContent.dungeons.valkyrie.timeLimit.startTime"
                          placeholder="开始"
                          size="small"
                          style="width: 60px;"
                      >
                        <el-option v-for="hour in 24" :key="hour-1" :label="(hour-1).toString().padStart(2, '0')" :value="(hour-1).toString().padStart(2, '0') + ':00'" />
                      </el-select>
                      <span class="time-separator">至</span>
                      <el-select
                          v-model="configContent.dungeons.valkyrie.timeLimit.endTime"
                          placeholder="结束"
                          size="small"
                          style="width: 60px;"
                      >
                        <el-option v-for="hour in 24" :key="hour-1" :label="(hour-1).toString().padStart(2, '0')" :value="(hour-1).toString().padStart(2, '0') + ':00'" />
                      </el-select>
                    </div>
                  </div>
                </div>

                <!-- 赛季英灵殿 -->
                <div class="dungeon-item seasonal-dungeon-item">
                  <el-checkbox v-model="configContent.dungeons.seasonalValkyrie.enabled" size="small">赛季英灵殿</el-checkbox>
                  <div class="dungeon-settings">
                    <div class="form-row">
                      <label>层数</label>
                      <el-select v-model="configContent.dungeons.seasonalValkyrie.floor" size="small" style="width: 120px;">
                        <el-option label="一层-25级" value="1"></el-option>
                        <el-option label="二层-35级" value="2"></el-option>
                        <el-option label="三层-45级" value="3"></el-option>
                        <el-option label="四层-55级" value="4"></el-option>
                        <el-option label="五层-65级" value="5"></el-option>
                      </el-select>
                    </div>
                    <div class="form-row">
                      <label v-if="configContent.dungeons.seasonalValkyrie.floor === '1'">等级</label>
                      <el-select v-if="configContent.dungeons.seasonalValkyrie.floor === '1'" v-model="configContent.dungeons.seasonalValkyrie.floorLevel" size="small" style="width: 80px;">
                        <el-option label="随便" value="-1"></el-option>
                        <el-option label="25级" value="25"></el-option>
                        <el-option label="27级" value="27"></el-option>
                        <el-option label="30级" value="30"></el-option>
                      </el-select>
                      <label v-if="configContent.dungeons.seasonalValkyrie.floor === '2'">等级</label>
                      <el-select v-if="configContent.dungeons.seasonalValkyrie.floor === '2'" v-model="configContent.dungeons.seasonalValkyrie.floorLevel" size="small" style="width: 80px;">
                        <el-option label="随便" value="-1"></el-option>
                        <el-option label="35级" value="35"></el-option>
                        <el-option label="37级" value="37"></el-option>
                        <el-option label="40级" value="40"></el-option>
                      </el-select>
                      <label v-if="configContent.dungeons.seasonalValkyrie.floor === '3'">等级</label>
                      <el-select v-if="configContent.dungeons.seasonalValkyrie.floor === '3'" v-model="configContent.dungeons.seasonalValkyrie.floorLevel" size="small" style="width: 80px;">
                        <el-option label="随便" value="-1"></el-option>
                        <el-option label="45级" value="45"></el-option>
                        <el-option label="47级" value="47"></el-option>
                        <el-option label="50级" value="50"></el-option>
                      </el-select>
                      <label v-if="configContent.dungeons.seasonalValkyrie.floor === '4'">等级</label>
                      <el-select v-if="configContent.dungeons.seasonalValkyrie.floor === '4'" v-model="configContent.dungeons.seasonalValkyrie.floorLevel" size="small" style="width: 80px;">
                        <el-option label="随便" value="-1"></el-option>
                        <el-option label="55级" value="55"></el-option>
                        <el-option label="57级" value="57"></el-option>
                        <el-option label="60级" value="60"></el-option>
                      </el-select>
                      <label v-if="configContent.dungeons.seasonalValkyrie.floor === '5'">等级</label>
                      <el-select v-if="configContent.dungeons.seasonalValkyrie.floor === '5'" v-model="configContent.dungeons.seasonalValkyrie.floorLevel" size="small" style="width: 80px;">
                        <el-option label="随便" value="-1"></el-option>
                        <el-option label="65级" value="65"></el-option>
                        <el-option label="67级" value="67"></el-option>
                        <el-option label="70级" value="70"></el-option>
                      </el-select>
                      <label>难度类型</label>
                      <el-select v-model="configContent.dungeons.seasonalValkyrie.difficultyType" size="small" style="width: 80px;">
                        <el-option label="随便" value="随便"></el-option>
                        <el-option label="普通" value="普通"></el-option>
                        <el-option label="精锐" value="精锐"></el-option>
                      </el-select>
                      <label>类型</label>
                      <el-select v-model="configContent.dungeons.seasonalValkyrie.type" size="small" style="width: 80px;">
                        <el-option label="成长屋" value="成长屋"></el-option>
                      </el-select>
                    </div>
                  </div>
                  <div class="form-row" style="margin-top: 10px;">
                    <span class="small-text">失败</span>
                    <input
                        type="number"
                        v-model.number="configContent.dungeons.seasonalValkyrie.failLimit"
                        :min="1"
                        :max="20"
                        class="simple-number-input"
                    />
                    <span class="form-label">次后</span>
                    <input
                        type="number"
                        v-model.number="configContent.dungeons.seasonalValkyrie.failCooldownHours"
                        :min="1"
                        :max="72"
                        class="simple-number-input"
                    />
                    <span class="form-label">小时内不再执行</span>
                  </div>
                </div>

                <!-- 周本-西格伦的迷宫西侧 -->
                <div class="dungeon-item">
                  <el-checkbox v-model="configContent.dungeons.sigren.enabled" size="small">周本-混沌神殿</el-checkbox>
                  <div class="dungeon-settings">
                    <label>层数</label>
                    <el-select v-model="configContent.dungeons.sigren.floor" size="small" style="width: 120px;">
                      <el-option label="一层-35级" value="1"></el-option>
                      <el-option label="二层-40级" value="2"></el-option>
                      <el-option label="三层-45级" value="3"></el-option>
                      <el-option label="四层-50级" value="4"></el-option>
                      <el-option label="五层-55级" value="5"></el-option>
                    </el-select>
                    <label>跨服</label>
                    <el-select v-model="configContent.dungeons.sigren.difficulty" size="small" style="width: 80px;">
                      <el-option label="普通" value="普通"></el-option>
                      <el-option label="跨服" value="PVP"></el-option>
                    </el-select>
                  </div>
                  <div class="dungeon-settings" v-if="configContent.dungeons.sigren.enabled && showSigrenPointButton">
                    <label>定点坐标：</label>
                    <el-button
                        type="primary"
                        size="small"
                        @click="openSigrenPointPicker"
                    >定点坐标</el-button>
                    <el-button
                        v-if="hasSigrenCustomPoints"
                        type="danger"
                        size="small"
                        @click="clearAllSigrenPoints"
                    >清除所有点</el-button>
                    <span v-if="hasSigrenCustomPoints" class="small-text">
                      已设{{ configContent.dungeons.sigren.customPoints.length }}个点（最多50个）
                    </span>
                  </div>
                  <div class="form-row" style="margin-top: 10px;">
                    <span class="small-text">失败</span>
                    <input
                        type="number"
                        v-model.number="configContent.dungeons.sigren.failLimit"
                        :min="1"
                        :max="20"
                        class="simple-number-input"
                    />
                    <span class="form-label">次后</span>
                    <input
                        type="number"
                        v-model.number="configContent.dungeons.sigren.failCooldownHours"
                        :min="1"
                        :max="72"
                        class="simple-number-input"
                    />
                    <span class="form-label">小时内不再执行</span>
                  </div>
                  <!-- 时间设置 -->
                  <div class="time-settings" style="margin-top: 10px;">
                    <el-checkbox v-model="configContent.dungeons.sigren.timeLimit.enabled" size="small">指定时间执行</el-checkbox>
                    <div v-if="configContent.dungeons.sigren.timeLimit.enabled" class="time-range" style="margin-top: 8px;">
                      <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
                        <span class="form-label">小时：</span>
                        <el-select
                            v-model="configContent.dungeons.sigren.timeLimit.startTime"
                            placeholder="开始"
                            size="small"
                            style="width: 60px;"
                        >
                          <el-option v-for="hour in 24" :key="hour-1" :label="(hour-1).toString().padStart(2, '0')" :value="(hour-1).toString().padStart(2, '0') + ':00'" />
                        </el-select>
                        <span class="time-separator">至</span>
                        <el-select
                            v-model="configContent.dungeons.sigren.timeLimit.endTime"
                            placeholder="结束"
                            size="small"
                            style="width: 60px;"
                        >
                          <el-option v-for="hour in 24" :key="hour-1" :label="(hour-1).toString().padStart(2, '0')" :value="(hour-1).toString().padStart(2, '0') + ':00'" />
                        </el-select>
                      </div>
                      <div style="display: flex; align-items: center; gap: 8px;">
                        <span class="form-label">周几：</span>
                        <el-checkbox-group v-model="configContent.dungeons.sigren.timeLimit.weekdays" size="small">
                          <el-checkbox :label="1">周一</el-checkbox>
                          <el-checkbox :label="2">周二</el-checkbox>
                          <el-checkbox :label="3">周三</el-checkbox>
                          <el-checkbox :label="4">周四</el-checkbox>
                          <el-checkbox :label="5">周五</el-checkbox>
                          <el-checkbox :label="6">周六</el-checkbox>
                          <el-checkbox :label="7">周日</el-checkbox>
                        </el-checkbox-group>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 周本-波尔克房间西侧 -->
                <div class="dungeon-item">
                  <el-checkbox v-model="configContent.dungeons.polk.enabled" size="small">周本-弗尔克范格</el-checkbox>
                  <div class="dungeon-settings">
                    <label>层数</label>
                    <el-select v-model="configContent.dungeons.polk.floor" size="small" style="width: 120px;">
                      <el-option label="一层-35级" value="1"></el-option>
                      <el-option label="二层-40级" value="2"></el-option>
                      <el-option label="三层-45级" value="3"></el-option>
                      <el-option label="四层-50级" value="4"></el-option>
                      <el-option label="五层-55级" value="5"></el-option>
                    </el-select>
                    <label>跨服</label>
                    <el-select v-model="configContent.dungeons.polk.difficulty" size="small" style="width: 80px;">
                      <el-option label="普通" value="普通"></el-option>
                      <el-option label="跨服" value="PVP"></el-option>
                    </el-select>
                    <label>类型</label>
                    <el-select v-model="configContent.dungeons.polk.type" size="small" style="width: 100px;">
                      <el-option label="刷怪" value="刷怪"></el-option>
                      <el-option label="琥珀屋" value="全图挖琥珀"></el-option>
                    </el-select>
                  </div>
                  <div class="dungeon-settings" v-if="configContent.dungeons.polk.enabled && showPolkPointButton">
                    <label>定点坐标：</label>
                    <el-button
                        type="primary"
                        size="small"
                        @click="openPolkPointPicker"
                    >定点坐标</el-button>
                    <el-button
                        v-if="hasPolkCustomPoints"
                        type="danger"
                        size="small"
                        @click="clearAllPolkPoints"
                    >清除所有点</el-button>
                    <span v-if="hasPolkCustomPoints" class="small-text">
                      已设{{ configContent.dungeons.polk.customPoints.length }}个点（最多50个）
                    </span>
                  </div>
                  <div class="form-row" style="margin-top: 10px;">
                    <span class="small-text">失败</span>
                    <input
                        type="number"
                        v-model.number="configContent.dungeons.polk.failLimit"
                        :min="1"
                        :max="20"
                        class="simple-number-input"
                    />
                    <span class="form-label">次后</span>
                    <input
                        type="number"
                        v-model.number="configContent.dungeons.polk.failCooldownHours"
                        :min="1"
                        :max="72"
                        class="simple-number-input"
                    />
                    <span class="form-label">小时内不再执行</span>
                  </div>
                  <!-- 时间设置 -->
                  <div class="time-settings" style="margin-top: 10px;">
                    <el-checkbox v-model="configContent.dungeons.polk.timeLimit.enabled" size="small">指定时间执行</el-checkbox>
                    <div v-if="configContent.dungeons.polk.timeLimit.enabled" class="time-range" style="margin-top: 8px;">
                      <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
                        <span class="form-label">小时：</span>
                        <el-select
                            v-model="configContent.dungeons.polk.timeLimit.startTime"
                            placeholder="开始"
                            size="small"
                            style="width: 60px;"
                        >
                          <el-option v-for="hour in 24" :key="hour-1" :label="(hour-1).toString().padStart(2, '0')" :value="(hour-1).toString().padStart(2, '0') + ':00'" />
                        </el-select>
                        <span class="time-separator">至</span>
                        <el-select
                            v-model="configContent.dungeons.polk.timeLimit.endTime"
                            placeholder="结束"
                            size="small"
                            style="width: 60px;"
                        >
                          <el-option v-for="hour in 24" :key="hour-1" :label="(hour-1).toString().padStart(2, '0')" :value="(hour-1).toString().padStart(2, '0') + ':00'" />
                        </el-select>
                      </div>
                      <div style="display: flex; align-items: center; gap: 8px;">
                        <span class="form-label">周几：</span>
                        <el-checkbox-group v-model="configContent.dungeons.polk.timeLimit.weekdays" size="small">
                          <el-checkbox :label="1">周一</el-checkbox>
                          <el-checkbox :label="2">周二</el-checkbox>
                          <el-checkbox :label="3">周三</el-checkbox>
                          <el-checkbox :label="4">周四</el-checkbox>
                          <el-checkbox :label="5">周五</el-checkbox>
                          <el-checkbox :label="6">周六</el-checkbox>
                          <el-checkbox :label="7">周日</el-checkbox>
                        </el-checkbox-group>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 黄金殿堂 -->
                <div class="dungeon-item" style="background: #fff9e6; padding: 15px; border-radius: 8px; margin-top: 15px;">
                  <el-checkbox v-model="configContent.dungeons.goldenHall.enabled" size="small">黄金殿堂</el-checkbox>
                  <div class="dungeon-settings">
                    <label>层数</label>
                    <el-select v-model="configContent.dungeons.goldenHall.floor" size="small" style="width: 120px;">
                      <el-option label="1层" value="1"></el-option>
                      <el-option label="2层" value="2"></el-option>
                      <el-option label="3层" value="3"></el-option>
                    </el-select>
                    <label>跨服</label>
                    <el-select v-model="configContent.dungeons.goldenHall.difficulty" size="small" style="width: 80px;">
                      <el-option label="普通" value="普通"></el-option>
                      <el-option label="跨服" value="PVP"></el-option>
                    </el-select>
                  </div>
                  <div class="dungeon-settings" v-if="configContent.dungeons.goldenHall.enabled && showGoldenHallPointButton">
                    <label>定点坐标：</label>
                    <el-button
                        type="primary"
                        size="small"
                        @click="openGoldenHallPointPicker"
                    >定点坐标</el-button>
                    <el-button
                        v-if="hasGoldenHallCustomPoints"
                        type="danger"
                        size="small"
                        @click="clearAllGoldenHallPoints"
                    >清除所有点</el-button>
                    <span v-if="hasGoldenHallCustomPoints" class="small-text">
                      已设{{ configContent.dungeons.goldenHall.customPoints.length }}个点（最多50个）
                    </span>
                  </div>
                </div>

                <!-- 其他设置移到这里 -->
                <div class="inventory-section">
                  <h3 class="section-title">其他设置</h3>
                  <div class="form-row">
                    <el-checkbox v-model="configContent.otherRequirements.commission" size="small">委托任务</el-checkbox>
                    <el-checkbox v-model="configContent.otherRequirements.sideQuest" size="small">挑战任务</el-checkbox>
                  </div>
                  <div class="form-row">
                    <span class="small-text">委托死亡</span>
                    <input
                        type="number"
                        v-model.number="configContent.otherRequirements.commissionDeathLimit"
                        :min="1"
                        :max="20"
                        class="simple-number-input"
                    />
                    <span class="form-label">次后</span>
                    <input
                        type="number"
                        v-model.number="configContent.otherRequirements.commissionCooldownHours"
                        :min="1"
                        :max="72"
                        class="simple-number-input"
                    />
                    <span class="form-label">小时内不再进行委托</span>
                  </div>
                  <div class="form-row">
                    <span class="small-text">挑战死亡</span>
                    <input
                        type="number"
                        v-model.number="configContent.otherRequirements.sideQuestDeathLimit"
                        :min="1"
                        :max="20"
                        class="simple-number-input"
                    />
                    <span class="form-label">次后</span>
                    <input
                        type="number"
                        v-model.number="configContent.otherRequirements.sideQuestCooldownHours"
                        :min="1"
                        :max="72"
                        class="simple-number-input"
                    />
                    <span class="form-label">小时内不再进行挑战</span>
                  </div>

                  <!-- 自动分解装备 - 单独放在最上面 -->
                  <div class="decompose-section">
                    <el-checkbox v-model="configContent.otherRequirements.autoDecomposeEquip" size="small">自动分解装备</el-checkbox>
                    <!-- 分解装备选项 -->
                    <div class="decompose-options">
                      <el-checkbox v-model="configContent.otherRequirements.decomposeWhite" size="small" :disabled="!configContent.otherRequirements.autoDecomposeEquip">白</el-checkbox>
                      <el-checkbox v-model="configContent.otherRequirements.decomposeGreen" size="small" :disabled="!configContent.otherRequirements.autoDecomposeEquip">绿</el-checkbox>
                      <el-checkbox v-model="configContent.otherRequirements.decomposeBlue" size="small" :disabled="!configContent.otherRequirements.autoDecomposeEquip">蓝</el-checkbox>
                      <el-checkbox v-model="configContent.otherRequirements.decomposeEnhanced" size="small" :disabled="!configContent.otherRequirements.autoDecomposeEquip">强化</el-checkbox>
                      <el-checkbox v-model="configContent.otherRequirements.decomposeIdentified" size="small" :disabled="!configContent.otherRequirements.autoDecomposeEquip">鉴定</el-checkbox>
                      <el-checkbox v-model="configContent.otherRequirements.decomposeSkillBook" size="small" :disabled="!configContent.otherRequirements.autoDecomposeEquip">技能书</el-checkbox>
                    </div>
                  </div>

                  <div class="checkbox-grid-aligned">
                    <el-checkbox v-model="configContent.otherRequirements.autoValkyrieCompose" size="small">自动女武神合成</el-checkbox>
                    <el-checkbox v-model="configContent.otherRequirements.autoPetCompose" size="small">自动宠物合成</el-checkbox>
                    <el-checkbox v-model="configContent.otherRequirements.autoEquipValkyrie" size="small">自动穿戴女武神卡</el-checkbox>
                    <el-checkbox v-model="configContent.otherRequirements.autoEquipPet" size="small">自动穿戴宠物</el-checkbox>
                    <el-checkbox v-model="configContent.otherRequirements.autoEquipBox" size="small">自动开装备箱子</el-checkbox>
                    <el-checkbox v-model="configContent.otherRequirements.autoWarehouse" size="small">自动仓库</el-checkbox>
                    <el-checkbox v-model="configContent.otherRequirements.warehouseInfiniteCleanup" size="small">空间不足无限清理</el-checkbox>
                    <el-checkbox v-model="configContent.otherRequirements.warehouseRedScreenAlert" size="small">空间不足红屏提醒</el-checkbox>
                    <el-checkbox v-model="configContent.otherRequirements.autoUltimate" size="small">自动释放大招</el-checkbox>
                    <el-checkbox v-model="configContent.otherRequirements.deathRecoverExp" size="small">死亡恢复经验</el-checkbox>
                  </div>

                  <div class="form-row" style="margin-top: 12px;">
                    <el-checkbox v-model="configContent.otherRequirements.autoAmberBox" size="small">自动琥珀封箱</el-checkbox>
                    <el-checkbox v-model="configContent.otherRequirements.amberBoxPriceThresholdEnabled" size="small">箱子价格少于</el-checkbox>
                    <input
                        type="number"
                        v-model.number="configContent.otherRequirements.amberBoxPriceThreshold"
                        :min="1"
                        :max="9999"
                        class="simple-number-input"
                        style="width: 80px;"
                        :disabled="!configContent.otherRequirements.amberBoxPriceThresholdEnabled"
                    />
                    <span class="form-label">钻不封箱</span>
                  </div>

                  <div class="form-row" style="margin-top: 12px;">
                    <el-checkbox v-model="configContent.otherRequirements.silverRestoreEnabled" size="small">银币恢复经验</el-checkbox>
                    <span class="form-label" v-if="configContent.otherRequirements.silverRestoreEnabled">少于</span>
                    <input
                      v-if="configContent.otherRequirements.silverRestoreEnabled"
                      type="number"
                      v-model.number="configContent.otherRequirements.silverRestore"
                      :min="0"
                      :max="999999999"
                      class="simple-number-input"
                      style="width: 120px;"
                    />
                    <span class="form-label" v-if="configContent.otherRequirements.silverRestoreEnabled">银币进行经验恢复</span>
                  </div>

                  <!-- 兑换码设置 -->
                  <div class="form-row" style="margin-top: 16px; flex-direction: column; align-items: flex-start;">
                    <div style="margin-bottom: 8px;">
                      <el-checkbox v-model="configContent.otherRequirements.redeemCodeEnabled" size="small">兑换码</el-checkbox>
                      <el-checkbox v-model="configContent.otherRequirements.useBuiltInRedeemCodes" size="small" style="margin-left: 16px;">脚本内置兑换码</el-checkbox>
                    </div>
                    <div v-if="configContent.otherRequirements.redeemCodeEnabled && !configContent.otherRequirements.useBuiltInRedeemCodes" style="width: 100%; margin-top: 8px;">
                      <el-input
                        v-model="configContent.otherRequirements.redeemCodes"
                        type="textarea"
                        :rows="4"
                        placeholder="请输入兑换码，一行一个 例如：&#10;800800TAIWAN&#10;1K1KTWYMIR"
                        style="width: 100%;"
                      />
                      <div style="color: #909399; font-size: 12px; margin-top: 4px;">提示：一行一个兑换码</div>
                    </div>
                  </div>
                  <div class="form-row" style="margin-top: 12px;">
                    <el-checkbox v-model="configContent.otherRequirements.blessingTower" size="small">祝福之塔</el-checkbox>
                  </div>
                  <div class="form-row" style="margin-top: 8px;">
                    <el-checkbox v-model="configContent.otherRequirements.autoClaimMail" size="small">自动领邮件</el-checkbox>
                  </div>

                </div>

                <!-- 战力提升区域 -->
                <div class="inventory-section">
                  <h3 class="section-title">战力提升区域</h3>
                  <div class="form-row">
                    <el-checkbox v-model="configContent.otherRequirements.autoEnhance" size="small">自动强化装备</el-checkbox>
                    <el-checkbox v-model="configContent.otherRequirements.dixierUpgrade" size="small">迪西尔升级</el-checkbox>
                    <el-checkbox v-model="configContent.otherRequirements.valkyrieUpgrade" size="small">瓦尔基里升级</el-checkbox>
                    <el-checkbox v-model="configContent.otherRequirements.autoSkillUpgrade" size="small">自动技能升级</el-checkbox>
                    <el-checkbox v-model="configContent.otherRequirements.autoArtifact" size="small">自动神器</el-checkbox>
                    <el-checkbox v-model="configContent.otherRequirements.autoSmartCraftBlue" size="small">自动智能制作蓝装</el-checkbox>
                    <el-checkbox v-model="configContent.otherRequirements.autoEquipAdvanced" size="small">自动穿戴高级装备</el-checkbox>
                  </div>
                  <div class="form-row" style="margin-top: 12px;">
                    <el-checkbox v-model="configContent.otherRequirements.autoBuySkill" size="small">自动购买技能</el-checkbox>
                    <span class="form-label" style="margin-left: 10px;">仅购买小于</span>
                    <input
                        type="number"
                        v-model.number="configContent.otherRequirements.autoBuySkillMaxSilver"
                        :min="0"
                        :max="100000000"
                        class="simple-number-input"
                        style="width: 120px;"
                    />
                    <span class="form-label">银币以内的技能</span>
                  </div>
                  <div class="form-row" style="margin-top: 12px;">
                    <el-checkbox v-model="configContent.otherRequirements.autoIdentifyEquipment" size="small">自动鉴定装备</el-checkbox>
                    <el-radio-group v-model="configContent.otherRequirements.identifyEquipmentType" size="small" style="margin-left: 10px;">
                      <el-radio label="法术系">法术系</el-radio>
                      <el-radio label="物理系">物理系</el-radio>
                    </el-radio-group>
                    <span class="form-label" style="margin-left: 10px;">鉴定装备级别：</span>
                    <el-select
                      v-model="configContent.otherRequirements.identifyEquipmentLevel"
                      size="small"
                      style="width: 100px; margin-left: 10px;"
                    >
                      <el-option label="随机" value="随机"></el-option>
                      <el-option label="低级" value="低级"></el-option>
                      <el-option label="中级" value="中级"></el-option>
                      <el-option label="上级" value="上级"></el-option>
                    </el-select>
                  </div>
                  <div class="form-row" style="margin-top: 12px;">
                    <el-checkbox v-model="configContent.otherRequirements.potentialUpgradeEnabled" size="small">潜能升级</el-checkbox>
                    <el-select
                      v-model="configContent.otherRequirements.potentialUpgradeSkill"
                      size="small"
                      style="width: 250px; margin-left: 10px;"
                    >
                      <el-option label="托尔怒气(升级物理攻击力)" :value="1"></el-option>
                      <el-option label="洛基机敏(升级命中闪避)" :value="2"></el-option>
                      <el-option label="奥丁洞察(升级魔法攻击力)" :value="3"></el-option>
                      <el-option label="提尔信念(升级物理魔法防御)" :value="4"></el-option>
                      <el-option label="伊登教徒(升级体力值恢复)" :value="5"></el-option>
                      <el-option label="密米尔智慧(升级魔法力恢复) 推荐👍" :value="6"></el-option>
                    </el-select>
                  </div>
                  <div class="form-row" style="margin-top: 12px;">
                    <el-checkbox v-model="configContent.otherRequirements.storyCardUpgradeEnabled" size="small">剧情卡组提升</el-checkbox>
                    <el-radio-group
                      v-if="configContent.otherRequirements.storyCardUpgradeEnabled"
                      v-model="configContent.otherRequirements.storyCardUpgradeLevel"
                      size="small"
                      style="margin-left: 10px;"
                    >
                      <el-radio :label="1">高级:绿</el-radio>
                      <el-radio :label="2">稀有:蓝 <span style="display: inline-block; background: linear-gradient(135deg, #ff6b6b, #ff8e53); color: #fff; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: bold; margin-left: 4px;">推荐</span></el-radio>
                      <el-radio :label="3">英雄:红</el-radio>
                      <el-radio :label="4">传说:金</el-radio>
                    </el-radio-group>
                  </div>
                  <div class="form-row" style="margin-top: 12px;">
                    <el-checkbox v-model="configContent.otherRequirements.seasonInstruction" size="small">赛季指令</el-checkbox>
                  </div>
                </div>

                <!-- 收藏区域 -->
                <div class="inventory-section">
                  <h3 class="section-title">收藏区域</h3>
                  <div class="form-row">
                    <el-checkbox v-model="configContent.collection.enabled" size="small">收藏开关</el-checkbox>
                  </div>

                  <div class="form-row">
                    <span class="form-label">只收藏</span>
                    <input
                        type="number"
                        v-model.number="configContent.collection.maxPrice"
                        :min="0.01"
                        :max="99.99"
                        :step="0.01"
                        class="simple-number-input"
                        style="width: 70px;"
                    />
                    <span class="form-label">钻以内道具</span>
                  </div>

                  <div class="form-row">
                    <span class="form-label">装备品质：</span>
                    <el-checkbox v-model="configContent.collection.equipmentQuality.white" size="small">白</el-checkbox>
                    <el-checkbox v-model="configContent.collection.equipmentQuality.green" size="small">绿</el-checkbox>
                    <el-checkbox v-model="configContent.collection.equipmentQuality.blue" size="small">蓝</el-checkbox>
                  </div>

                  <!-- 普通交易所 -->
                  <div class="inventory-section">
                    <h3 class="section-title">普通交易所</h3>
                    <div class="form-row">
                      <el-checkbox v-model="configContent.tradingPost.autoSell" size="small">自动出售物品</el-checkbox>
                      <div class="night-mode-notice" style="margin-left: 12px;">
                        <span class="notice-text">提示：默认上架总价值最贵的五个道具</span>
                      </div>
                    </div>
                    <div class="form-row">
                      <el-checkbox v-model="configContent.tradingPost.enablePriceDifference" size="small">比当前最低价少</el-checkbox>
                      <input
                          type="number"
                          v-model.number="configContent.tradingPost.priceDifference"
                          :min="-50"
                          :max="50"
                          class="simple-number-input"
                      />
                      <span class="form-label">钻上架</span>
                      <div class="night-mode-notice" style="margin-left: 12px;">
                        <span class="notice-text">提示：可以填负数就是加价上架</span>
                      </div>
                    </div>
                    <div class="form-row">
                      <span class="form-label">下架超过</span>
                      <input
                          type="number"
                          v-model.number="configContent.tradingPost.delistHours"
                          :min="1"
                          :max="48"
                          class="simple-number-input"
                          style="width: 50px;"
                      />
                      <span class="form-label">小时的物品</span>
                    </div>
                    <!-- 价格保护 -->
                    <div class="form-row" style="flex-direction: column; align-items: flex-start; margin-top: 15px;">
                      <label class="form-label" style="margin-bottom: 10px; font-weight: bold; display: flex; align-items: center; gap: 8px;">
                        <span style="color: #409EFF; font-size: 16px;">🛡️</span>
                        <el-tooltip
                          effect="dark"
                          placement="top"
                          :content="'低于保护价格时，按设置的最低价卖。例如：琥珀箱子当前最低价是110，设置的最低价是130，会按130去挂（取两者最大值）。道具名称需完全一致才能生效'"
                        >
                          <span style="color: #409EFF; cursor: help;">价格保护</span>
                        </el-tooltip>
                      </label>
                      <div style="width: 100%;">
                        <div
                            v-for="(item, index) in configContent.tradingPost.priceProtection"
                            :key="index"
                            class="form-row"
                            style="margin-bottom: 8px; align-items: center;"
                        >
                          <input
                              type="text"
                              v-model="item.itemName"
                              placeholder="道具名称"
                              class="simple-text-input"
                              style="width: 150px; margin-right: 10px;"
                          />
                          <span class="form-label" style="margin: 0 5px;">最低价格：</span>
                          <input
                              type="number"
                              v-model.number="item.minPrice"
                              :min="1"
                              :max="10000"
                              class="simple-number-input"
                              style="width: 100px; margin-right: 10px;"
                          />
                          <span class="form-label" style="margin: 0 5px;">钻石</span>
                          <el-button
                              type="danger"
                              size="small"
                              @click="removePriceProtection(index)"
                              :disabled="item.itemName === '琥珀箱子：50,000'"
                              style="margin-left: 10px;"
                          >
                            删除
                          </el-button>
                        </div>
                        <el-button
                            type="primary"
                            size="small"
                            @click="addPriceProtection"
                            style="margin-top: 5px;"
                        >
                          添加道具
                        </el-button>
                      </div>
                    </div>
                    <!-- 强化可交易装备 -->
                    <div class="form-row" style="flex-direction: column; align-items: flex-start; margin-top: 15px;">
                      <label class="form-label" style="margin-bottom: 10px; font-weight: bold; display: flex; align-items: center; gap: 8px;">
                        <span style="color: #E6A23C; font-size: 16px;">⚡</span>
                        <span style="color: #E6A23C;">强化可交易装备</span>
                      </label>
                      <div style="width: 100%;">
                        <div class="form-row" style="margin-bottom: 10px;">
                          <el-checkbox v-model="configContent.tradingPost.equipmentEnhancement.autoOpenStoneBox" size="small">是否智能开强化石箱子</el-checkbox>
                          <div class="night-mode-notice" style="margin-left: 12px;">
                            <span class="notice-text">备注：开石头箱子不会增加负重</span>
                          </div>
                        </div>
                        <div v-if="configContent.tradingPost.equipmentEnhancement.autoOpenStoneBox" style="margin-left: 20px; margin-top: 8px; margin-bottom: 10px;">
                          <div class="form-row" style="flex-wrap: wrap; gap: 15px;">
                            <el-checkbox v-model="configContent.tradingPost.equipmentEnhancement.weaponStone" size="small">武器强化石</el-checkbox>
                            <el-checkbox v-model="configContent.tradingPost.equipmentEnhancement.armorStone" size="small">防具强化石</el-checkbox>
                            <el-checkbox v-model="configContent.tradingPost.equipmentEnhancement.accessoryStone" size="small">饰品强化石</el-checkbox>
                            <el-checkbox v-model="configContent.tradingPost.equipmentEnhancement.lifeEquipmentStone" size="small">生活装备强化石</el-checkbox>
                          </div>
                        </div>
                        <div class="form-row" style="margin-bottom: 10px;">
                          <el-checkbox v-model="configContent.tradingPost.equipmentEnhancement.enabled" size="small">是否开启强化装备</el-checkbox>
                          <div class="night-mode-notice" style="margin-left: 12px;">
                            <span class="notice-text">提示：推荐+6的装备即可</span>
                          </div>
                        </div>
                        <div v-if="configContent.tradingPost.equipmentEnhancement.enabled" style="margin-left: 20px;">
                          <div class="form-row" style="margin-bottom: 10px; align-items: center;">
                            <span class="form-label" style="margin-right: 10px;">装备品质:</span>
                            <el-checkbox v-model="configContent.tradingPost.equipmentEnhancement.equipmentQuality.white.enabled" size="small" style="margin-right: 10px;">白</el-checkbox>
                            <el-select
                                v-if="configContent.tradingPost.equipmentEnhancement.equipmentQuality.white.enabled"
                                v-model.number="configContent.tradingPost.equipmentEnhancement.equipmentQuality.white.level"
                                size="small"
                                style="width: 100px; margin-right: 15px;"
                            >
                              <el-option
                                  v-for="n in 12"
                                  :key="n"
                                  :label="`+${n}`"
                                  :value="n"
                              ></el-option>
                            </el-select>
                            <el-checkbox v-model="configContent.tradingPost.equipmentEnhancement.equipmentQuality.green.enabled" size="small" style="margin-right: 10px;">绿</el-checkbox>
                            <el-select
                                v-if="configContent.tradingPost.equipmentEnhancement.equipmentQuality.green.enabled"
                                v-model.number="configContent.tradingPost.equipmentEnhancement.equipmentQuality.green.level"
                                size="small"
                                style="width: 100px;"
                            >
                              <el-option
                                  v-for="n in 12"
                                  :key="n"
                                  :label="`+${n}`"
                                  :value="n"
                              ></el-option>
                            </el-select>
                          </div>
                          <div class="form-row" style="margin-top: 10px; align-items: center;">
                            <span class="form-label" style="margin-right: 10px;">强化装备默认个数:</span>
                            <input
                                type="number"
                                v-model.number="configContent.tradingPost.equipmentEnhancement.defaultCount"
                                :min="1"
                                :max="999"
                                class="simple-number-input"
                                style="width: 80px;"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    <!-- 装备自定义价格 -->
                    <div class="form-row" style="flex-direction: column; align-items: flex-start; margin-top: 15px;">
                      <div class="form-row" style="margin-bottom: 10px; align-items: center;">
                        <label class="form-label" style="margin-right: 10px; font-weight: bold; display: flex; align-items: center; gap: 8px;">
                          <span style="color: #67C23A; font-size: 16px;">💰</span>
                          <span style="color: #67C23A;">装备自定义价格</span>
                        </label>
                        <el-checkbox v-model="configContent.tradingPost.equipmentCustomPricesEnabled" size="small">是否上架自定义装备价格</el-checkbox>
                      </div>
                      <div v-if="configContent.tradingPost.equipmentCustomPricesEnabled" style="width: 100%;">
                        <div
                            v-for="(priceItem, index) in configContent.tradingPost.equipmentCustomPrices"
                            :key="index"
                            class="form-row"
                            style="margin-bottom: 10px; align-items: center; padding: 10px; background: #f5f7fa; border-radius: 6px;"
                        >
                          <span class="form-label" style="margin-right: 10px; font-weight: 500;">+</span>
                          <input
                              type="number"
                              v-model.number="priceItem.level"
                              :min="1"
                              :max="20"
                              class="simple-number-input"
                              :style="`width: 70px; margin-right: 15px; ${checkLevelDuplicate(priceItem.level, index) ? 'border-color: #f56c6c;' : ''}`"
                              placeholder="等级"
                              @blur="validateEquipmentLevel(index)"
                          />
                          <span v-if="checkLevelDuplicate(priceItem.level, index)" style="color: #f56c6c; font-size: 12px; margin-right: 10px;">等级重复</span>
                          <span class="form-label" style="margin-right: 10px;">白装价格:</span>
                          <input
                              type="number"
                              v-model.number="priceItem.whitePrice"
                              :min="0"
                              class="simple-number-input"
                              style="width: 100px; margin-right: 15px;"
                              placeholder="钻石"
                          />
                          <span class="form-label" style="margin-right: 10px;">绿装价格:</span>
                          <input
                              type="number"
                              v-model.number="priceItem.greenPrice"
                              :min="0"
                              class="simple-number-input"
                              style="width: 100px; margin-right: 15px;"
                              placeholder="钻石"
                          />
                          <el-button
                              type="danger"
                              size="small"
                              @click="removeEquipmentCustomPrice(index)"
                              style="margin-left: 10px;"
                          >
                            删除
                          </el-button>
                        </div>
                        <el-button
                            type="primary"
                            size="small"
                            @click="addEquipmentCustomPrice"
                            style="margin-top: 5px;"
                        >
                          添加装备等级
                        </el-button>
                      </div>
                    </div>
                  </div>

                  <!-- 区域交易所 -->
                  <div class="inventory-section">
                    <h3 class="section-title">区域交易所</h3>
                    <div class="form-row">
                      <el-checkbox v-model="configContent.regionalExchange.autoSell" size="small">自动出售物品</el-checkbox>
                    </div>
                    <div class="form-row">
                      <el-checkbox v-model="configContent.regionalExchange.exchangeTypeEnabled" size="small">交易类型</el-checkbox>
                      <el-radio-group v-model="configContent.regionalExchange.exchangeType" size="small" style="margin-left: 20px;">
                        <el-radio label="钻石">钻石</el-radio>
                        <el-radio label="WEMIX">WEMIX</el-radio>
                      </el-radio-group>
                    </div>
                    <div class="form-row" style="margin-top: 8px;">
                      <span class="form-label">上架类型：</span>
                      <el-checkbox v-model="configContent.regionalExchange.itemType" size="small">普通道具</el-checkbox>
                      <el-checkbox v-model="configContent.regionalExchange.equipment" size="small" style="margin-left: 20px;">装备</el-checkbox>
                    </div>
                    <div class="form-row">
                      <el-checkbox v-model="configContent.regionalExchange.enablePriceDifference" size="small">比当前最低价少</el-checkbox>
                      <input
                          type="number"
                          v-model.number="configContent.regionalExchange.priceDifference"
                          :min="-50"
                          :max="50"
                          class="simple-number-input"
                      />
                      <span class="form-label">钻上架</span>
                      <div class="night-mode-notice" style="margin-left: 12px;">
                        <span class="notice-text">提示：可以填负数就是加价上架</span>
                      </div>
                    </div>
                    <div class="form-row">
                      <span class="form-label">下架超过</span>
                      <input
                          type="number"
                          v-model.number="configContent.regionalExchange.delistHours"
                          :min="1"
                          :max="48"
                          class="simple-number-input"
                          style="width: 50px;"
                      />
                      <span class="form-label">小时的物品</span>
                    </div>
                  </div>

                  <!-- 制作 -->
                  <div class="inventory-section">
                    <h3 class="section-title">制作</h3>
                    <div class="form-row">
                      <el-checkbox v-model="configContent.activityCraft.synthesizeBlue" size="small">合成蓝色材料</el-checkbox>
                      <el-checkbox v-model="configContent.activityCraft.antiqueCraft" size="small">古董制作</el-checkbox>
                      <el-checkbox v-model="configContent.activityCraft.scrollCraft" size="small">制作卷轴</el-checkbox>
                      <el-checkbox v-model="configContent.activityCraft.foodCraft" size="small">制作食物</el-checkbox>
                    </div>
                    <div class="form-row" style="margin-left: 20px; padding: 8px; background-color: #f5f7fa; border-radius: 4px; border-left: 3px solid #409EFF;">
                      <el-checkbox v-model="configContent.activityCraft.nornTreasure" size="small">诺恩宝物</el-checkbox>
                      <el-checkbox v-model="configContent.activityCraft.useTradableTreasure" size="small">使用可交易宝物</el-checkbox>
                      <el-checkbox v-model="configContent.activityCraft.tradableTreasureNormal" size="small" :disabled="!configContent.activityCraft.useTradableTreasure">普通</el-checkbox>
                      <el-checkbox v-model="configContent.activityCraft.tradableTreasureAdvanced" size="small" :disabled="!configContent.activityCraft.useTradableTreasure">高级</el-checkbox>
                    </div>
                  </div>

                  <!-- 活动制作选择 -->
                  <div class="inventory-section">
                    <div class="section-header">
                      <h3 class="section-title">活动制作选择</h3>
                      <el-switch
                        v-model="configContent.activityCraft.enabled"
                        @change="onActivityCraftEnabledChange"
                        size="small"
                        inline-prompt
                        active-text="开"
                        inactive-text="关"
                      />
                    </div>
                    <div v-if="activityCraftLoading" class="loading-container">
                      <div class="loading-spinner">
                        <div class="spinner"></div>
                        <p>加载中...</p>
                      </div>
                    </div>
                    <div v-else-if="activityCraftTree.length === 0" class="empty-state">
                      <div class="empty-text">暂无活动模板，请联系管理员添加</div>
                    </div>
                    <div v-else class="activity-craft-selection">
                      <div class="compact-craft-panel">
                        <div v-for="activity in activityCraftTree" :key="activity.id" class="craft-row">
                          <!-- 左侧：活动名称（带全选功能） -->
                          <div class="row-label" @click="toggleActivity(activity.id, !isActivityEnabled(activity.id))">
                            <span :class="['activity-tag', { 'is-active': isActivityEnabled(activity.id) }]">
                              {{ activity.name }}
                            </span>
                          </div>

                          <!-- 右侧：商品平铺 -->
                          <div class="row-content">
                            <!-- 渲染直接商品 (level 2 且没有子节点) -->
                            <div 
                              v-for="product in getDirectProducts(activity)" 
                              :key="product.id"
                              :class="['product-tag', { 'is-checked': isProductEnabled(product.id) }]"
                              @click="toggleProduct(product.id, !isProductEnabled(product.id))"
                            >
                              {{ product.name }}
                            </div>

                            <!-- 渲染二级菜单下的商品 (level 2 且有子节点) -->
                            <div v-for="menu in getMenus(activity)" :key="menu.id">
                              <div 
                                v-for="subProduct in menu.children" 
                                :key="subProduct.id"
                                :class="['product-tag', 'has-menu', { 'is-checked': isProductEnabled(subProduct.id) }]"
                                @click="toggleProduct(subProduct.id, !isProductEnabled(subProduct.id))"
                              >
                                <small class="menu-prefix">{{ menu.name }}·</small>{{ subProduct.name }}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- 背包 -->
                  <div class="inventory-section">
                    <h3 class="section-title">背包</h3>
                    <div class="form-row">
                      <el-checkbox v-model="configContent.backpack.slotsEnabled" size="small">背包开格子</el-checkbox>
                      <input
                          type="number"
                          v-model.number="configContent.backpack.slots"
                          :min="1"
                          :max="999"
                          class="simple-number-input"
                          style="width: 80px;"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Third column: Right side sections in unified container -->
              <div class="form-section grouped-section">
                <div class="right-sections-content">
                  <!-- 野外挂机 -->
                  <div class="section-divider">
                    <h4 class="compact-title">野外挂机</h4>

                    <!-- 普通地图区域 -->
                    <div class="outdoor-subsection">
                      <h5 class="subsection-title">普通地图</h5>
                      <div class="form-row single-line">
                        <label>类型：</label>
                        <el-select v-model="configContent.outdoor.type" size="small" style="width: 120px;" @change="onOutdoorTypeChange">
                          <el-option label="挖矿开箱" value="chest"></el-option>
                          <el-option label="野外挂机" value="hang"></el-option>
                          <el-option label="迷宫挂机" value="maze"></el-option>
                        </el-select>
                        <el-select v-model="configContent.outdoor.location" size="small" style="width: 150px;" v-if="configContent.outdoor.type">
                          <el-option
                              v-for="option in outdoorOptions"
                              :key="option.value"
                              :label="option.label"
                              :value="option.value">
                          </el-option>
                        </el-select>
                      </div>
                      <!-- 野外挖琥珀防封策略说明 -->
                      <div v-if="configContent.outdoor.type === 'amber'" class="form-row anti-ban-notice">
                        <div class="anti-ban-content">
                          <i class="el-icon-shield" style="color: #10b981; margin-right: 6px;"></i>
                          <span class="anti-ban-text">
                      <strong>防封策略：</strong>路径最优算法：采用距离优化算法选择最近的4个未访问采集点，结合随机选择确保每个账号的采集路径都是独特且不可预测的，有效模拟真实玩家行为避免被检测。
                    </span>
                        </div>
                      </div>
                      <div class="form-row" v-if="(configContent.outdoor.type === 'hang' || configContent.outdoor.type === 'maze') && showPointButton">
                        <label>定点挂机：</label>
                        <el-button
                            type="primary"
                            size="small"
                            @click="openPointPicker"
                        >定点坐标</el-button>
                        <el-button
                            v-if="showPointButton && hasCustomPoints"
                            type="danger"
                            size="small"
                            @click="clearAllPoints"
                        >清除所有挂机点</el-button>
                        <span v-if="showPointButton && hasCustomPoints" class="small-text">
                    已设{{ configContent.outdoor.customPoints.length }}个点（最多50个）
                  </span>
                      </div>
                      <!-- 挖矿开箱的定点功能 -->
                      <div class="form-row" v-if="configContent.outdoor.type === 'chest' && showPointButton">
                        <label>多点挖矿：</label>
                        <el-button
                            type="primary"
                            size="small"
                            @click="openPointPicker"
                        >多点挖矿</el-button>
                        <el-button
                            v-if="hasCustomPoints"
                            type="danger"
                            size="small"
                            @click="clearAllPoints"
                        >清除所有点</el-button>
                        <span v-if="hasCustomPoints" class="small-text">
                    已设{{ configContent.outdoor.customPoints.length }}个点（最多50个，最少1个）
                  </span>
                        <span v-else class="small-text" style="color: #f56c6c;">
                    请至少设置1个定点
                  </span>
                      </div>
                      <!-- 挖矿开箱定点提示 -->
                      <div v-if="configContent.outdoor.type === 'chest' && showPointButton" class="form-row">
                  <span class="inline-notice" style="margin-left: 0;">
                    <i class="el-icon-info" style="color: #409eff; margin-right: 4px;"></i>
                    <span style="color: #666; font-size: 12px;">提示：如果定点处能看到宝箱的抓手会顺带开箱子</span>
                  </span>
                      </div>
                      <div class="form-row">
                        <el-checkbox v-model="configContent.outdoor.autoSilverFarming" size="small">银币低于20万去15级图刷银币</el-checkbox>
                      </div>
                      <div class="form-row">
                        <el-checkbox v-model="configContent.outdoor.eliteMap" size="small">精锐地图</el-checkbox>
                      </div>
                    </div>

                    <!-- 挖琥珀区域 -->
                    <div class="outdoor-subsection">
                      <h5 class="subsection-title">挖琥珀区域</h5>
                      <div class="compact-note" style="margin-bottom: 10px;">
                        <i class="el-icon-info" style="color: #409EFF; margin-right: 6px;"></i>
                        <strong>提示：</strong>建议每天只挖4小时，长时间挖矿可能会判定封号
                      </div>
                      <div class="form-row">
                        <el-checkbox v-model="configContent.outdoor.amber.enabled" size="small">启用挖琥珀</el-checkbox>
                      </div>
                      <!-- 时间设置 -->
                      <div class="time-settings">
                        <el-checkbox v-model="configContent.outdoor.amber.timeLimit.enabled" size="small">指定时间执行（包括开始和结束的小时）</el-checkbox>
                        <div v-if="configContent.outdoor.amber.timeLimit.enabled" class="time-range">
                          <el-select
                              v-model="configContent.outdoor.amber.timeLimit.startTime"
                              placeholder="开始"
                              size="small"
                              style="width: 60px;"
                          >
                            <el-option v-for="hour in 24" :key="hour-1" :label="(hour-1).toString().padStart(2, '0')" :value="(hour-1).toString().padStart(2, '0') + ':00'" />
                          </el-select>
                          <span class="time-separator">至</span>
                          <el-select
                              v-model="configContent.outdoor.amber.timeLimit.endTime"
                              placeholder="结束"
                              size="small"
                              style="width: 60px;"
                          >
                            <el-option v-for="hour in 24" :key="hour-1" :label="(hour-1).toString().padStart(2, '0')" :value="(hour-1).toString().padStart(2, '0') + ':00'" />
                          </el-select>
                        </div>
                      </div>
                      <div class="form-row single-line">
                        <label>地图：</label>
                        <el-select v-model="configContent.outdoor.amber.location" size="small" style="width: 150px;">
                          <el-option label="世界树一层" value="世界树一层"></el-option>
                          <el-option label="世界树二层" value="世界树二层"></el-option>
                          <el-option label="世界树三层" value="世界树三层"></el-option>
                          <el-option label="世界树四层" value="世界树四层"></el-option>
                        </el-select>
                        <span class="inline-notice">
                    <i class="el-icon-info" style="color: #409eff; margin-left: 12px; margin-right: 4px;"></i>
                    <span style="color: #666; font-size: 12px;">提示：挖满会去挂机</span>
                  </span>
                      </div>
                      <div class="form-row single-line">
                        <label>更换频道：</label>
                        <el-select v-model="configContent.outdoor.amber.channel" size="small" style="width: 150px;">
                          <el-option label="与挂机设置相同" value="与挂机设置相同"></el-option>
                          <el-option label="频道1" value="频道1"></el-option>
                          <el-option label="频道2" value="频道2"></el-option>
                          <el-option label="频道3" value="频道3"></el-option>
                          <el-option label="频道4" value="频道4"></el-option>
                          <el-option label="频道5" value="频道5"></el-option>
                          <el-option label="频道6" value="频道6"></el-option>
                          <el-option label="频道7" value="频道7"></el-option>
                          <el-option label="频道8" value="频道8"></el-option>
                          <el-option label="频道9" value="频道9"></el-option>
                          <el-option label="频道10" value="频道10"></el-option>
                        </el-select>
                      </div>
                      <div class="form-row" v-if="configContent.outdoor.amber.enabled && showAmberPointButton">
                        <label>定点挖琥珀：</label>
                        <el-button
                            type="primary"
                            size="small"
                            @click="openAmberPointPicker"
                        >定点挖琥珀</el-button>
                        <el-button
                            v-if="hasAmberCustomPoints"
                            type="danger"
                            size="small"
                            @click="clearAllAmberPoints"
                        >清除所有点</el-button>
                        <span v-if="hasAmberCustomPoints" class="small-text">
                    已设{{ configContent.outdoor.amber.customPoints.length }}个点（最多50个）
                  </span>
                      </div>
                    </div>

                    <!-- 激战地区域 -->
                    <div class="outdoor-subsection">
                      <h5 class="subsection-title">激战地</h5>
                      <div class="form-row">
                        <el-checkbox v-model="configContent.outdoor.battleground.enabled" size="small">启用激战地挂机</el-checkbox>
                      </div>
                      <!-- 时间设置 -->
                      <div class="time-settings">
                        <el-checkbox v-model="configContent.outdoor.battleground.timeLimit.enabled" size="small">指定时间执行（包括开始和结束的小时）</el-checkbox>
                        <div v-if="configContent.outdoor.battleground.timeLimit.enabled" class="time-range">
                          <el-select
                              v-model="configContent.outdoor.battleground.timeLimit.startTime"
                              placeholder="开始"
                              size="small"
                              style="width: 60px;"
                          >
                            <el-option v-for="hour in 24" :key="hour-1" :label="(hour-1).toString().padStart(2, '0')" :value="(hour-1).toString().padStart(2, '0') + ':00'" />
                          </el-select>
                          <span class="time-separator">至</span>
                          <el-select
                              v-model="configContent.outdoor.battleground.timeLimit.endTime"
                              placeholder="结束"
                              size="small"
                              style="width: 60px;"
                          >
                            <el-option v-for="hour in 24" :key="hour-1" :label="(hour-1).toString().padStart(2, '0')" :value="(hour-1).toString().padStart(2, '0') + ':00'" />
                          </el-select>
                        </div>
                      </div>
                      <div class="form-row single-line">
                        <label>地图：</label>
                        <el-select v-model="configContent.outdoor.battleground.location" size="small" style="width: 150px;">
                          <el-option label="诸神黄昏山谷-40级" value="诸神黄昏山谷"></el-option>
                          <el-option label="诸神黄昏岔路-45级" value="诸神黄昏岔路"></el-option>
                          <el-option label="世界树峡谷深层-56级" value="世界树峡谷深层-56级"></el-option>
                          <el-option label="尼达维利尔峡谷-65级" value="尼达维利尔峡谷-65级"></el-option>
                        </el-select>
                      </div>
                      <div class="form-row" v-if="configContent.outdoor.battleground.enabled">
                        <label>定点挂机：</label>
                        <el-button
                            type="primary"
                            size="small"
                            @click="openBattlegroundPointPicker"
                        >定点坐标</el-button>
                        <el-button
                            v-if="hasBattlegroundCustomPoints"
                            type="danger"
                            size="small"
                            @click="clearAllBattlegroundPoints"
                        >清除所有点</el-button>
                        <span v-if="hasBattlegroundCustomPoints" class="small-text">
                          已设{{ configContent.outdoor.battleground.customPoints.length }}个点（最多50个）
                        </span>
                      </div>
                      <div class="form-row">
                        <span class="form-label">死亡</span>
                        <input
                            type="number"
                            v-model.number="configContent.outdoor.battleground.deathCount"
                            :min="1"
                            :max="20"
                            class="simple-number-input"
                        />
                        <span class="form-label">次后去普通地图挂机</span>
                      </div>
                    </div>

                    <!-- 离线挂机区域 -->
                    <div class="outdoor-subsection">
                      <h5 class="subsection-title">离线挂机</h5>
                      <div class="form-row">
                        <el-checkbox v-model="configContent.outdoor.offlineHang.enabled" size="small">启用离线挂机</el-checkbox>
                      </div>
                      <div class="time-settings">
                        <el-checkbox v-model="configContent.outdoor.offlineHang.timeLimit.enabled" size="small">指定时间执行（包括开始和结束的小时）</el-checkbox>
                        <div v-if="configContent.outdoor.offlineHang.timeLimit.enabled" class="time-range">
                          <el-select
                              v-model="configContent.outdoor.offlineHang.timeLimit.startTime"
                              placeholder="开始"
                              size="small"
                              style="width: 60px;"
                          >
                            <el-option v-for="hour in 24" :key="hour-1" :label="(hour-1).toString().padStart(2, '0')" :value="(hour-1).toString().padStart(2, '0') + ':00'" />
                          </el-select>
                          <span class="time-separator">至</span>
                          <el-select
                              v-model="configContent.outdoor.offlineHang.timeLimit.endTime"
                              placeholder="结束"
                              size="small"
                              style="width: 60px;"
                          >
                            <el-option v-for="hour in 24" :key="hour-1" :label="(hour-1).toString().padStart(2, '0')" :value="(hour-1).toString().padStart(2, '0') + ':00'" />
                          </el-select>
                        </div>
                      </div>
                      <div class="form-row single-line">
                        <label>地图：</label>
                        <el-select v-model="configContent.outdoor.offlineHang.location" size="small" style="width: 230px;">
                          <el-option
                            v-for="option in offlineHangOptions"
                            :key="option.value"
                            :label="option.label"
                            :value="option.value"
                          />
                        </el-select>
                      </div>
                    </div>
                  </div>

                  <!-- 挂机设置 -->
                  <div class="section-divider">
                    <h4 class="compact-title">挂机设置</h4>
                    <div class="form-row">
                      <el-checkbox v-model="configContent.hangSettings.autoBuyPotion" size="small">自动买药</el-checkbox>
                      <div class="night-mode-notice" style="margin-left: 12px;">
                        <span class="notice-text">提示：每隔这些时间自动补齐至这么多药水</span>
                      </div>
                    </div>
                    <div class="form-row">
                      <span class="form-label">间隔</span>
                      <input
                          type="number"
                          v-model.number="configContent.hangSettings.potionInterval"
                          :min="1"
                          :max="24"
                          class="simple-number-input"
                          style="width: 50px;"
                      />
                      <span class="form-label">小时</span>
                    </div>
                    <!-- 道具数量设置 -->
                    <div class="form-row">
                      <label>体力药水：</label>
                      <input
                          type="number"
                          v-model.number="configContent.hangSettings.staminaPotion"
                          :min="100"
                          :max="2000"
                          class="simple-number-input"
                          style="width: 80px;"
                      />
                      <span class="form-label">（100～2000）</span>
                    </div>
                    <div class="form-row">
                      <label>移动卷轴：</label>
                      <input
                          type="number"
                          v-model.number="configContent.hangSettings.moveScroll"
                          :min="20"
                          :max="100"
                          class="simple-number-input"
                          style="width: 80px;"
                      />
                      <span class="form-label">（20～100）</span>
                    </div>
                    <div class="form-row">
                      <label>回城卷轴：</label>
                      <input
                          type="number"
                          v-model.number="configContent.hangSettings.returnScroll"
                          :min="20"
                          :max="100"
                          class="simple-number-input"
                          style="width: 80px;"
                      />
                      <span class="form-label">（20～100）</span>
                    </div>
                    <div class="form-row">
                      <el-checkbox v-model="configContent.hangSettings.powerSaveMode" size="small">开启省电模式</el-checkbox>
                    </div>
                    <div class="form-row">
                      <el-checkbox v-model="configContent.hangSettings.autoUseConsumables" size="small">自动使用所有消耗品</el-checkbox>
                    </div>
                    <div class="form-row">
                      <el-checkbox v-model="configContent.hangSettings.nightMode" size="small">夜晚模式</el-checkbox>
                      <div class="night-mode-notice">
                        <span class="notice-text">半夜减少脚本操作，仅买药和省电挂机</span>
                      </div>
                    </div>
                    <div class="form-row">
                      <label>血量低于</label>
                      <input
                          type="number"
                          v-model.number="configContent.hangSettings.healthThreshold"
                          :min="1"
                          :max="100"
                          class="simple-number-input"
                      />
                      <span class="form-label">%自动回城</span>
                      <span class="form-hint">（越高越好）</span>
                    </div>
                    <div class="form-row">
                      <label>挂机范围：</label>
                      <input
                          type="number"
                          v-model.number="configContent.hangSettings.hangRange"
                          :min="5"
                          :max="50"
                          class="simple-number-input"
                      />
                      <span class="form-label">（5～50）</span>
                    </div>
                    <div class="form-row">
                      <label>采集范围：</label>
                      <input
                          type="number"
                          v-model.number="configContent.hangSettings.collectRange"
                          :min="5"
                          :max="50"
                          class="simple-number-input"
                      />
                      <span class="form-label">（5～50）</span>
                    </div>
                    <div class="form-row">
                      <label>自动使用体力药水：</label>
                      <input
                          type="number"
                          v-model.number="configContent.hangSettings.autoUseStaminaPotion"
                          :min="50"
                          :max="100"
                          class="simple-number-input"
                          style="width: 80px;"
                      />
                      <span class="form-label">%（50～100）</span>
                    </div>
                    <div class="form-row">
                      <el-checkbox v-model="configContent.hangSettings.changeChannel" size="small">更换频道</el-checkbox>
                      <label v-if="configContent.hangSettings.changeChannel">频道</label>
                      <el-select
                          v-if="configContent.hangSettings.changeChannel"
                          v-model="configContent.hangSettings.channel"
                          size="small"
                          style="width: 100px;"
                      >
                        <el-option label="随机" value="随机"></el-option>
                        <el-option label="频道1" value="1"></el-option>
                        <el-option label="频道2" value="2"></el-option>
                        <el-option label="频道3" value="3"></el-option>
                        <el-option label="频道4" value="4"></el-option>
                        <el-option label="频道5" value="5"></el-option>
                        <el-option label="频道6" value="6"></el-option>
                        <el-option label="频道7" value="7"></el-option>
                        <el-option label="频道8" value="8"></el-option>
                        <el-option label="频道9" value="9"></el-option>
                        <el-option label="频道10" value="10"></el-option>
                        <el-option label="频道11" value="11"></el-option>
                        <el-option label="频道12" value="12"></el-option>
                        <el-option label="频道13" value="13"></el-option>
                        <el-option label="频道14" value="14"></el-option>
                        <el-option label="频道15" value="15"></el-option>
                        <el-option label="频道16" value="16"></el-option>
                        <el-option label="频道17" value="17"></el-option>
                        <el-option label="频道18" value="18"></el-option>
                        <el-option label="频道19" value="19"></el-option>
                        <el-option label="频道20" value="20"></el-option>
                        <el-option label="频道21" value="21"></el-option>
                        <el-option label="频道22" value="22"></el-option>
                        <el-option label="频道23" value="23"></el-option>
                        <el-option label="频道24" value="24"></el-option>
                        <el-option label="频道25" value="25"></el-option>
                      </el-select>
                    </div>
                    <div v-if="configContent.hangSettings.changeChannel" class="form-row">
                      <div class="night-mode-notice">
                        <span class="notice-text">如果选择的频道不存在，会找前面最后一个频道进入</span>
                      </div>
                    </div>
                    <div class="form-row">
                      <el-checkbox v-model="configContent.hangSettings.archerReturnOriginEnabled" size="small">每隔</el-checkbox>
                      <input
                        type="number"
                        v-model.number="configContent.hangSettings.archerReturnOriginInterval"
                        :min="1"
                        :max="600"
                        class="simple-number-input"
                        style="width: 60px;"
                      />
                      <span class="form-label">秒回一次原点</span>
                    </div>
                  </div>

                  <!-- 间隔时间区域 -->
                  <div class="section-divider">
                    <h4 class="compact-title">间隔时间（单位：分钟）</h4>
                    <div class="form-row" style="display: flex; gap: 20px;">
                      <div style="display: flex; align-items: center;">
                        <label style="width: 80px; text-align: right; margin-right: 8px;">分解装备：</label>
                        <input
                          type="number"
                          v-model.number="configContent.hangSettings.decomposeInterval"
                          :min="5"
                          :max="3600"
                          class="simple-number-input"
                          style="width: 48px;"
                        />
                      </div>
                      <div style="display: flex; align-items: center;">
                        <label style="width: 80px; text-align: right; margin-right: 8px;">清理背包：</label>
                        <input
                          type="number"
                          v-model.number="configContent.hangSettings.cleanBagInterval"
                          :min="5"
                          :max="3600"
                          class="simple-number-input"
                          style="width: 48px;"
                        />
                      </div>
                    </div>
                    <div class="form-row" style="display: flex; gap: 20px;">
                      <div style="display: flex; align-items: center;">
                        <label style="width: 80px; text-align: right; margin-right: 8px;">仓库存储：</label>
                        <input
                          type="number"
                          v-model.number="configContent.hangSettings.storageInterval"
                          :min="5"
                          :max="3600"
                          class="simple-number-input"
                          style="width: 48px;"
                        />
                      </div>
                      <div style="display: flex; align-items: center;">
                        <label style="width: 80px; text-align: right; margin-right: 8px;">诺恩宝物：</label>
                        <input
                          type="number"
                          v-model.number="configContent.hangSettings.nornTreasureInterval"
                          :min="5"
                          :max="3600"
                          class="simple-number-input"
                          style="width: 48px;"
                        />
                      </div>
                      <div style="display: flex; align-items: center;">
                        <label style="width: 80px; text-align: right; margin-right: 8px;">兑换码：</label>
                        <input
                          type="number"
                          v-model.number="configContent.hangSettings.redeemCodeInterval"
                          :min="5"
                          :max="10080"
                          class="simple-number-input"
                          style="width: 60px;"
                        />
                      </div>
                    </div>
                    <div class="form-row">
                      <div class="night-mode-notice">
                        <span class="notice-text">提示：时间会上下浮动15%，不可能真的60分钟一次，容易被系统判定为固定行为</span>
                      </div>
                    </div>
                  </div>

                  <!-- 工会 -->
                  <div class="section-divider">
                    <h4 class="compact-title">工会</h4>
                    <!-- 第一行：自动加入公会 -->
                    <div class="form-row">
                      <el-checkbox v-model="configContent.guild.autoJoin" size="small">自动加入工会</el-checkbox>
                    </div>
                    <hr style="margin: 10px 0; border: none; border-top: 1px solid #e0e0e0;">
                    <!-- 第二行：捐献区域 -->
                    <div class="form-row">
                      <el-checkbox v-model="configContent.guild.silverContribute" size="small">银币捐献</el-checkbox>
                      <el-checkbox v-model="configContent.guild.amberContribute" size="small">琥珀捐献</el-checkbox>
                    </div>
                    <hr style="margin: 10px 0; border: none; border-top: 1px solid #e0e0e0;">
                    <!-- 第三行：技能协作 -->
                    <div class="form-row">
                      <el-checkbox v-model="configContent.guild.techContribute24" size="small">24次科技捐献</el-checkbox>
                      <label style="margin-left: 10px;">协作道具：</label>
                      <el-select v-model="configContent.guild.collaborationItemType" size="small" style="width: 120px;">
                        <el-option label="仅银币" value="仅银币"></el-option>
                        <el-option label="仅琥珀" value="仅琥珀"></el-option>
                        <el-option label="不限制" value="不限制"></el-option>
                      </el-select>
                    </div>
                    <!-- 技能协作间隔单独一行 -->
                    <div class="form-row">
                      <label>技能协作间隔：</label>
                      <input
                        type="number"
                        v-model.number="configContent.guild.skillCollaborationInterval"
                        :min="1"
                        :max="24"
                        class="simple-number-input"
                        style="width: 60px;"
                      />
                      <span class="form-label">小时</span>
                    </div>
                    <!-- 公会技能单独一行 -->
                    <div class="form-row">
                      <label>公会技能：</label>
                      <el-select v-model="configContent.guild.guildSkill" size="small" style="width: 150px;">
                        <el-option label="公会发展" value="公会发展"></el-option>
                        <el-option label="战斗训练" value="战斗训练"></el-option>
                        <el-option label="战争战术" value="战争战术"></el-option>
                        <el-option label="会长推荐" value="会长推荐"></el-option>
                      </el-select>
                    </div>
                    <hr style="margin: 10px 0; border: none; border-top: 1px solid #e0e0e0;">
                    <!-- 第四行：公会帮助 -->
                    <div class="form-row">
                      <el-checkbox v-model="configContent.guild.guildHelp" size="small">工会帮助</el-checkbox>
                    </div>
                  </div>


                  <!-- BOSS区域 -->
                  <div class="section-divider">
                    <h4 class="compact-title">BOSS区域</h4>
                    <div class="boss-time-notice">
                      <div class="notice-content">
                        <i class="el-icon-info" style="color: #409EFF; margin-right: 6px;"></i>
                        <span class="notice-text">
                    <strong>提示：</strong>会在野外挂机和挖琥珀执行时间之内进行远征
                  </span>
                      </div>
                    </div>
                    <!-- 时间设置 -->
                    <div class="form-row">
                      <div class="time-settings">
                        <el-checkbox v-model="configContent.bossArea.timeLimit.enabled" size="small">指定时间执行（包括开始和结束的小时）</el-checkbox>
                        <div v-if="configContent.bossArea.timeLimit.enabled" class="time-range">
                          <el-select
                              v-model="configContent.bossArea.timeLimit.startTime"
                              placeholder="开始"
                              size="small"
                              style="width: 60px;"
                          >
                            <el-option v-for="hour in 24" :key="hour-1" :label="(hour-1).toString().padStart(2, '0')" :value="(hour-1).toString().padStart(2, '0') + ':00'" />
                          </el-select>
                          <span class="time-separator">至</span>
                          <el-select
                              v-model="configContent.bossArea.timeLimit.endTime"
                              placeholder="结束"
                              size="small"
                              style="width: 60px;"
                          >
                            <el-option v-for="hour in 24" :key="hour-1" :label="(hour-1).toString().padStart(2, '0')" :value="(hour-1).toString().padStart(2, '0') + ':00'" />
                          </el-select>
                        </div>
                      </div>
                    </div>
                    <div class="form-row expedition-5-row">
                      <div class="expedition-setting">
                        <el-checkbox v-model="configContent.bossArea.expedition5.enabled" size="small">远征5人</el-checkbox>
                        <el-select
                            v-model="configContent.bossArea.expedition5.level"
                            size="small"
                            style="width: 85px;"
                            :disabled="!configContent.bossArea.expedition5.enabled"
                        >
                          <el-option label="智能选择" value="auto" />
                          <el-option label="28级五人副本" value="28" />
                          <el-option label="35级五人副本" value="35" />
                          <el-option label="40级五人副本" value="40" />
                          <el-option label="45级五人副本" value="45" />
                          <el-option label="50级五人副本" value="50" />
                          <el-option label="55级五人副本" value="55" />
                          <el-option label="60级五人副本" value="60" />
                          <el-option label="65级五人副本" value="65" />
                          <el-option label="70级五人副本" value="70" />
                          <el-option label="75级五人副本" value="75" />
                        </el-select>
                        <el-checkbox
                            v-model="configContent.bossArea.expedition5.createRoom"
                            size="small"
                            :disabled="!configContent.bossArea.expedition5.enabled"
                            class="expedition-inline-option"
                        >
                          没有队伍就开房间
                        </el-checkbox>
                      </div>
                    </div>
                    <div class="form-row">
                      <div class="expedition-setting">
                        <el-checkbox v-model="configContent.bossArea.expedition15.enabled" size="small">远征15人</el-checkbox>
                        <el-select
                            v-model="configContent.bossArea.expedition15.level"
                            size="small"
                            style="width: 85px;"
                            :disabled="!configContent.bossArea.expedition15.enabled"
                        >
                          <el-option label="智能选择" value="auto" />
                          <el-option label="33级十五人副本" value="33" />
                          <el-option label="38级十五人副本" value="38" />
                          <el-option label="43级十五人副本" value="43" />
                          <el-option label="48级十五人副本" value="48" />
                          <el-option label="53级十五人副本" value="53" />
                          <el-option label="58级十五人副本" value="58" />
                          <el-option label="70级十五人副本" value="70" />
                          <el-option label="80级十五人副本" value="80" />
                        </el-select>
                        <el-checkbox
                            v-model="configContent.bossArea.expedition15.createRoom"
                            size="small"
                            :disabled="!configContent.bossArea.expedition15.enabled"
                            class="expedition-inline-option"
                        >
                          没有队伍就开房间
                        </el-checkbox>
                      </div>
                    </div>
                    <div class="form-row expedition-5-row seasonal-expedition-row">
                      <div class="expedition-setting">
                        <el-checkbox v-model="configContent.bossArea.expeditionSeasonal5.enabled" size="small">赛季5人</el-checkbox>
                        <el-select
                            v-model="configContent.bossArea.expeditionSeasonal5.level"
                            size="small"
                            style="width: 85px;"
                            :disabled="!configContent.bossArea.expeditionSeasonal5.enabled"
                        >
                          <el-option label="28级五人副本" value="28" />
                          <el-option label="40级五人副本" value="40" />
                          <el-option label="50级五人副本" value="50" />
                          <el-option label="55级五人副本" value="55" />
                        </el-select>
                        <el-checkbox
                            v-model="configContent.bossArea.expeditionSeasonal5.createRoom"
                            size="small"
                            :disabled="!configContent.bossArea.expeditionSeasonal5.enabled"
                            class="expedition-inline-option"
                        >
                          没有队伍就开房间
                        </el-checkbox>
                      </div>
                    </div>
                    <div class="form-row">
                      <el-checkbox v-model="configContent.bossArea.worldBoss" size="small">世界boss</el-checkbox>
                    </div>
                  </div>
                </div>
              </div>
            </div>


          </div>

          <!-- 道具配置Tab -->
          <div v-if="activeTab === 'items'" class="tab-pane">
            <div class="items-config-container">

              <!-- 需要存仓库的道具 -->
              <div class="form-section full-width">
                <div class="section-header">
                  <h3 class="section-title">需要存仓库的道具</h3>
                  <button 
                    class="select-all-btn"
                    @click="toggleSelectAllWarehouse"
                    :disabled="warehouseItems.length === 0"
                  >
                    {{ isAllWarehouseSelected ? '取消全选' : '全选' }}
                  </button>
                </div>
                <div class="form-row" style="margin-bottom: 10px;">
                  <el-checkbox v-model="configContent.builtInWarehouse" size="small">内置存仓</el-checkbox>
                  <span class="form-hint" style="margin-left: 8px;">会加强存储更多以下没有到绑定道具</span>
                </div>
                <div class="item-grid">
                  <div
                      v-for="item in warehouseItems"
                      :key="item.id"
                      class="item-container"
                      :class="{ selected: configContent.warehouseItems.includes(item.id) }"
                      @click="toggleWarehouseItem(item.id)"
                  >
                    <div class="item-icon" :class="getItemBackgroundColorClass(item.background_color)">
                      <img :src="item.icon_url" :alt="item.item_name" @error="handleImageError" />
                      <div class="item-checkbox">
                        <el-checkbox
                            :model-value="configContent.warehouseItems.includes(item.id)"
                            size="small"
                        ></el-checkbox>
                      </div>
                    </div>
                    <div class="item-name">{{ item.item_name }}</div>
                  </div>
                </div>
                <div v-if="warehouseItems.length === 0" class="empty-items">
                  暂无可存仓库的道具
                </div>
              </div>

              <!-- 需要删除的垃圾道具 -->
              <div class="form-section full-width">
                <div class="section-header">
                  <h3 class="section-title">需要删除的垃圾道具</h3>
                </div>
                <div class="form-row" style="margin-bottom: 10px;">
                  <el-checkbox v-model="configContent.builtInDelete" size="small">内置删除</el-checkbox>
                  <span class="form-hint" style="margin-left: 8px;">会加强删除更多未在列表中单独配置的垃圾道具</span>
                </div>
                <div class="item-grid">
                  <div
                      v-for="item in deleteItems"
                      :key="item.id"
                      class="item-container"
                      :class="{ selected: configContent.deleteItems.includes(item.id) }"
                      @click="toggleDeleteItem(item.id)"
                  >
                    <div class="item-icon" :class="getItemBackgroundColorClass(item.background_color)">
                      <img :src="item.icon_url" :alt="item.item_name" @error="handleImageError" />
                      <div class="item-checkbox">
                        <el-checkbox
                            :model-value="configContent.deleteItems.includes(item.id)"
                            size="small"
                        ></el-checkbox>
                      </div>
                    </div>
                    <div class="item-name">{{ item.item_name }}</div>
                  </div>
                </div>
                <div v-if="deleteItems.length === 0" class="empty-items">
                  暂无可删除的道具
                </div>
              </div>

              <!-- 需要使用的道具 -->
              <div class="form-section full-width">
                <div class="section-header">
                  <h3 class="section-title">需要使用的道具</h3>
                  <button 
                    class="select-all-btn"
                    @click="toggleSelectAllUse"
                    :disabled="useItems.length === 0"
                  >
                    {{ isAllUseSelected ? '取消全选' : '全选' }}
                  </button>
                </div>
                <div class="item-grid">
                  <div
                      v-for="item in useItems"
                      :key="item.id"
                      class="item-container"
                      :class="{ selected: configContent.useItems && configContent.useItems.includes(item.id) }"
                      @click="toggleUseItem(item.id)"
                  >
                    <div class="item-icon" :class="getItemBackgroundColorClass(item.background_color)">
                      <img :src="item.icon_url" :alt="item.item_name" @error="handleImageError" />
                      <div class="item-checkbox">
                        <el-checkbox
                            :model-value="configContent.useItems && configContent.useItems.includes(item.id)"
                            size="small"
                        ></el-checkbox>
                      </div>
                    </div>
                    <div class="item-name">{{ item.item_name }}</div>
                  </div>
                </div>
                <div v-if="useItems && useItems.length === 0" class="empty-items">
                  暂无可使用的道具
                </div>
              </div>

              <!-- 特殊道具使用 -->
              <div class="form-section full-width">
                <h3 class="section-title">特殊道具使用</h3>
                <div class="section-divider">
                  <div class="form-row">
                    <label>成长召唤券选择箱子：</label>
                    <el-select v-model="configContent.growthSummonTicketBox" size="small" style="width: 150px;">
                      <el-option label="不使用" value="不使用"></el-option>
                      <el-option label="瓦尔基里" value="瓦尔基里"></el-option>
                      <el-option label="迪西尔" value="迪西尔"></el-option>
                    </el-select>
                  </div>
                  <div class="form-row">
                    <label>高级升阶结晶石选择箱子：</label>
                    <el-select v-model="configContent.advancedUpgradeCrystalBox" size="small" style="width: 150px;">
                      <el-option label="不使用" value="不使用"></el-option>
                      <el-option label="随机" value="随机"></el-option>
                      <el-option label="瓦尔基里" value="瓦尔基里"></el-option>
                      <el-option label="迪西尔" value="迪西尔"></el-option>
                    </el-select>
                  </div>
                  <div class="form-row">
                    <label>高级帕特拉卡牌选择箱子：</label>
                    <el-select v-model="configContent.advancedPatraCardBox" size="small" style="width: 150px;">
                      <el-option label="不使用" value="不使用"></el-option>
                      <el-option label="随机" value="随机"></el-option>
                      <el-option label="瓦尔基里" value="瓦尔基里"></el-option>
                      <el-option label="迪西尔" value="迪西尔"></el-option>
                    </el-select>
                  </div>
                  <div class="form-row">
                    <label>宝物岛召唤券选择箱子：</label>
                    <el-select v-model="configContent.treasureIslandSummonTicketBox" size="small" style="width: 150px;">
                      <el-option label="不使用" value="不使用"></el-option>
                      <el-option label="随机" value="随机"></el-option>
                      <el-option label="瓦尔基里" value="瓦尔基里"></el-option>
                      <el-option label="迪西尔" value="迪西尔"></el-option>
                    </el-select>
                  </div>
                  <div class="form-row">
                    <label>玛尼召唤券选择箱子：</label>
                    <el-select v-model="configContent.maniSummonTicketBox" size="small" style="width: 150px;">
                      <el-option label="不使用" value="不使用"></el-option>
                      <el-option label="随机" value="随机"></el-option>
                      <el-option label="瓦尔基里" value="瓦尔基里"></el-option>
                      <el-option label="迪西尔" value="迪西尔"></el-option>
                    </el-select>
                  </div>
                  <div class="form-row">
                    <label>宝物岛稀有召唤券选择箱子：</label>
                    <el-select v-model="configContent.treasureIslandRareSummonTicketBox" size="small" style="width: 150px;">
                      <el-option label="不使用" value="不使用"></el-option>
                      <el-option label="随机" value="随机"></el-option>
                      <el-option label="瓦尔基里" value="瓦尔基里"></el-option>
                      <el-option label="迪西尔" value="迪西尔"></el-option>
                    </el-select>
                  </div>
                  <div class="form-row">
                    <label>协作召唤券选择箱子：</label>
                    <el-select v-model="configContent.collaborationSummonTicketBox" size="small" style="width: 150px;">
                      <el-option label="不使用" value="不使用"></el-option>
                      <el-option label="随机" value="随机"></el-option>
                      <el-option label="瓦尔基里" value="瓦尔基里"></el-option>
                      <el-option label="迪西尔" value="迪西尔"></el-option>
                    </el-select>
                  </div>
                  <div class="form-row">
                    <label>誓言召唤券11次选择箱子：</label>
                    <el-select v-model="configContent.oathSummonTicket11TimesBox" size="small" style="width: 150px;">
                      <el-option label="不使用" value="不使用"></el-option>
                      <el-option label="随机" value="随机"></el-option>
                      <el-option label="瓦尔基里" value="瓦尔基里"></el-option>
                      <el-option label="迪西尔" value="迪西尔"></el-option>
                    </el-select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 自动喊话Tab -->
        <div v-if="activeTab === 'shout'" class="tab-pane">
          <div class="shout-config-container">
            <div class="form-section full-width">
              <h3 class="section-title">自动喊话设置</h3>
              <div class="section-divider">
                <div class="form-row">
                  <el-checkbox v-model="configContent.autoShout.enabled" size="small">自动喊话开启</el-checkbox>
                </div>
                <div class="form-row">
                  <label class="setting-label">喊话内容：</label>
                  <div style="width: 100%; max-width: 600px;">
                    <el-input
                        v-model="configContent.autoShout.content"
                        type="textarea"
                        :rows="3"
                        placeholder="请输入喊话内容"
                        :maxlength="200"
                        show-word-limit
                        style="width: 100%;"
                    ></el-input>
                  </div>
                </div>
                <div class="form-row">
                  <label class="setting-label">间隔时间：</label>
                  <input
                      type="number"
                      v-model.number="configContent.autoShout.interval"
                      :min="1"
                      :max="3600"
                      class="simple-number-input"
                      style="width: 80px;"
                  />
                  <span class="form-label">秒（1～3600秒）</span>
                </div>
                <div class="form-row" style="margin-top: 10px;">
                  <div class="google-login-tip">
                    <div class="tip-content">
                      <span class="tip-icon">💡</span>
                      <span class="tip-text">喊话功能开启后将不会做其他任务</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 额外功能 Tab -->
        <div v-if="activeTab === 'extraFeatures'" class="tab-pane">
          <div class="shout-config-container">
            <div class="form-section full-width">
              <h3 class="section-title">额外功能</h3>
              <div class="section-divider">
                <div class="form-row">
                  <el-checkbox v-model="configContent.extraFeatures.keepOpeningOfflineChest" size="small">不断开离线箱子</el-checkbox>
                </div>
                <div class="form-row" style="margin-top: 8px;">
                  <el-checkbox v-model="configContent.extraFeatures.autoCraftBlueBook" size="small">自动做蓝书</el-checkbox>
                </div>
                <div class="form-row" style="margin-top: 8px;">
                  <el-checkbox v-model="configContent.extraFeatures.autoCraftRedBook" size="small">自动做红书</el-checkbox>
                </div>
                <div class="form-row" style="margin-top: 10px;">
                  <div class="google-login-tip">
                    <div class="tip-content">
                      <span class="tip-icon">💡</span>
                      <span class="tip-text">勾选并启动脚本后会持续开离线箱子；请保证背包容量充足，用完后请取消勾选。</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 斯巴达的迷宫 定点坐标选择对话框 -->
  <el-dialog
      v-model="pointDialogVisible"
      width="900px"
  >
    <template #header>
      <div style="display: flex; justify-content: space-between; align-items: center; width: 100%;">
        <span>选择定点坐标（横屏 1280×720）</span>
      </div>
    </template>
    <div class="map-picker-wrapper">
      <div class="map-canvas" ref="mapCanvasRef" @click="onMapClick">
        <img
            class="map-image"
            :src="mapImageUrl"
            alt="地图"
            draggable="false"
            @load="mapImageLoaded = true"
            @error="mapImageLoaded = false"
        />
        <div
            v-for="(p, idx) in customPointsDisplay"
            :key="idx"
            class="map-marker"
            :class="{ 
              'map-marker-numbered': pointPickerMode === 'amber' || pointPickerMode === 'polk' || pointPickerMode === 'sigren' || pointPickerMode === 'battleground',
              'map-marker-hovered': hoveredPointIndex === idx
            }"
            :style="{ left: p.x + 'px', top: p.y + 'px' }"
            :title="`点 ${idx + 1}: (${p.x}, ${p.y})`"
        >
          <span v-if="pointPickerMode === 'amber' || pointPickerMode === 'polk' || pointPickerMode === 'sigren' || pointPickerMode === 'battleground'" class="marker-number">{{ idx + 1 }}</span>
        </div>
      </div>
      <div class="map-info">
        <div v-if="pointPickerMode === 'battleground'">
          已设<strong>{{ configContent.outdoor.battleground.customPoints.length }}</strong>/50 个点
          <span v-if="configContent.outdoor.battleground.customPoints.length === 0">（点击地图添加）</span>
        </div>
        <div v-else-if="pointPickerMode === 'polk'">
          已设<strong>{{ configContent.dungeons.polk.customPoints.length }}</strong>/50 个点
          <span v-if="configContent.dungeons.polk.customPoints.length === 0">（点击地图添加）</span>
        </div>
        <div v-else-if="pointPickerMode === 'sigren'">
          已设<strong>{{ configContent.dungeons.sigren.customPoints.length }}</strong>/50 个点
          <span v-if="configContent.dungeons.sigren.customPoints.length === 0">（点击地图添加）</span>
        </div>
        <div v-else-if="pointPickerMode === 'amber'">
          已设<strong>{{ configContent.outdoor.amber.customPoints.length }}</strong>/50 个点
          <span v-if="configContent.outdoor.amber.customPoints.length === 0">（点击地图添加）</span>
        </div>
        <div v-else>
          已设<strong>{{ configContent.outdoor.customPoints.length }}</strong>/50 个点
          <span v-if="configContent.outdoor.customPoints.length === 0">（点击地图添加）</span>
        </div>
        <div v-if="pointPickerMode === 'battleground' && configContent.outdoor.battleground.customPoints.length > 0" class="hint">
          <span 
            v-for="(p, idx) in configContent.outdoor.battleground.customPoints" 
            :key="idx"
            style="margin-right: 8px; cursor: pointer; color: #f56c6c; text-decoration: underline;"
            @click.stop="removePoint(idx)"
            @mouseenter="hoveredPointIndex = idx"
            @mouseleave="hoveredPointIndex = null"
            :title="`点击删除点 ${idx + 1}`"
          >({{ p.x }}, {{ p.y }})</span>
        </div>
        <div v-else-if="pointPickerMode === 'polk' && configContent.dungeons.polk.customPoints.length > 0" class="hint">
          <span 
            v-for="(p, idx) in configContent.dungeons.polk.customPoints" 
            :key="idx"
            style="margin-right: 8px; cursor: pointer; color: #f56c6c; text-decoration: underline;"
            @click.stop="removePoint(idx)"
            @mouseenter="hoveredPointIndex = idx"
            @mouseleave="hoveredPointIndex = null"
            :title="`点击删除点 ${idx + 1}`"
          >({{ p.x }}, {{ p.y }})</span>
        </div>
        <div v-else-if="pointPickerMode === 'sigren' && configContent.dungeons.sigren.customPoints.length > 0" class="hint">
          <span 
            v-for="(p, idx) in configContent.dungeons.sigren.customPoints" 
            :key="idx"
            style="margin-right: 8px; cursor: pointer; color: #f56c6c; text-decoration: underline;"
            @click.stop="removePoint(idx)"
            @mouseenter="hoveredPointIndex = idx"
            @mouseleave="hoveredPointIndex = null"
            :title="`点击删除点 ${idx + 1}`"
          >({{ p.x }}, {{ p.y }})</span>
        </div>
        <div v-else-if="pointPickerMode === 'amber' && configContent.outdoor.amber.customPoints.length > 0" class="hint">
          <span 
            v-for="(p, idx) in configContent.outdoor.amber.customPoints" 
            :key="idx"
            style="margin-right: 8px; cursor: pointer; color: #f56c6c; text-decoration: underline;"
            @click.stop="removePoint(idx)"
            @mouseenter="hoveredPointIndex = idx"
            @mouseleave="hoveredPointIndex = null"
            :title="`点击删除点 ${idx + 1}`"
          >({{ p.x }}, {{ p.y }})</span>
        </div>
        <div v-else-if="pointPickerMode !== 'amber' && pointPickerMode !== 'polk' && pointPickerMode !== 'battleground' && configContent.outdoor.customPoints.length > 0" class="hint">
          <span 
            v-for="(p, idx) in configContent.outdoor.customPoints" 
            :key="idx"
            style="margin-right: 8px; cursor: pointer; color: #f56c6c; text-decoration: underline;"
            @click.stop="removePoint(idx)"
            @mouseenter="hoveredPointIndex = idx"
            @mouseleave="hoveredPointIndex = null"
            :title="`点击删除点 ${idx + 1}`"
          >({{ p.x }}, {{ p.y }})</span>
        </div>
        <div class="hint" v-if="pointPickerMode === 'battleground'">提示：左上角为 (0,0)。点击地图添加坐标，最多50个点。</div>
        <div class="hint" v-else-if="pointPickerMode === 'polk'">提示：左上角为 (0,0)。点击地图添加坐标，最多50个点。刷怪和挖琥珀共用一套定点坐标。</div>
        <div class="hint" v-else-if="pointPickerMode === 'sigren'">提示：左上角为 (0,0)。点击地图添加坐标，最多50个点。</div>
        <div class="hint" v-else-if="pointPickerMode === 'amber'">提示：左上角为 (0,0)。点击地图添加坐标，最多50个点。</div>
        <div class="hint" v-else>提示：左上角为 (0,0)。点击地图添加坐标，最多50个{{ configContent.outdoor.type === 'chest' ? '，最少1个' : '' }}。</div>
        
        <!-- 坐标点复制和导入功能 -->
        <div style="margin-top: 15px; padding-top: 15px; border-top: 1px solid #e4e7ed;">
          <div style="margin-bottom: 10px; display: flex; align-items: center; gap: 10px;">
            <el-button size="small" type="primary" :disabled="!hasCurrentCustomPoints" @click="copyPointsToClipboard">
              一键复制坐标点
            </el-button>
          </div>
          <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 10px;">
            <el-input
                v-model="importPointsText"
                type="textarea"
                :rows="2"
                placeholder="(100, 200)，(300, 400)，(500, 600)"
                style="flex: 1;"
            />
            <el-button size="small" type="success" @click="importPoints">一键导入</el-button>
          </div>
        </div>
      </div>
    </div>
    <template #footer>
      <span class="dialog-footer">
        <el-button size="small" @click="closePointDialog">关闭</el-button>
        <el-button size="small" type="danger" :disabled="!hasCurrentCustomPoints" @click="clearAllPoints">{{ getClearButtonText() }}</el-button>
        <el-button size="small" type="primary" :disabled="!hasCurrentCustomPoints" @click="savePoint">保存</el-button>
      </span>
    </template>
  </el-dialog>

  <!-- 挖琥珀定点坐标选择对话框 -->
  <el-dialog
      v-model="amberPointDialogVisible"
      title="选择挖琥珀定点坐标（横屏 1280×720）"
      width="900px"
  >
    <div class="map-picker-wrapper">
      <div class="map-canvas" ref="amberMapCanvasRef" @click="onMapClick">
        <img
            class="map-image"
            :src="mapImageUrl"
            alt="地图"
            draggable="false"
            @load="amberMapImageLoaded = true"
            @error="amberMapImageLoaded = false"
        />
        <div
            v-for="(p, idx) in customPointsDisplay"
            :key="idx"
            class="map-marker map-marker-numbered"
            :style="{ left: p.x + 'px', top: p.y + 'px' }"
            :class="{ 'map-marker-hovered': hoveredPointIndex === idx }"
            :title="`点 ${idx + 1}: (${p.x}, ${p.y})`"
        >
          <span class="marker-number">{{ idx + 1 }}</span>
        </div>
      </div>
      <div class="map-info">
        <div>
          已设<strong>{{ configContent.outdoor.amber.customPoints.length }}</strong>/50 个点
          <span v-if="configContent.outdoor.amber.customPoints.length === 0">（点击地图添加）</span>
        </div>
        <div v-if="configContent.outdoor.amber.customPoints.length > 0" class="hint">
          {{ configContent.outdoor.amber.customPoints.map(p => `(${p.x}, ${p.y})`).join('，') }}
        </div>
        <div class="hint">提示：左上角为 (0,0)。点击地图添加坐标，最多50个点。</div>
        
        <!-- 坐标点复制和导入功能 -->
        <div style="margin-top: 15px; padding-top: 15px; border-top: 1px solid #e4e7ed;">
          <div style="margin-bottom: 10px; display: flex; align-items: center; gap: 10px;">
            <el-button size="small" type="primary" :disabled="!hasAmberCustomPoints" @click="copyPointsToClipboard">
              一键复制坐标点
            </el-button>
          </div>
          <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 10px;">
            <el-input
                v-model="importPointsText"
                type="textarea"
                :rows="2"
                placeholder="(100, 200)，(300, 400)，(500, 600)"
                style="flex: 1;"
            />
            <el-button size="small" type="success" @click="importPoints">一键导入</el-button>
          </div>
        </div>
      </div>
    </div>
    <template #footer>
      <span class="dialog-footer">
        <el-button size="small" @click="amberPointDialogVisible = false">关闭</el-button>
        <el-button size="small" type="danger" :disabled="!hasAmberCustomPoints" @click="clearAllAmberPoints">清除所有挖琥珀点</el-button>
        <el-button size="small" type="primary" :disabled="!hasAmberCustomPoints" @click="savePoint">保存</el-button>
      </span>
    </template>
  </el-dialog>

  <!-- 弗尔克范格定点坐标选择对话框 -->
  <el-dialog
      v-model="polkPointDialogVisible"
      title="选择弗尔克范格定点坐标（横屏 1280×720）"
      width="900px"
  >
    <div class="map-picker-wrapper">
      <div class="map-canvas" ref="polkMapCanvasRef" @click="onMapClick">
        <img
            class="map-image"
            :src="mapImageUrl"
            alt="地图"
            draggable="false"
            @load="polkMapImageLoaded = true"
            @error="polkMapImageLoaded = false"
        />
        <div
            v-for="(p, idx) in customPointsDisplay"
            :key="idx"
            class="map-marker map-marker-numbered"
            :style="{ left: p.x + 'px', top: p.y + 'px' }"
            :class="{ 'map-marker-hovered': hoveredPointIndex === idx }"
            :title="`点 ${idx + 1}: (${p.x}, ${p.y})`"
        >
          <span class="marker-number">{{ idx + 1 }}</span>
        </div>
      </div>
      <div class="map-info">
        <div>
          已设<strong>{{ configContent.dungeons.polk.customPoints.length }}</strong>/50 个点
          <span v-if="configContent.dungeons.polk.customPoints.length === 0">（点击地图添加）</span>
        </div>
        <div v-if="configContent.dungeons.polk.customPoints.length > 0" class="hint">
          {{ configContent.dungeons.polk.customPoints.map(p => `(${p.x}, ${p.y})`).join('，') }}
        </div>
        <div class="hint">提示：左上角为 (0,0)。点击地图添加坐标，最多50个点。刷怪和挖琥珀共用一套定点坐标。</div>
        
        <!-- 坐标点复制和导入功能 -->
        <div style="margin-top: 15px; padding-top: 15px; border-top: 1px solid #e4e7ed;">
          <div style="margin-bottom: 10px; display: flex; align-items: center; gap: 10px;">
            <el-button size="small" type="primary" :disabled="!hasPolkCustomPoints" @click="copyPointsToClipboard">
              一键复制坐标点
            </el-button>
          </div>
          <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 10px;">
            <el-input
                v-model="importPointsText"
                type="textarea"
                :rows="2"
                placeholder="(100, 200)，(300, 400)，(500, 600)"
                style="flex: 1;"
            />
            <el-button size="small" type="success" @click="importPoints">一键导入</el-button>
          </div>
        </div>
      </div>
    </div>
    <template #footer>
      <span class="dialog-footer">
        <el-button size="small" @click="polkPointDialogVisible = false">关闭</el-button>
        <el-button size="small" type="danger" :disabled="!hasPolkCustomPoints" @click="clearAllPolkPoints">清除所有点</el-button>
        <el-button size="small" type="primary" :disabled="!hasPolkCustomPoints" @click="savePoint">保存</el-button>
      </span>
    </template>
  </el-dialog>

  <!-- 黄金殿堂定点坐标选择对话框 -->
  <el-dialog
      v-model="goldenHallPointDialogVisible"
      title="选择黄金殿堂定点坐标（横屏 1280×720）"
      width="900px"
  >
    <div class="map-picker-wrapper">
      <div class="map-canvas" ref="goldenHallMapCanvasRef" @click="onMapClick">
        <img
            class="map-image"
            :src="mapImageUrl"
            alt="地图"
            draggable="false"
            @load="goldenHallMapImageLoaded = true"
            @error="goldenHallMapImageLoaded = false"
        />
        <div
            v-for="(p, idx) in customPointsDisplay"
            :key="idx"
            class="map-marker map-marker-numbered"
            :style="{ left: p.x + 'px', top: p.y + 'px' }"
            :class="{ 'map-marker-hovered': hoveredPointIndex === idx }"
            :title="`点 ${idx + 1}: (${p.x}, ${p.y})`"
        >
          <span class="marker-number">{{ idx + 1 }}</span>
        </div>
      </div>主线区域

      <div class="map-info">
        <div>
          已设<strong>{{ configContent.dungeons.goldenHall.customPoints.length }}</strong>/50 个点
          <span v-if="configContent.dungeons.goldenHall.customPoints.length === 0">（点击地图添加）</span>
        </div>
        <div v-if="configContent.dungeons.goldenHall.customPoints.length > 0" class="hint">
          {{ configContent.dungeons.goldenHall.customPoints.map(p => `(${p.x}, ${p.y})`).join('，') }}
        </div>
        <div class="hint">提示：左上角为 (0,0)。点击地图添加坐标，最多50个点。</div>
        
        <!-- 坐标点复制和导入功能 -->
        <div style="margin-top: 15px; padding-top: 15px; border-top: 1px solid #e4e7ed;">
          <div style="margin-bottom: 10px; display: flex; align-items: center; gap: 10px;">
            <el-button size="small" type="primary" :disabled="!hasGoldenHallCustomPoints" @click="copyPointsToClipboard">
              一键复制坐标点
            </el-button>
          </div>
          <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 10px;">
            <el-input
                v-model="importPointsText"
                type="textarea"
                :rows="2"
                placeholder="(100, 200)，(300, 400)，(500, 600)"
                style="flex: 1;"
            />
            <el-button size="small" type="success" @click="importPoints">一键导入</el-button>
          </div>
        </div>
      </div>
    </div>
    <template #footer>
      <span class="dialog-footer">
        <el-button size="small" @click="goldenHallPointDialogVisible = false">关闭</el-button>
        <el-button size="small" type="danger" :disabled="!hasGoldenHallCustomPoints" @click="clearAllGoldenHallPoints">清除所有点</el-button>
        <el-button size="small" type="primary" :disabled="!hasGoldenHallCustomPoints" @click="savePoint">保存</el-button>
      </span>
    </template>
  </el-dialog>

  <!-- 混沌神殿定点坐标选择对话框 -->
  <el-dialog
      v-model="sigrenPointDialogVisible"
      title="选择混沌神殿定点坐标（横屏 1280×720）"
      width="900px"
  >
    <div class="map-picker-wrapper">
      <div class="map-canvas" ref="sigrenMapCanvasRef" @click="onMapClick">
        <img
            class="map-image"
            :src="mapImageUrl"
            alt="地图"
            draggable="false"
            @load="sigrenMapImageLoaded = true"
            @error="sigrenMapImageLoaded = false"
        />
        <div
            v-for="(p, idx) in customPointsDisplay"
            :key="idx"
            class="map-marker map-marker-numbered"
            :style="{ left: p.x + 'px', top: p.y + 'px' }"
            :class="{ 'map-marker-hovered': hoveredPointIndex === idx }"
            :title="`点 ${idx + 1}: (${p.x}, ${p.y})`"
        >
          <span class="marker-number">{{ idx + 1 }}</span>
        </div>
      </div>
      <div class="map-info">
        <div>
          已设<strong>{{ configContent.dungeons.sigren.customPoints.length }}</strong>/50 个点
          <span v-if="configContent.dungeons.sigren.customPoints.length === 0">（点击地图添加）</span>
        </div>
        <div v-if="configContent.dungeons.sigren.customPoints.length > 0" class="hint">
          {{ configContent.dungeons.sigren.customPoints.map(p => `(${p.x}, ${p.y})`).join('，') }}
        </div>
        <div class="hint">提示：左上角为 (0,0)。点击地图添加坐标，最多50个点。</div>
        
        <!-- 坐标点复制和导入功能 -->
        <div style="margin-top: 15px; padding-top: 15px; border-top: 1px solid #e4e7ed;">
          <div style="margin-bottom: 10px; display: flex; align-items: center; gap: 10px;">
            <el-button size="small" type="primary" :disabled="!hasSigrenCustomPoints" @click="copyPointsToClipboard">
              一键复制坐标点
            </el-button>
          </div>
          <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 10px;">
            <el-input
                v-model="importPointsText"
                type="textarea"
                :rows="2"
                placeholder="(100, 200)，(300, 400)，(500, 600)"
                style="flex: 1;"
            />
            <el-button size="small" type="success" @click="importPoints">一键导入</el-button>
          </div>
        </div>
      </div>
    </div>
    <template #footer>
      <span class="dialog-footer">
        <el-button size="small" @click="sigrenPointDialogVisible = false">关闭</el-button>
        <el-button size="small" type="danger" :disabled="!hasSigrenCustomPoints" @click="clearAllSigrenPoints">清除所有点</el-button>
        <el-button size="small" type="primary" :disabled="!hasSigrenCustomPoints" @click="savePoint">保存</el-button>
      </span>
    </template>
  </el-dialog>

  <!-- 激战地定点坐标选择对话框 -->
  <el-dialog
      v-model="battlegroundPointDialogVisible"
      title="选择激战地定点坐标（横屏 1280×720）"
      width="900px"
  >
    <div class="map-picker-wrapper">
      <div class="map-canvas" ref="battlegroundMapCanvasRef" @click="onMapClick">
        <img
            class="map-image"
            :src="mapImageUrl"
            :key="'battleground-' + configContent.outdoor.battleground.location"
            alt="地图"
            draggable="false"
            @load="battlegroundMapImageLoaded = true"
            @error="battlegroundMapImageLoaded = false"
        />
        <div
            v-for="(p, idx) in customPointsDisplay"
            :key="idx"
            class="map-marker map-marker-numbered"
            :style="{ left: p.x + 'px', top: p.y + 'px' }"
            :class="{ 'map-marker-hovered': hoveredPointIndex === idx }"
            :title="`点 ${idx + 1}: (${p.x}, ${p.y})`"
        >
          <span class="marker-number">{{ idx + 1 }}</span>
        </div>
      </div>
      <div class="map-info">
        <div>
          已设<strong>{{ configContent.outdoor.battleground.customPoints.length }}</strong>/50 个点
          <span v-if="configContent.outdoor.battleground.customPoints.length === 0">（点击地图添加）</span>
        </div>
        <div v-if="configContent.outdoor.battleground.customPoints.length > 0" class="hint">
          {{ configContent.outdoor.battleground.customPoints.map(p => `(${p.x}, ${p.y})`).join('，') }}
        </div>
        <div class="hint">提示：左上角为 (0,0)。点击地图添加坐标，最多50个点。</div>
        
        <!-- 坐标点复制和导入功能 -->
        <div style="margin-top: 15px; padding-top: 15px; border-top: 1px solid #e4e7ed;">
          <div style="margin-bottom: 10px; display: flex; align-items: center; gap: 10px;">
            <el-button size="small" type="primary" :disabled="!hasBattlegroundCustomPoints" @click="copyPointsToClipboard">
              一键复制坐标点
            </el-button>
          </div>
          <div style="display: flex; align-items: center; gap: 10px; margin-bottom: 10px;">
            <el-input
                v-model="importPointsText"
                type="textarea"
                :rows="2"
                placeholder="(100, 200)，(300, 400)，(500, 600)"
                style="flex: 1;"
            />
            <el-button size="small" type="success" @click="importPoints">一键导入</el-button>
          </div>
        </div>
      </div>
    </div>
    <template #footer>
      <span class="dialog-footer">
        <el-button size="small" @click="battlegroundPointDialogVisible = false">关闭</el-button>
        <el-button size="small" type="danger" :disabled="!hasBattlegroundCustomPoints" @click="clearAllBattlegroundPoints">清除所有点</el-button>
        <el-button size="small" type="primary" :disabled="!hasBattlegroundCustomPoints" @click="savePoint">保存</el-button>
      </span>
    </template>
  </el-dialog>

  </div>
</template>

<script>
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { ElMessage } from 'element-plus'
import { getConfig, createConfig, updateConfig } from '@/api/configs'
import { gameItemsAPI } from '@/api/gameItems'
import {
  getActivityCraftTemplateTree,
  getConfigActivityCraft,
  saveConfigActivityCraft
} from '@/api/activityCraft'

export default {
  name: 'CreateConfig',
  props: {
    id: {
      type: String,
      default: null
    },
    viewMode: {
      type: [Boolean, String],
      default: false
    }
  },
  setup(props) {
    const router = useRouter()
    const route = useRoute()
    const saving = ref(false)
    const loading = ref(false)
    const activeTab = ref('basic') // 默认显示基础配置
    const useManualInput = ref(false) // 是否使用手动输入模式

    // 判断是否为编辑模式
    const isEditMode = computed(() => !!props.id)
    // 判断是否为查看模式（只读模式，管理员查看其他用户的配置）
    const isViewMode = computed(() => {
      // 支持从props或路由查询参数获取
      return props.viewMode === true || props.viewMode === 'true' || 
             route.query.viewMode === 'true' || route.query.viewMode === true
    })
    const isDefaultConfig = ref(false)
    const isSharedConfig = ref(false) // 是否为共享配置（不是自己的）

    // 配置表单
    const configForm = ref({
      name: isEditMode.value ? '' : '配置001',
      description: ''
    })


    // 配置内容
    const configContent = ref({
      characterMode: 'single',
      accountRegion: '亚服',
      mainCharacterPosition: '第一个',
      placeholderZone: 'ASIA038',
      createCharacter: {
        enabled: false,
        profession: '奶妈'
      },
      gameUpdate: 'gametoday',
      autoUpdate: true,
      proxyType: 'kit',
      enableProxy: false,
      mainQuest: {
        enabled: true,
        type: 'level',
        level: 20,
        chapterMajor: 18,
        chapterMinor: 35,
        deathLimit: 3,
        cooldownHours: 6
      },
      dailyQuest: {
        enabled: false,
        repeatChallenge: {
          enabled: false,
          type: '野外',
          count: '10次'
        },
        collectionTask: false
      },
      storyAchievement: {
        exchangeTask: false,
        ultimateTask: false,
        utgardShadow: false,
        worldTreeDisir: false,
        blueBird: false,
        purpleBird: false,
        sigrenTrial: false
      },
      systemSettings: {
        autoStart: false,
        ailinSystem: false,
        randomDelayStart: false,
        scheduledScreenshot: true,
        autoUpdateScript: false,
        killProxyAndGameOnStart: false,
        killProxyAndGameOnUpdate: false,
        openProxyBeforeStart: false,
        android11InputMethod: false,
        autoRestartOnIdle: false,
        loginType: null,
        googleAccountType: null,
        "谷歌登录界面选择": "直接登录",
        countdownSeconds: 20,
        gameEnterFailStopThreshold: 0
      },
      gameShop: {
        autoBuyMana: false,
        weaponStone: false,
        clothesStone: false,
        accessoryStone: false,
        hammerStone: false,
        weaponStoneBox: false,
        clothesStoneBox: false,
        accessoryStoneBox: false,
        hammerStoneBox: false,
        valkyrieDraw: false,
        dixierDraw: false,
        restPoints: false,
        restPointItems: {
          amberPocket: false,
          sealEnhanceStone: false,
          manaRegenPotion: false,
          enhanceStoneBox: false
        },
        honorMerchant: {
          enabled: false,
          heroSoul: false,
          duskStone: false,
          victorySealPack: false,
          sealEnhanceStone: false,
          gloriousEnhanceBox: false,
          darkEnhanceBox: false,
          patternBox: false,
          guildSkillBookBox: false,
          midMaterialBox: false,
          lowMaterialBox: false,
          manaRegenPotion: false,
          swiftMead: false
        }
      },
      dungeons: {
        valkyrie: {
          enabled: true,
          floor: '1',
          floorLevel: '25',
          difficulty: '普通',
          difficultyType: '普通',
          type: '经验屋',
          mapCircle: false,
          failLimit: 3,
          failCooldownHours: 6,
          timeLimit: {
            enabled: false,
            startTime: '09:00',
            endTime: '18:00'
          }
        },
        sigren: {
          enabled: true,
          floor: '1',
          difficulty: '普通',
          position: '随机',
          customPoints: [],
          failLimit: 3,
          failCooldownHours: 6,
          timeLimit: {
            enabled: false,
            startTime: '09:00',
            endTime: '18:00',
            weekdays: [1, 2, 3, 4, 5, 6, 7] // 默认周一到周日
          }
        },
        polk: {
          enabled: true,
          floor: '1',
          difficulty: '普通',
          position: '随机',
          type: '刷怪',
          customPoints: [],
          failLimit: 3,
          failCooldownHours: 6,
          timeLimit: {
            enabled: false,
            startTime: '09:00',
            endTime: '18:00',
            weekdays: [1, 2, 3, 4, 5, 6, 7] // 默认周一到周日
          }
        },
        goldenHall: {
          enabled: false,
          floor: '1',
          difficulty: '普通',
          customPoints: [],
          failLimit: 3,
          failCooldownHours: 6,
          timeLimit: {
            enabled: false,
            startTime: '09:00',
            endTime: '18:00',
            weekdays: [1, 2, 3, 4, 5, 6, 7] // 默认周一到周日
          }
        },
        seasonalValkyrie: {
          enabled: true,
          floor: '1',
          floorLevel: '25',
          difficultyType: '普通',
          type: '成长屋',
          mapCircle: false,
          failLimit: 3,
          failCooldownHours: 6,
          timeLimit: {
            enabled: false,
            startTime: '09:00',
            endTime: '18:00'
          }
        }
      },
      outdoor: {
        type: 'hang',
        location: '伊顿的峡谷',
        position: '随机',
        autoChest: false,
        autoSilverFarming: false,
        eliteMap: false,
        customPoints: [],
        timeLimit: {
          enabled: false,
          startTime: '09:00',
          endTime: '18:00'
        },
        amber: {
          enabled: false,
          location: '世界树一层',
          channel: '与挂机设置相同',
          customPoints: [],
          timeLimit: {
            enabled: false,
            startTime: '09:00',
            endTime: '18:00'
          }
        },
        battleground: {
          enabled: false,
          location: '诸神黄昏岔路',
          position: '随机',
          customPoints: [],
          deathCount: 3,
          timeLimit: {
            enabled: false,
            startTime: '09:00',
            endTime: '18:00'
          }
        },
        offlineHang: {
          enabled: false,
          location: '洞穴巨人墓地1层',
          timeLimit: {
            enabled: false,
            startTime: '09:00',
            endTime: '18:00'
          }
        }
      },
      hangSettings: {
        autoBuyPotion: true,
        powerSaveMode: true,
        healthThreshold: 65,
        potionInterval: 4,
        nightMode: false,
        hangRange: 50,
        collectRange: 50,
        autoUseConsumables: false,
        changeChannel: false,
        channel: '5',
        staminaPotion: 500,
        autoUseStaminaPotion: 80,
        moveScroll: 50,
        returnScroll: 50,
        archerReturnOriginEnabled: false,
        archerReturnOriginInterval: 200,
        decomposeInterval: 60,
        cleanBagInterval: 360,
        storageInterval: 360,
        nornTreasureInterval: 60,
        redeemCodeInterval: 1440
      },
      otherRequirements: {
        commission: true,
        commissionDeathLimit: 1,
        commissionCooldownHours: 12,
        sideQuest: true,
        sideQuestDeathLimit: 1,
        sideQuestCooldownHours: 12,
        commissionFailHours: 3,
        deathRecoverExp: true,
        autoDecomposeEquip: true,
        decomposeWhite: true,
        decomposeGreen: true,
        decomposeBlue: false,
        decomposeEnhanced: true,
        decomposeIdentified: true,
        decomposeSkillBook: false,
        autoEnhance: true,
        autoValkyrieCompose: true,
        autoPetCompose: true,
        autoEquipValkyrie: true,
        autoEquipPet: true,
        autoAmberBox: false,
        amberBoxPriceThreshold: 120,
        amberBoxPriceThresholdEnabled: false,
        autoEquipBox: false,
        autoBuySkill: false,
        autoBuySkillMaxSilver: 100000,
        autoWarehouse: false,
        warehouseInfiniteCleanup: false,
        warehouseRedScreenAlert: false,
        autoUltimate: false,
        silverRestoreEnabled: false,
        silverRestore: 20000,
        potentialUpgradeEnabled: false,
        potentialUpgradeSkill: 6,
        dixierUpgrade: false,
        valkyrieUpgrade: false,
        autoSkillUpgrade: false,
        autoArtifact: false,
        autoSmartCraftBlue: false,
        autoEquipAdvanced: false,
        autoIdentifyEquipment: false,
        identifyEquipmentType: '法术系',
        identifyEquipmentLevel: '随机',
        storyCardUpgradeEnabled: false,
        storyCardUpgradeLevel: 1,
        redeemCodeEnabled: false,
        redeemCodes: '',
        useBuiltInRedeemCodes: false,
        seasonInstruction: false,
        blessingTower: false,
        autoClaimMail: true
      },
      guild: {
        autoJoin: true,
        techContribute24: true,
        silverContribute: false,
        amberContribute: false,
        collectGuildChest: false,
        guildHelp: false,
        guildSkill: '公会发展',
        collaborationItemType: '仅银币',
        skillCollaborationInterval: 6
      },
      tradingPost: {
        autoSell: true,
        enablePriceDifference: false,
        priceDifference: 1,
        delistHours: 5,
        priceProtection: [
          { itemName: '琥珀箱子：50,000', minPrice: 300 }
        ],
        equipmentEnhancement: {
          enabled: false,
          equipmentQuality: {
            white: {
              enabled: true,
              level: 6
            },
            green: {
              enabled: true,
              level: 6
            }
          },
          defaultCount: 5,
          autoOpenStoneBox: false,
          weaponStone: true,         // 武器强化石（默认勾选）
          armorStone: true,          // 防具强化石（默认勾选）
          accessoryStone: true,      // 饰品强化石（默认勾选）
          lifeEquipmentStone: false  // 生活装备强化石
        },
        equipmentCustomPrices: [], // 装备自定义价格 [{ level: 6, whitePrice: 10, greenPrice: 15 }, ...]
        equipmentCustomPricesEnabled: false // 是否启用装备自定义价格
      },
      regionalExchange: {
        autoSell: false,
        exchangeTypeEnabled: false, // 交易类型checkbox
        exchangeType: '钻石', // 钻石/WEMIX
        itemType: false, // 普通道具
        equipment: false, // 装备
        enablePriceDifference: false,
        priceDifference: 1,
        delistHours: 5
      },
      activityCraft: {
        enabled: false,
        synthesizeBlue: false,
        nornTreasure: false,
        useTradableTreasure: false,
        tradableTreasureNormal: false,
        tradableTreasureAdvanced: false,
        antiqueCraft: false,
        scrollCraft: false,
        foodCraft: false
      },
      backpack: {
        slotsEnabled: false,
        slots: 100
      },
      collection: {
        enabled: false,
        maxPrice: 0.5,
        equipmentQuality: {
          white: true,
          green: true,
          blue: false
        }
      },
      bossArea: {
        expedition5: {
          enabled: false,
          level: 'auto',
          createRoom: false
        },
        expedition15: {
          enabled: false,
          level: 'auto',
          createRoom: false
        },
        expeditionSeasonal5: {
          enabled: false,
          level: '28',
          createRoom: false
        },
        worldBoss: false,
        timeLimit: {
          enabled: false,
          startTime: '09:00',
          endTime: '18:00'
        }
      },
      warehouseItems: [], // 需要存仓库的道具ID列表
      builtInWarehouse: false, // 内置存仓：会加强存储更多以下没有到绑定道具
      deleteItems: [], // 需要删除的垃圾道具ID列表
      builtInDelete: false, // 内置删除：会加强删除更多未在列表中单独配置的垃圾道具
      useItems: [], // 需要使用的道具ID列表
      growthSummonTicketBox: '不使用', // 成长召唤券选择箱子：不使用/瓦尔基里/迪西尔
      advancedUpgradeCrystalBox: '不使用', // 高级升阶结晶石选择箱子：不使用/随机/瓦尔基里/迪西尔
      advancedPatraCardBox: '不使用', // 高级帕特拉卡牌选择箱子：不使用/随机/瓦尔基里/迪西尔
      treasureIslandSummonTicketBox: '不使用', // 宝物岛召唤券选择箱子：不使用/随机/瓦尔基里/迪西尔
      maniSummonTicketBox: '不使用', // 玛尼召唤券选择箱子：不使用/随机/瓦尔基里/迪西尔
      treasureIslandRareSummonTicketBox: '不使用', // 宝物岛稀有召唤券选择箱子：不使用/随机/瓦尔基里/迪西尔
      collaborationSummonTicketBox: '不使用', // 协作召唤券选择箱子：不使用/随机/瓦尔基里/迪西尔
      oathSummonTicket11TimesBox: '不使用', // 誓言召唤券11次选择箱子：不使用/随机/瓦尔基里/迪西尔
      autoShout: {
        enabled: false,
        content: '',
        interval: 60 // 默认60秒
      },
      extraFeatures: {
        keepOpeningOfflineChest: false,
        autoCraftBlueBook: false,
        autoCraftRedBook: false
      }
    })

    // 活动制作相关
    const activityCraftTree = ref([])
    const activityCraftLoading = ref(false)
    const activityCraftSelections = ref({}) // key=templateId, value=isEnabled
    const activeActivityCollapseItems = ref([])
    const isAnyActivityCraftSelected = computed(() => {
      return Object.values(activityCraftSelections.value || {}).some(value => Number(value) === 1)
    })

    // 自动联动：有任意选中则开启活动制作总开关
    watch(isAnyActivityCraftSelected, (hasSelected) => {
      if (hasSelected) {
        configContent.value.activityCraft.enabled = true
      }
    }, { immediate: true })

    // 手动关闭总开关时，清空活动制作选择
    const onActivityCraftEnabledChange = (enabled) => {
      if (!enabled) {
        activityCraftSelections.value = {}
      }
    }

    // 野外挂机选项
    const outdoorOptionsMap = {
      chest: [
        { label: '赫尔莫德的岔路-20级', value: '赫尔莫德的岔路' },
        { label: '洞穴巨人墓地1层-21级', value: '洞穴巨人墓地1层' },
        { label: '洞穴巨人墓地2层-22级', value: '洞穴巨人墓地2层' },
        { label: '巨魔遗址下层-25级', value: '巨魔遗址下层' },
        { label: '巨魔遗址上层-26级', value: '巨魔遗址上层' },
        { label: '格拉西尔森林-30级', value: '格拉西尔森林' },
        { label: '阿萨神族山-32级', value: '阿萨神族山' },
        { label: '诸神黄昏山谷-40级', value: '诸神黄昏山谷' },
        { label: '诸神黄昏岔路-45级', value: '诸神黄昏岔路' }
      ],
      amber: [
        { label: '世界树一层', value: '世界树一层' },
        { label: '世界树二层', value: '世界树二层' },
        { label: '世界树三层', value: '世界树三层' },
        { label: '世界树四层', value: '世界树四层' }
      ],
      hang: [
        { label: '伊顿的峡谷-13级', value: '伊顿的峡谷' },
        { label: '乌坦加尔德-15级', value: '乌坦加尔德' },
        { label: '洞穴巨人墓地1层-21级', value: '洞穴巨人墓地1层' },
        { label: '洞穴巨人墓地2层-22级', value: '洞穴巨人墓地2层' },
        { label: '巨魔遗址下层-25级', value: '巨魔遗址下层' },
        { label: '巨魔遗址上层-26级', value: '巨魔遗址上层' },
        { label: '赫尔莫德岔路-27级', value: '赫尔莫德岔路' },
        { label: '格拉西尔森林-30级', value: '格拉西尔森林' },
        { label: '乌特加德要塞1层-31级', value: '乌特加德要塞1层' },
        { label: '阿萨神族山-32级', value: '阿萨神族山' },
        { label: '乌特加德要塞2层-34级', value: '乌特加德要塞2层' },
        { label: '白狼溪谷-34级', value: '白狼溪谷' },
        { label: '乌特加德要塞3层-35级', value: '乌特加德要塞3层' },
        { label: '世界树一层-35级', value: '世界树一层' },
        { label: '世界树二层-40级', value: '世界树二层' },
        { label: '世界树三层-45级', value: '世界树三层' },
        { label: '世界树四层-50级', value: '世界树四层' },
        { label: '王之墓地-66级', value: '王之墓地' },
        { label: '米尔克海姆-66级', value: '米尔克海姆' },
      ],
      maze: [
        { label: '斯巴达的迷宫一层-28级', value: '斯巴达的迷宫一层-28级' },
        { label: '斯巴达的迷宫二层-35级', value: '斯巴达的迷宫二层-35级' },
        { label: '斯巴达的迷宫三层-40级', value: '斯巴达的迷宫三层-40级' },
        { label: '斯巴达的迷宫四层-45级', value: '斯巴达的迷宫四层-45级' },
        { label: '斯巴达的迷宫五层-50级', value: '斯巴达的迷宫五层-50级' },
        { label: '卡拉的迷宫一层-35级', value: '卡拉的迷宫一层-35级' },
        { label: '卡拉的迷宫二层-40级', value: '卡拉的迷宫二层-40级' },
        { label: '卡拉的迷宫三层-45级', value: '卡拉的迷宫三层-45级' },
        { label: '卡拉的迷宫四层-50级', value: '卡拉的迷宫四层-50级' },
        { label: '阿尔比特迷宫1层-65级', value: '阿尔比特迷宫1层-65级' }
      ]
    }

    const outdoorOptions = computed(() => {
      return outdoorOptionsMap[configContent.value.outdoor.type] || []
    })

    const offlineHangOptions = [
      { label: '洞穴巨人墓地1层（推荐3万战力）', value: '洞穴巨人墓地1层' },
      { label: '洞穴巨人墓地2层（推荐3万5战力）', value: '洞穴巨人墓地2层' },
      { label: '乌特加德要塞1层（推荐4万战力）', value: '乌特加德要塞1层' },
      { label: '乌特加德要塞2层（推荐4万5战力）', value: '乌特加德要塞2层' },
      { label: '乌特加德要塞3层（推荐5万战力）', value: '乌特加德要塞3层' },
      { label: '格拉希尔森林1层（推荐4万战力）', value: '格拉希尔森林1层' },
      { label: '格拉希尔森林2层（推荐4万5战力）', value: '格拉希尔森林2层' },
      { label: '郝尔莫德岔路（推荐1万5战力）', value: '郝尔莫德岔路' },
      { label: '乌特加德（推荐1万战力）', value: '乌特加德' },
      { label: '洞穴巨人遗迹1层（推荐3万5战力）', value: '洞穴巨人遗迹1层' },
      { label: '洞穴巨人遗迹2层（推荐4万战力）', value: '洞穴巨人遗迹2层' },
      { label: '米尔克海姆（需要觉醒，推荐14万战力）', value: '米尔克海姆' },
      { label: '王之墓地（需要觉醒，推荐15万战力）', value: '王之墓地' },
      { label: '斯伐瓦迷宫1层（推荐5万战力）', value: '斯伐瓦迷宫1层' },
      { label: '斯伐瓦迷宫2层（推荐7万战力）', value: '斯伐瓦迷宫2层' },
      { label: '斯伐瓦迷宫3层（推荐9万战力）', value: '斯伐瓦迷宫3层' },
      { label: '斯伐瓦迷宫4层（推荐10万战力）', value: '斯伐瓦迷宫4层' },
      { label: '斯伐瓦迷宫5层（推荐11万战力）', value: '斯伐瓦迷宫5层' },
      { label: '卡拉迷宫1层（推荐7万战力）', value: '卡拉迷宫1层' },
      { label: '卡拉迷宫2层（推荐9万战力）', value: '卡拉迷宫2层' },
      { label: '卡拉迷宫3层（推荐10万战力）', value: '卡拉迷宫3层' },
      { label: '卡拉迷宫4层（推荐11万战力）', value: '卡拉迷宫4层' },
      { label: '世界树峡谷1层（推荐6万战力）', value: '世界树峡谷1层' },
      { label: '世界树峡谷2层（推荐7万战力）', value: '世界树峡谷2层' },
      { label: '世界树峡谷3层（推荐9万战力）', value: '世界树峡谷3层' },
      { label: '世界树峡谷4层（推荐10万战力）', value: '世界树峡谷4层' }
    ]

    // 道具数据
    const warehouseItems = ref([]) // 需要存仓库的道具
    const deleteItems = ref([]) // 需要删除的垃圾道具
    const useItems = ref([]) // 需要使用的道具

    // 定点坐标选择（斯巴达的迷宫1～3层）
    const MAP_WIDTH = 1280
    const MAP_HEIGHT = 720
    const pointDialogVisible = ref(false)
    const amberPointDialogVisible = ref(false) // 挖琥珀定点对话框
    const polkPointDialogVisible = ref(false) // 弗尔克范格定点对话框
    const sigrenPointDialogVisible = ref(false) // 混沌神殿定点对话框
    const battlegroundPointDialogVisible = ref(false) // 激战地定点对话框
    const goldenHallPointDialogVisible = ref(false) // 黄金殿堂定点对话框
    const mapCanvasRef = ref(null)
    const amberMapCanvasRef = ref(null) // 挖琥珀地图引用
    const polkMapCanvasRef = ref(null) // 弗尔克范格地图引用
    const sigrenMapCanvasRef = ref(null) // 混沌神殿地图引用
    const battlegroundMapCanvasRef = ref(null) // 激战地地图引用
    const goldenHallMapCanvasRef = ref(null) // 黄金殿堂地图引用
    const mapImageLoaded = ref(false)
    const amberMapImageLoaded = ref(false) // 挖琥珀地图加载状态
    const polkMapImageLoaded = ref(false) // 弗尔克范格地图加载状态
    const sigrenMapImageLoaded = ref(false) // 混沌神殿地图加载状态
    const battlegroundMapImageLoaded = ref(false) // 激战地地图加载状态
    const goldenHallMapImageLoaded = ref(false) // 黄金殿堂地图加载状态
    const isLoadingConfig = ref(false) // 是否正在加载配置，用于禁用 watch 清空逻辑
    const pointPickerMode = ref('outdoor') // 'outdoor'、'amber'、'polk'、'sigren'、'battleground' 或 'goldenHall'，用于区分不同的定点模式
    const hoveredPointIndex = ref(null) // 当前悬浮的坐标点索引
    const showPointButton = computed(() => {
      const type = configContent.value.outdoor.type
      const loc = configContent.value.outdoor.location || ''
      const sparta = type === 'maze' && ['斯巴达的迷宫一层', '斯巴达的迷宫二层', '斯巴达的迷宫三层', '斯巴达的迷宫四层', '斯巴达的迷宫五层'].some(k => loc.includes(k))
      const kara = type === 'maze' && ['卡拉的迷宫一层', '卡拉的迷宫二层', '卡拉的迷宫三层', '卡拉的迷宫四层'].some(k => loc.includes(k))
      const arbit = type === 'maze' && loc.includes('阿尔比特迷宫1层')
      const troll = type === 'hang' && (loc === '巨魔遗址下层')
      const trollUpper = type === 'hang' && (loc === '巨魔遗址上层')
      const utgard = type === 'hang' && (loc === '乌坦加尔德')
      const tomb1 = type === 'hang' && (loc === '洞穴巨人墓地1层')
      const tomb2 = type === 'hang' && (loc === '洞穴巨人墓地2层')
      const fortress1 = type === 'hang' && (loc === '乌特加德要塞1层')
      const asgard = type === 'hang' && (loc === '阿萨神族山')
      const fortress2 = type === 'hang' && (loc === '乌特加德要塞2层')
      const whiteWolf = type === 'hang' && (loc === '白狼溪谷')
      const fortress3 = type === 'hang' && (loc === '乌特加德要塞3层')
      const worldTree1 = type === 'hang' && (loc === '世界树一层')
      const worldTree2 = type === 'hang' && (loc === '世界树二层')
      const worldTree3 = type === 'hang' && (loc === '世界树三层')
      const worldTree4 = type === 'hang' && (loc === '世界树四层')
      const kingTomb = type === 'hang' && (loc === '王之墓地')
      const helmod = type === 'hang' && (loc === '赫尔莫德岔路')
      const milkheim = type === 'hang' && (loc === '米尔克海姆')
      const grasil = type === 'hang' && (loc === '格拉西尔森林')
      const eiden = type === 'hang' && (loc === '伊顿的峡谷')
      // 挖矿开箱的所有地图都支持定点
      const chest = type === 'chest' && loc && ['赫尔莫德的岔路', '洞穴巨人墓地1层', '洞穴巨人墓地2层', '巨魔遗址下层', '巨魔遗址上层', '格拉西尔森林', '阿萨神族山', '诸神黄昏山谷', '诸神黄昏岔路'].includes(loc)
      return sparta || kara || arbit || troll || trollUpper || utgard || tomb1 || tomb2 || fortress1 || asgard || fortress2 || whiteWolf || fortress3 || worldTree1 || worldTree2 || worldTree3 || worldTree4 || kingTomb || helmod || milkheim || grasil || eiden || chest
    })
    // 挖琥珀定点按钮显示条件
    const showAmberPointButton = computed(() => {
      const loc = configContent.value.outdoor.amber.location || ''
      return ['世界树一层', '世界树二层', '世界树三层', '世界树四层'].includes(loc)
    })
    // 弗尔克范格定点按钮显示条件（1-5层支持定点）
    const showPolkPointButton = computed(() => {
      const floor = configContent.value.dungeons.polk.floor || ''
      return ['1', '2', '3', '4', '5'].includes(floor)
    })
    // 混沌神殿定点按钮显示条件（1-5层支持定点）
    const showSigrenPointButton = computed(() => {
      const floor = configContent.value.dungeons.sigren.floor || ''
      return ['1', '2', '3', '4', '5'].includes(floor)
    })
    const showGoldenHallPointButton = computed(() => {
      const floor = configContent.value.dungeons.goldenHall.floor || ''
      return ['1', '2', '3'].includes(floor)
    })

    const mapImageUrl = computed(() => {
      const type = configContent.value.outdoor.type
      const loc = configContent.value.outdoor.location || ''
      const mapBaseUrl = 'https://liuce.oss-cn-hangzhou.aliyuncs.com/map/'

      // 激战地定点 - 优先判断
      if (pointPickerMode.value === 'battleground' || battlegroundPointDialogVisible.value) {
        const bgLoc = configContent.value.outdoor.battleground.location || ''
        if (bgLoc === '诸神黄昏山谷') {
          return mapBaseUrl + '诸神黄昏山谷.png'
        }
        if (bgLoc === '诸神黄昏岔路') {
          return mapBaseUrl + '诸神黄昏岔路.png'
        }
        if (bgLoc === '世界树峡谷深层-56级') {
          return mapBaseUrl + '世界树峡谷深层2.png'
        }
        if (bgLoc === '尼达维利尔峡谷-65级') {
          return mapBaseUrl + '尼达维利尔峡谷.png'
        }
        // 如果没有匹配到，默认返回诸神黄昏岔路
        return mapBaseUrl + '诸神黄昏岔路.png'
      }
      // 弗尔克范格定点 - 优先判断
      if (pointPickerMode.value === 'polk' || polkPointDialogVisible.value) {
        const floor = configContent.value.dungeons.polk.floor || ''
        if (['1', '2', '3', '4', '5'].includes(floor)) {
          return mapBaseUrl + '弗尔克范格1层.png'
        }
      }
      // 混沌神殿定点 - 优先判断
      if (pointPickerMode.value === 'sigren' || sigrenPointDialogVisible.value) {
        const floor = configContent.value.dungeons.sigren.floor || ''
        if (['1', '2'].includes(floor)) {
          return mapBaseUrl + '混沌神殿1层.png'
        }
        if (floor === '3') {
          return mapBaseUrl + '混沌神殿3层.png'
        }
        if (floor === '4') {
          return mapBaseUrl + '混沌神殿4层.png'
        }
        if (floor === '5') {
          return mapBaseUrl + '混沌神殿5层.png'
        }
      }
      // 黄金殿堂定点 - 优先判断
      if (pointPickerMode.value === 'goldenHall' || goldenHallPointDialogVisible.value) {
        const floor = configContent.value.dungeons.goldenHall.floor || '1'
        if (floor === '1') {
          return mapBaseUrl + '黄金殿堂1层.png'
        }
        if (floor === '2') {
          return mapBaseUrl + '黄金殿堂2层.png'
        }
        if (floor === '3') {
          return mapBaseUrl + '黄金殿堂3层.png'
        }
        // 默认返回1层
        return mapBaseUrl + '黄金殿堂1层.png'
      }
      // 挖琥珀区域 - 优先判断，避免与野外挂机冲突
      if (pointPickerMode.value === 'amber' || amberPointDialogVisible.value) {
        const amberLoc = configContent.value.outdoor.amber.location || ''
        if (amberLoc === '世界树一层') {
          return mapBaseUrl + '世界树峡谷1层.png'
        }
        if (amberLoc === '世界树二层') {
          return mapBaseUrl + '世界树峡谷2层.png'
        }
        if (amberLoc === '世界树三层') {
          return mapBaseUrl + '世界树峡谷3层.png'
        }
        if (amberLoc === '世界树四层') {
          return mapBaseUrl + '世界树峡谷4层.png'
        }
      }

      // 迷宫挂机
      if (type === 'maze' && ['斯巴达的迷宫一层', '斯巴达的迷宫二层', '斯巴达的迷宫三层'].some(k => loc.includes(k))) {
        return mapBaseUrl + '斯伐瓦迷宫1.png'
      }
      if (type === 'maze' && ['斯巴达的迷宫四层', '斯巴达的迷宫五层'].some(k => loc.includes(k))) {
        return mapBaseUrl + '斯伐瓦迷宫4.png'
      }
      if (type === 'maze' && ['卡拉的迷宫一层', '卡拉的迷宫二层', '卡拉的迷宫三层'].some(k => loc.includes(k))) {
        return mapBaseUrl + '卡拉迷宫1-3层.png'
      }
      if (type === 'maze' && loc.includes('卡拉的迷宫四层')) {
        return mapBaseUrl + '卡拉迷宫4层.png'
      }
      if (type === 'maze' && loc.includes('阿尔比特迷宫1层')) {
        return mapBaseUrl + '阿尔比特迷宫1层.png'
      }
      // 野外挂机
      if (type === 'hang' && loc === '伊顿的峡谷') {
        return mapBaseUrl + '伊登山谷.png'
      }
      if (type === 'hang' && loc === '巨魔遗址下层') {
        const isElite = configContent.value.outdoor.eliteMap
        return mapBaseUrl + (isElite ? '洞穴巨人遗迹下层-精锐.png' : '洞穴巨人遗迹下层.png')
      }
      if (type === 'hang' && loc === '巨魔遗址上层') {
        const isElite = configContent.value.outdoor.eliteMap
        return mapBaseUrl + (isElite ? '洞穴巨人遗迹上层-精锐.png' : '洞穴巨人遗迹上层.png')
      }
      if (type === 'hang' && loc === '乌坦加尔德') {
        return mapBaseUrl + '乌特加德.png'
      }
      if (type === 'hang' && loc === '洞穴巨人墓地1层') {
        const isElite = configContent.value.outdoor.eliteMap
        return mapBaseUrl + (isElite ? '洞穴巨人墓地1层-精锐.png' : '洞穴巨人墓地1层.png')
      }
      if (type === 'hang' && loc === '洞穴巨人墓地2层') {
        const isElite = configContent.value.outdoor.eliteMap
        return mapBaseUrl + (isElite ? '洞穴巨人墓地2层-精锐.png' : '洞穴巨人墓地2层.png')
      }
      if (type === 'hang' && loc === '乌特加德要塞1层') {
        const isElite = configContent.value.outdoor.eliteMap
        return mapBaseUrl + (isElite ? '乌特加德要塞1层-精锐.png' : '乌特加德要塞1层.png')
      }
      if (type === 'hang' && loc === '阿萨神族山') {
        return mapBaseUrl + '阿萨神族山.png'
      }
      if (type === 'hang' && loc === '乌特加德要塞2层') {
        const isElite = configContent.value.outdoor.eliteMap
        return mapBaseUrl + (isElite ? '乌特加德要塞2层-精锐.png' : '乌特加德要塞2层.png')
      }
      if (type === 'hang' && loc === '白狼溪谷') {
        return mapBaseUrl + '白狼溪谷.png'
      }
      if (type === 'hang' && loc === '乌特加德要塞3层') {
        const isElite = configContent.value.outdoor.eliteMap
        return mapBaseUrl + (isElite ? '乌特加德要塞3层-精锐.png' : '乌特加德要塞3层.png')
      }
      if (type === 'hang' && loc === '世界树一层') {
        return mapBaseUrl + '世界树峡谷1层.png'
      }
      if (type === 'hang' && loc === '世界树二层') {
        return mapBaseUrl + '世界树峡谷2层.png'
      }
      if (type === 'hang' && loc === '世界树三层') {
        return mapBaseUrl + '世界树峡谷3层.png'
      }
      if (type === 'hang' && loc === '世界树四层') {
        return mapBaseUrl + '世界树峡谷4层.png'
      }
      if (type === 'hang' && loc === '王之墓地') {
        return mapBaseUrl + '王之墓地.png'
      }
      if (type === 'hang' && loc === '赫尔莫德岔路') {
        return mapBaseUrl + '赫尔莫德岔路.png'
      }
      if (type === 'hang' && loc === '米尔克海姆') {
        return mapBaseUrl + '米尔克海姆.png'
      }
      if (type === 'hang' && loc === '格拉西尔森林') {
        return mapBaseUrl + '格拉希尔森林.png'
      }
      // 挖矿开箱
      if (type === 'chest') {
        if (loc === '赫尔莫德的岔路') {
          return mapBaseUrl + '赫尔莫德的岔路.png'
        }
        if (loc === '洞穴巨人墓地1层') {
          const isElite = configContent.value.outdoor.eliteMap
          return mapBaseUrl + (isElite ? '洞穴巨人墓地1层-精锐.png' : '洞穴巨人墓地1层.png')
        }
        if (loc === '洞穴巨人墓地2层') {
          const isElite = configContent.value.outdoor.eliteMap
          return mapBaseUrl + (isElite ? '洞穴巨人墓地2层-精锐.png' : '洞穴巨人墓地2层.png')
        }
        if (loc === '巨魔遗址下层') {
          const isElite = configContent.value.outdoor.eliteMap
          return mapBaseUrl + (isElite ? '洞穴巨人遗迹下层-精锐.png' : '洞穴巨人遗迹下层.png')
        }
        if (loc === '巨魔遗址上层') {
          const isElite = configContent.value.outdoor.eliteMap
          return mapBaseUrl + (isElite ? '洞穴巨人遗迹上层-精锐.png' : '洞穴巨人遗迹上层.png')
        }
        if (loc === '格拉西尔森林') {
          return mapBaseUrl + '格拉希尔森林.png'
        }
        if (loc === '阿萨神族山') {
          return mapBaseUrl + '阿萨神族山.png'
        }
        if (loc === '诸神黄昏山谷') {
          return mapBaseUrl + '诸神黄昏山谷.png'
        }
        if (loc === '诸神黄昏岔路') {
          return mapBaseUrl + '诸神黄昏岔路.png'
        }
      }
      // 默认用斯巴达图占位
      return mapBaseUrl + '斯伐瓦迷宫1.png'
    })
    const hasCustomPoints = computed(() => (configContent.value.outdoor.customPoints || []).length > 0)
    const hasAmberCustomPoints = computed(() => (configContent.value.outdoor.amber.customPoints || []).length > 0)
    const hasPolkCustomPoints = computed(() => (configContent.value.dungeons.polk.customPoints || []).length > 0)
    const hasSigrenCustomPoints = computed(() => (configContent.value.dungeons.sigren.customPoints || []).length > 0)
    const hasGoldenHallCustomPoints = computed(() => (configContent.value.dungeons.goldenHall.customPoints || []).length > 0)
    const hasBattlegroundCustomPoints = computed(() => (configContent.value.outdoor.battleground.customPoints || []).length > 0)
    const customPointsDisplay = computed(() => {
      if (pointPickerMode.value === 'battleground') {
        if (!battlegroundMapImageLoaded.value) return []
        const el = battlegroundMapCanvasRef.value
        const img = el ? el.querySelector('img') : null
        if (!img) return []
        const rect = img.getBoundingClientRect()
        if (rect.width === 0 || rect.height === 0) return []
        const scaleX = rect.width / MAP_WIDTH
        const scaleY = rect.height / MAP_HEIGHT
        const points = (configContent.value.outdoor.battleground.customPoints || [])
        return points.map(p => ({
          x: p.x * scaleX,
          y: p.y * scaleY
        }))
      } else if (pointPickerMode.value === 'polk') {
        if (!polkMapImageLoaded.value) return []
        const el = polkMapCanvasRef.value
        const img = el ? el.querySelector('img') : null
        if (!img) return []
        const rect = img.getBoundingClientRect()
        if (rect.width === 0 || rect.height === 0) return []
        const scaleX = rect.width / MAP_WIDTH
        const scaleY = rect.height / MAP_HEIGHT
        const points = (configContent.value.dungeons.polk.customPoints || [])
        return points.map(p => ({
          x: p.x * scaleX,
          y: p.y * scaleY
        }))
      } else if (pointPickerMode.value === 'sigren') {
        if (!sigrenMapImageLoaded.value) return []
        const el = sigrenMapCanvasRef.value
        const img = el ? el.querySelector('img') : null
        if (!img) return []
        const rect = img.getBoundingClientRect()
        if (rect.width === 0 || rect.height === 0) return []
        const scaleX = rect.width / MAP_WIDTH
        const scaleY = rect.height / MAP_HEIGHT
        const points = (configContent.value.dungeons.sigren.customPoints || [])
        return points.map(p => ({
          x: p.x * scaleX,
          y: p.y * scaleY
        }))
      } else if (pointPickerMode.value === 'goldenHall') {
        if (!goldenHallMapImageLoaded.value) return []
        const el = goldenHallMapCanvasRef.value
        const img = el ? el.querySelector('img') : null
        if (!img) return []
        const rect = img.getBoundingClientRect()
        if (rect.width === 0 || rect.height === 0) return []
        const scaleX = rect.width / MAP_WIDTH
        const scaleY = rect.height / MAP_HEIGHT
        const points = (configContent.value.dungeons.goldenHall.customPoints || [])
        return points.map(p => ({
          x: p.x * scaleX,
          y: p.y * scaleY
        }))
      } else if (pointPickerMode.value === 'amber') {
        if (!amberMapImageLoaded.value) return []
        const el = amberMapCanvasRef.value
        const img = el ? el.querySelector('img') : null
        if (!img) return []
        const rect = img.getBoundingClientRect()
        if (rect.width === 0 || rect.height === 0) return []
        const scaleX = rect.width / MAP_WIDTH
        const scaleY = rect.height / MAP_HEIGHT
        const points = (configContent.value.outdoor.amber.customPoints || [])
        return points.map(p => ({
          x: p.x * scaleX,
          y: p.y * scaleY
        }))
      } else {
        if (!mapImageLoaded.value) return []
        const el = mapCanvasRef.value
        const img = el ? el.querySelector('img') : null
        if (!img) return []
        const rect = img.getBoundingClientRect()
        // 如果图片还没有加载完成，rect 可能不准确
        if (rect.width === 0 || rect.height === 0) return []
        const scaleX = rect.width / MAP_WIDTH
        const scaleY = rect.height / MAP_HEIGHT
        const points = (configContent.value.outdoor.customPoints || [])
        return points.map(p => ({
          x: p.x * scaleX,
          y: p.y * scaleY
        }))
      }
    })
    const openPointPicker = () => {
      pointPickerMode.value = 'outdoor'
      // 确保 customPoints 被初始化
      if (!Array.isArray(configContent.value.outdoor.customPoints)) {
        configContent.value.outdoor.customPoints = []
      }
      mapImageLoaded.value = false
      pointDialogVisible.value = true
      // 等待对话框打开和图片加载完成
      nextTick(() => {
        const img = mapCanvasRef.value ? mapCanvasRef.value.querySelector('img') : null
        if (img) {
          if (img.complete) {
            // 图片已经加载完成
            mapImageLoaded.value = true
          } else {
            // 等待图片加载完成
            img.onload = () => {
              mapImageLoaded.value = true
            }
            img.onerror = () => {
              mapImageLoaded.value = false
            }
          }
        }
      })
    }
    const openAmberPointPicker = () => {
      pointPickerMode.value = 'amber'
      // 确保 customPoints 被初始化
      if (!Array.isArray(configContent.value.outdoor.amber.customPoints)) {
        configContent.value.outdoor.amber.customPoints = []
      }
      amberMapImageLoaded.value = false
      amberPointDialogVisible.value = true
      // 等待对话框打开和图片加载完成
      nextTick(() => {
        const img = amberMapCanvasRef.value ? amberMapCanvasRef.value.querySelector('img') : null
        if (img) {
          if (img.complete) {
            amberMapImageLoaded.value = true
          } else {
            img.onload = () => {
              amberMapImageLoaded.value = true
            }
            img.onerror = () => {
              amberMapImageLoaded.value = false
            }
          }
        }
      })
    }
    const openPolkPointPicker = () => {
      pointPickerMode.value = 'polk'
      // 确保 customPoints 被初始化
      if (!Array.isArray(configContent.value.dungeons.polk.customPoints)) {
        configContent.value.dungeons.polk.customPoints = []
      }
      polkMapImageLoaded.value = false
      polkPointDialogVisible.value = true
      // 等待对话框打开和图片加载完成
      nextTick(() => {
        const img = polkMapCanvasRef.value ? polkMapCanvasRef.value.querySelector('img') : null
        if (img) {
          if (img.complete) {
            polkMapImageLoaded.value = true
          } else {
            img.onload = () => {
              polkMapImageLoaded.value = true
            }
            img.onerror = () => {
              polkMapImageLoaded.value = false
            }
          }
        }
      })
    }
    const openSigrenPointPicker = () => {
      pointPickerMode.value = 'sigren'
      // 确保 customPoints 被初始化
      if (!Array.isArray(configContent.value.dungeons.sigren.customPoints)) {
        configContent.value.dungeons.sigren.customPoints = []
      }
      sigrenMapImageLoaded.value = false
      sigrenPointDialogVisible.value = true
      // 等待对话框打开和图片加载完成
      nextTick(() => {
        const img = sigrenMapCanvasRef.value ? sigrenMapCanvasRef.value.querySelector('img') : null
        if (img) {
          if (img.complete) {
            sigrenMapImageLoaded.value = true
          } else {
            img.onload = () => {
              sigrenMapImageLoaded.value = true
            }
            img.onerror = () => {
              sigrenMapImageLoaded.value = false
            }
          }
        }
      })
    }
    const openGoldenHallPointPicker = () => {
      pointPickerMode.value = 'goldenHall'
      // 确保 customPoints 被初始化
      if (!Array.isArray(configContent.value.dungeons.goldenHall.customPoints)) {
        configContent.value.dungeons.goldenHall.customPoints = []
      }
      goldenHallMapImageLoaded.value = false
      goldenHallPointDialogVisible.value = true
      // 等待对话框打开和图片加载完成
      nextTick(() => {
        const img = goldenHallMapCanvasRef.value ? goldenHallMapCanvasRef.value.querySelector('img') : null
        if (img) {
          if (img.complete) {
            goldenHallMapImageLoaded.value = true
          } else {
            img.onload = () => {
              goldenHallMapImageLoaded.value = true
            }
            img.onerror = () => {
              goldenHallMapImageLoaded.value = false
            }
          }
        }
      })
    }
    const openBattlegroundPointPicker = () => {
      // 先设置模式，确保地图URL计算正确
      pointPickerMode.value = 'battleground'
      // 确保 customPoints 被初始化
      if (!Array.isArray(configContent.value.outdoor.battleground.customPoints)) {
        configContent.value.outdoor.battleground.customPoints = []
      }
      battlegroundMapImageLoaded.value = false
      // 等待 nextTick 确保 pointPickerMode 已更新
      nextTick(() => {
        battlegroundPointDialogVisible.value = true
        // 再次等待对话框打开和图片加载完成
        nextTick(() => {
          const img = battlegroundMapCanvasRef.value ? battlegroundMapCanvasRef.value.querySelector('img') : null
          if (img) {
            if (img.complete) {
              battlegroundMapImageLoaded.value = true
            } else {
              img.onload = () => {
                battlegroundMapImageLoaded.value = true
              }
              img.onerror = () => {
                battlegroundMapImageLoaded.value = false
              }
            }
          }
        })
      })
    }

    // 监听对话框关闭，重置图片加载状态
    watch(pointDialogVisible, (visible) => {
      if (!visible) {
        mapImageLoaded.value = false
      }
    })
    watch(amberPointDialogVisible, (visible) => {
      if (!visible) {
        amberMapImageLoaded.value = false
      }
    })
    watch(polkPointDialogVisible, (visible) => {
      if (!visible) {
        polkMapImageLoaded.value = false
      }
    })
    watch(sigrenPointDialogVisible, (visible) => {
      if (!visible) {
        sigrenMapImageLoaded.value = false
      }
    })
    watch(goldenHallPointDialogVisible, (visible) => {
      if (!visible) {
        goldenHallMapImageLoaded.value = false
      }
    })
    // 监听瓦尔基里殿堂层数变化，自动设置默认等级
    watch(() => configContent.value.dungeons.valkyrie.floor, (newFloor, oldFloor) => {
      // 如果正在加载配置，不重置等级
      if (isLoadingConfig.value) {
        return
      }
      // 只在用户手动改变层数时重置等级（从有值变为另一个值）
      // 如果当前值是-1（随便），不重置
      if (oldFloor !== undefined && oldFloor !== null && oldFloor !== newFloor) {
        const currentLevel = configContent.value.dungeons.valkyrie.floorLevel
        if (currentLevel === '-1' || currentLevel === -1) {
          return // 如果选择的是"随便"，保持-1不变
        }
        if (newFloor === '2') {
          configContent.value.dungeons.valkyrie.floorLevel = '35'
        } else if (newFloor === '3') {
          configContent.value.dungeons.valkyrie.floorLevel = '45'
        } else if (newFloor === '4') {
          configContent.value.dungeons.valkyrie.floorLevel = '55'
        } else if (newFloor === '5') {
          configContent.value.dungeons.valkyrie.floorLevel = '65'
        } else if (newFloor === '1') {
          configContent.value.dungeons.valkyrie.floorLevel = '25'
        }
      }
    })
    // 监听赛季英灵殿堂数变化，自动设置默认等级
    watch(() => configContent.value.dungeons.seasonalValkyrie.floor, (newFloor, oldFloor) => {
      // 如果正在加载配置，不重置等级
      if (isLoadingConfig.value) {
        return
      }
      // 只在用户手动改变层数时重置等级（从有值变为另一个值）
      // 如果当前值是-1（随便），不重置
      if (oldFloor !== undefined && oldFloor !== null && oldFloor !== newFloor) {
        const currentLevel = configContent.value.dungeons.seasonalValkyrie.floorLevel
        if (currentLevel === '-1' || currentLevel === -1) {
          return // 如果选择的是"随便"，保持-1不变
        }
        if (newFloor === '2') {
          configContent.value.dungeons.seasonalValkyrie.floorLevel = '35'
        } else if (newFloor === '3') {
          configContent.value.dungeons.seasonalValkyrie.floorLevel = '45'
        } else if (newFloor === '4') {
          configContent.value.dungeons.seasonalValkyrie.floorLevel = '55'
        } else if (newFloor === '5') {
          configContent.value.dungeons.seasonalValkyrie.floorLevel = '65'
        } else if (newFloor === '1') {
          configContent.value.dungeons.seasonalValkyrie.floorLevel = '25'
        }
      }
    })
    watch(battlegroundPointDialogVisible, (visible) => {
      if (!visible) {
        battlegroundMapImageLoaded.value = false
      }
    })
    const onMapClick = (event) => {
      const img = event.currentTarget.querySelector('img')
      if (!img) return
      const rect = img.getBoundingClientRect()
      const x = Math.round((event.clientX - rect.left) * (MAP_WIDTH / rect.width))
      const y = Math.round((event.clientY - rect.top) * (MAP_HEIGHT / rect.height))
      const p = {
        x: Math.max(0, Math.min(MAP_WIDTH, x)),
        y: Math.max(0, Math.min(MAP_HEIGHT, y))
      }

      if (pointPickerMode.value === 'battleground') {
        // 激战地定点
        if (!Array.isArray(configContent.value.outdoor.battleground.customPoints)) {
          configContent.value.outdoor.battleground.customPoints = []
        }
        const list = configContent.value.outdoor.battleground.customPoints
        const maxPoints = 50 // 激战地最多50个点
        if (list.length >= maxPoints) {
          ElMessage.warning(`最多只能设置${maxPoints}个点`)
          return
        }
        configContent.value.outdoor.battleground.customPoints = [...list, p]
        ElMessage.success(`已添加点位 (${p.x}, ${p.y})`)
      } else if (pointPickerMode.value === 'polk') {
        // 弗尔克范格定点
        if (!Array.isArray(configContent.value.dungeons.polk.customPoints)) {
          configContent.value.dungeons.polk.customPoints = []
        }
        const list = configContent.value.dungeons.polk.customPoints
        const maxPoints = 50 // 弗尔克范格最多50个点
        if (list.length >= maxPoints) {
          ElMessage.warning(`最多只能设置${maxPoints}个点`)
          return
        }
        configContent.value.dungeons.polk.customPoints = [...list, p]
        ElMessage.success(`已添加点位 (${p.x}, ${p.y})`)
      } else if (pointPickerMode.value === 'sigren') {
        // 混沌神殿定点
        if (!Array.isArray(configContent.value.dungeons.sigren.customPoints)) {
          configContent.value.dungeons.sigren.customPoints = []
        }
        const list = configContent.value.dungeons.sigren.customPoints
        const maxPoints = 50 // 混沌神殿最多50个点
        if (list.length >= maxPoints) {
          ElMessage.warning(`最多只能设置${maxPoints}个点`)
          return
        }
        configContent.value.dungeons.sigren.customPoints = [...list, p]
        ElMessage.success(`已添加点位 (${p.x}, ${p.y})`)
      } else if (pointPickerMode.value === 'goldenHall') {
        // 黄金殿堂定点
        if (!Array.isArray(configContent.value.dungeons.goldenHall.customPoints)) {
          configContent.value.dungeons.goldenHall.customPoints = []
        }
        const list = configContent.value.dungeons.goldenHall.customPoints
        const maxPoints = 50 // 黄金殿堂最多50个点
        if (list.length >= maxPoints) {
          ElMessage.warning(`最多只能设置${maxPoints}个点`)
          return
        }
        configContent.value.dungeons.goldenHall.customPoints = [...list, p]
        ElMessage.success(`已添加点位 (${p.x}, ${p.y})`)
      } else if (pointPickerMode.value === 'amber') {
        // 挖琥珀定点
        if (!Array.isArray(configContent.value.outdoor.amber.customPoints)) {
          configContent.value.outdoor.amber.customPoints = []
        }
        const list = configContent.value.outdoor.amber.customPoints
        const maxPoints = 50 // 挖琥珀最多50个点
        if (list.length >= maxPoints) {
          ElMessage.warning(`最多只能设置${maxPoints}个点`)
          return
        }
        configContent.value.outdoor.amber.customPoints = [...list, p]
        ElMessage.success(`已添加点位 (${p.x}, ${p.y})`)
      } else {
        // 野外挂机定点
        if (!Array.isArray(configContent.value.outdoor.customPoints)) {
          configContent.value.outdoor.customPoints = []
        }
        const list = configContent.value.outdoor.customPoints
        const type = configContent.value.outdoor.type
        // 定点挂机最多50个点
        const maxPoints = 50
        if (list.length >= maxPoints) {
          ElMessage.warning(`最多只能设置${maxPoints}个点`)
          return
        }
        configContent.value.outdoor.customPoints = [...list, p]
        ElMessage.success(`已添加点位 (${p.x}, ${p.y})`)
      }
    }
    // 删除定点坐标
    const removePoint = (index) => {
      if (pointPickerMode.value === 'battleground') {
        const points = configContent.value.outdoor.battleground.customPoints || []
        if (index >= 0 && index < points.length) {
          const removed = points[index]
          configContent.value.outdoor.battleground.customPoints = points.filter((_, i) => i !== index)
          ElMessage.success(`已删除点位 (${removed.x}, ${removed.y})`)
        }
      } else if (pointPickerMode.value === 'polk') {
        const points = configContent.value.dungeons.polk.customPoints || []
        if (index >= 0 && index < points.length) {
          const removed = points[index]
          configContent.value.dungeons.polk.customPoints = points.filter((_, i) => i !== index)
          ElMessage.success(`已删除点位 (${removed.x}, ${removed.y})`)
        }
      } else if (pointPickerMode.value === 'sigren') {
        const points = configContent.value.dungeons.sigren.customPoints || []
        if (index >= 0 && index < points.length) {
          const removed = points[index]
          configContent.value.dungeons.sigren.customPoints = points.filter((_, i) => i !== index)
          ElMessage.success(`已删除点位 (${removed.x}, ${removed.y})`)
        }
      } else if (pointPickerMode.value === 'goldenHall') {
        const points = configContent.value.dungeons.goldenHall.customPoints || []
        if (index >= 0 && index < points.length) {
          const removed = points[index]
          configContent.value.dungeons.goldenHall.customPoints = points.filter((_, i) => i !== index)
          ElMessage.success(`已删除点位 (${removed.x}, ${removed.y})`)
        }
      } else if (pointPickerMode.value === 'amber') {
        const points = configContent.value.outdoor.amber.customPoints || []
        if (index >= 0 && index < points.length) {
          const removed = points[index]
          configContent.value.outdoor.amber.customPoints = points.filter((_, i) => i !== index)
          ElMessage.success(`已删除点位 (${removed.x}, ${removed.y})`)
        }
      } else {
        const points = configContent.value.outdoor.customPoints || []
        if (index >= 0 && index < points.length) {
          const removed = points[index]
          configContent.value.outdoor.customPoints = points.filter((_, i) => i !== index)
          ElMessage.success(`已删除点位 (${removed.x}, ${removed.y})`)
        }
      }
    }

    const savePoint = () => {
      if (pointPickerMode.value === 'battleground') {
        const points = configContent.value.outdoor.battleground.customPoints || []
        battlegroundPointDialogVisible.value = false
        ElMessage.success('激战地定点坐标已保存')
      } else if (pointPickerMode.value === 'polk') {
        const points = configContent.value.dungeons.polk.customPoints || []
        polkPointDialogVisible.value = false
        ElMessage.success('弗尔克范格定点坐标已保存')
      } else if (pointPickerMode.value === 'sigren') {
        const points = configContent.value.dungeons.sigren.customPoints || []
        sigrenPointDialogVisible.value = false
        ElMessage.success('混沌神殿定点坐标已保存')
      } else if (pointPickerMode.value === 'goldenHall') {
        const points = configContent.value.dungeons.goldenHall.customPoints || []
        goldenHallPointDialogVisible.value = false
        ElMessage.success('黄金殿堂定点坐标已保存')
      } else if (pointPickerMode.value === 'amber') {
        const points = configContent.value.outdoor.amber.customPoints || []
        amberPointDialogVisible.value = false
        ElMessage.success('挖琥珀定点坐标已保存')
      } else {
        const type = configContent.value.outdoor.type
        const points = configContent.value.outdoor.customPoints || []
        // 挖矿开箱至少需要1个点
        if (type === 'chest' && points.length < 1) {
          ElMessage.error('挖矿开箱至少需要设置1个定点')
          return
        }
        pointDialogVisible.value = false
        ElMessage.success('定点坐标已保存')
      }
    }
    const clearAllPoints = () => {
      if (pointPickerMode.value === 'battleground') {
        configContent.value.outdoor.battleground.customPoints = []
        ElMessage.success('已清除所有激战地点')
      } else if (pointPickerMode.value === 'polk') {
        configContent.value.dungeons.polk.customPoints = []
        ElMessage.success('已清除所有弗尔克范格点')
      } else if (pointPickerMode.value === 'sigren') {
        configContent.value.dungeons.sigren.customPoints = []
        ElMessage.success('已清除所有混沌神殿点')
      } else if (pointPickerMode.value === 'goldenHall') {
        configContent.value.dungeons.goldenHall.customPoints = []
        ElMessage.success('已清除所有黄金殿堂点')
      } else if (pointPickerMode.value === 'amber') {
        configContent.value.outdoor.amber.customPoints = []
        ElMessage.success('已清除所有挖琥珀点')
      } else {
        configContent.value.outdoor.customPoints = []
        ElMessage.success('已清除所有挂机点')
      }
    }
    const clearAllBattlegroundPoints = () => {
      configContent.value.outdoor.battleground.customPoints = []
      ElMessage.success('已清除所有激战地点')
    }
    const clearAllAmberPoints = () => {
      configContent.value.outdoor.amber.customPoints = []
      ElMessage.success('已清除所有挖琥珀点')
    }
    const clearAllPolkPoints = () => {
      configContent.value.dungeons.polk.customPoints = []
      ElMessage.success('已清除所有弗尔克范格点')
    }
    const clearAllSigrenPoints = () => {
      configContent.value.dungeons.sigren.customPoints = []
      ElMessage.success('已清除所有混沌神殿点')
    }
    const clearAllGoldenHallPoints = () => {
      configContent.value.dungeons.goldenHall.customPoints = []
      ElMessage.success('已清除所有黄金殿堂点')
    }

    // 价格保护相关方法
    const addPriceProtection = () => {
      if (!Array.isArray(configContent.value.tradingPost.priceProtection)) {
        configContent.value.tradingPost.priceProtection = []
      }
      configContent.value.tradingPost.priceProtection.push({
        itemName: '',
        minPrice: 200
      })
    }

    const removePriceProtection = (index) => {
      if (Array.isArray(configContent.value.tradingPost.priceProtection)) {
        const item = configContent.value.tradingPost.priceProtection[index]
        // 琥珀箱子不可删除
        if (item && item.itemName === '琥珀箱子：50,000') {
          ElMessage.warning('琥珀箱子：50,000是必须项，不可删除')
          return
        }
        configContent.value.tradingPost.priceProtection.splice(index, 1)
      }
    }

    // 装备自定义价格相关方法
    const addEquipmentCustomPrice = () => {
      if (!Array.isArray(configContent.value.tradingPost.equipmentCustomPrices)) {
        configContent.value.tradingPost.equipmentCustomPrices = []
      }
      // 找到下一个不重复的等级
      const existingLevels = configContent.value.tradingPost.equipmentCustomPrices.map(item => item.level).filter(level => level > 0)
      let nextLevel = 6
      if (existingLevels.length > 0) {
        const maxLevel = Math.max(...existingLevels)
        // 从最大等级+1开始查找，如果到20还没找到，就从6开始找第一个空缺
        for (let i = maxLevel + 1; i <= 20; i++) {
          if (!existingLevels.includes(i)) {
            nextLevel = i
            break
          }
        }
        // 如果没找到，从6开始找第一个空缺
        if (nextLevel === maxLevel + 1 && nextLevel > 20) {
          for (let i = 6; i <= 20; i++) {
            if (!existingLevels.includes(i)) {
              nextLevel = i
              break
            }
          }
        }
      }
      configContent.value.tradingPost.equipmentCustomPrices.push({
        level: nextLevel,
        whitePrice: 10,
        greenPrice: 15
      })
    }

    // 检查等级是否重复
    const checkLevelDuplicate = (level, currentIndex) => {
      if (!level || level < 1) return false
      const prices = configContent.value.tradingPost.equipmentCustomPrices
      if (!Array.isArray(prices)) return false
      return prices.some((item, index) => item.level === level && index !== currentIndex)
    }

    // 验证并修正装备等级
    const validateEquipmentLevel = (index) => {
      const prices = configContent.value.tradingPost.equipmentCustomPrices
      if (!Array.isArray(prices) || !prices[index]) return
      
      const currentItem = prices[index]
      const currentLevel = currentItem.level
      
      if (!currentLevel || currentLevel < 1 || currentLevel > 20) {
        // 如果等级无效，设置为6
        currentItem.level = 6
        return
      }
      
      // 检查是否重复（排除当前项）
      const isDuplicate = prices.some((item, idx) => item.level === currentLevel && idx !== index)
      if (isDuplicate) {
        // 获取所有已使用的等级（排除当前项）
        const existingLevels = prices
          .map((item, idx) => idx !== index ? item.level : null)
          .filter(level => level && level > 0)
        
        // 从当前等级+1开始查找可用等级
        let nextLevel = currentLevel + 1
        let found = false
        
        for (let i = nextLevel; i <= 20; i++) {
          if (!existingLevels.includes(i)) {
            nextLevel = i
            found = true
            break
          }
        }
        
        // 如果没找到，从6开始找第一个空缺
        if (!found) {
          for (let i = 6; i < currentLevel; i++) {
            if (!existingLevels.includes(i)) {
              nextLevel = i
              found = true
              break
            }
          }
        }
        
        // 如果还是没找到，说明所有等级都被占用了，保持原值但提示
        if (!found) {
          ElMessage.warning(`等级 +${currentLevel} 已存在，请选择其他等级`)
          return
        }
        
        ElMessage.warning(`等级 +${currentLevel} 已存在，已自动调整为 +${nextLevel}`)
        currentItem.level = nextLevel
      }
    }

    const removeEquipmentCustomPrice = (index) => {
      if (Array.isArray(configContent.value.tradingPost.equipmentCustomPrices)) {
        configContent.value.tradingPost.equipmentCustomPrices.splice(index, 1)
      }
    }

    // 关闭定点对话框
    const closePointDialog = () => {
      if (pointPickerMode.value === 'battleground') {
        battlegroundPointDialogVisible.value = false
      } else if (pointPickerMode.value === 'polk') {
        polkPointDialogVisible.value = false
      } else if (pointPickerMode.value === 'sigren') {
        sigrenPointDialogVisible.value = false
      } else if (pointPickerMode.value === 'goldenHall') {
        goldenHallPointDialogVisible.value = false
      } else if (pointPickerMode.value === 'amber') {
        amberPointDialogVisible.value = false
      } else {
        pointDialogVisible.value = false
      }
    }

    // 获取清除按钮文本
    const getClearButtonText = () => {
      if (pointPickerMode.value === 'battleground') {
        return '清除所有激战地点'
      } else if (pointPickerMode.value === 'polk') {
        return '清除所有弗尔克范格点'
      } else if (pointPickerMode.value === 'sigren') {
        return '清除所有混沌神殿点'
      } else if (pointPickerMode.value === 'goldenHall') {
        return '清除所有黄金殿堂点'
      } else if (pointPickerMode.value === 'amber') {
        return '清除所有挖琥珀点'
      } else {
        return '清除所有挂机点'
      }
    }

    // 获取是否有定点坐标
    const hasCurrentCustomPoints = computed(() => {
      if (pointPickerMode.value === 'battleground') {
        return hasBattlegroundCustomPoints.value
      } else if (pointPickerMode.value === 'polk') {
        return hasPolkCustomPoints.value
      } else if (pointPickerMode.value === 'sigren') {
        return hasSigrenCustomPoints.value
      } else if (pointPickerMode.value === 'goldenHall') {
        return hasGoldenHallCustomPoints.value
      } else if (pointPickerMode.value === 'amber') {
        return hasAmberCustomPoints.value
      } else {
        return hasCustomPoints.value
      }
    })

    // 获取当前坐标点的原始数据
    const getCurrentCustomPoints = () => {
      if (pointPickerMode.value === 'battleground') {
        return configContent.value.outdoor.battleground.customPoints || []
      } else if (pointPickerMode.value === 'polk') {
        return configContent.value.dungeons.polk.customPoints || []
      } else if (pointPickerMode.value === 'sigren') {
        return configContent.value.dungeons.sigren.customPoints || []
      } else if (pointPickerMode.value === 'goldenHall') {
        return configContent.value.dungeons.goldenHall.customPoints || []
      } else if (pointPickerMode.value === 'amber') {
        return configContent.value.outdoor.amber.customPoints || []
      } else {
        return configContent.value.outdoor.customPoints || []
      }
    }

    // 设置当前坐标点
    const setCurrentCustomPoints = (points) => {
      if (pointPickerMode.value === 'battleground') {
        configContent.value.outdoor.battleground.customPoints = points
      } else if (pointPickerMode.value === 'polk') {
        configContent.value.dungeons.polk.customPoints = points
      } else if (pointPickerMode.value === 'sigren') {
        configContent.value.dungeons.sigren.customPoints = points
      } else if (pointPickerMode.value === 'goldenHall') {
        configContent.value.dungeons.goldenHall.customPoints = points
      } else if (pointPickerMode.value === 'amber') {
        configContent.value.outdoor.amber.customPoints = points
      } else {
        configContent.value.outdoor.customPoints = points
      }
    }

    // 一键复制坐标点
    const copyPointsToClipboard = () => {
      const points = getCurrentCustomPoints()
      if (points.length === 0) {
        ElMessage.warning('没有可复制的坐标点')
        return
      }
      const pointsStr = points.map(p => `(${p.x}, ${p.y})`).join('，')
      navigator.clipboard.writeText(pointsStr).then(() => {
        ElMessage.success('坐标点已复制到剪贴板')
      }).catch(() => {
        ElMessage.error('复制失败，请手动复制')
      })
    }

    // 导入坐标点
    const importPointsText = ref('')
    const importPoints = () => {
      const text = importPointsText.value.trim()
      if (!text) {
        ElMessage.warning('请输入坐标点')
        return
      }

      // 解析坐标点格式：(x, y)，(x, y) 或 (x, y)， (x, y) 等
      const pointRegex = /\((\d+),\s*(\d+)\)/g
      const points = []
      let match

      while ((match = pointRegex.exec(text)) !== null) {
        const x = parseInt(match[1], 10)
        const y = parseInt(match[2], 10)
        if (!isNaN(x) && !isNaN(y) && x >= 0 && y >= 0) {
          points.push({ x, y })
        }
      }

      if (points.length === 0) {
        ElMessage.error('未找到有效的坐标点，请检查格式是否正确')
        return
      }

      // 根据不同的模式设置最大点数
      let maxPoints = 50

      if (points.length > maxPoints) {
        ElMessage.warning(`最多只能导入${maxPoints}个点，已自动截取前${maxPoints}个`)
        points.splice(maxPoints)
      }

      setCurrentCustomPoints(points)
      importPointsText.value = ''
      ElMessage.success(`成功导入${points.length}个坐标点`)
    }

    // 野外类型改变时重置位置
    const onOutdoorTypeChange = () => {
      configContent.value.outdoor.location = ''
      configContent.value.outdoor.customPoints = []
    }

    // 地图或类型变更时，清空定点挂机点位
    watch(
        () => ({ type: configContent.value.outdoor.type, location: configContent.value.outdoor.location }),
        (curr, prev) => {
          if (!prev) return
          // 如果正在加载配置，不清空数组
          if (isLoadingConfig.value) {
            return
          }
          // 如果 prev 是初始值（type 或 location 为空），说明是首次加载，不清空
          if (!prev.type || !prev.location || prev.type === '' || prev.location === '') {
            return
          }
          // 只有当类型或位置真正发生变化时才清空
          if (curr.type !== prev.type || curr.location !== prev.location) {
            configContent.value.outdoor.customPoints = []
          }
        }
    )

    // 加载道具数据
    const loadGameItems = async () => {
      try {
        const response = await gameItemsAPI.getGameItems('尤弥尔传奇国际服')
        const allItems = response.data.data || response.data || []

        // 按背景色排序函数
        const sortByBackgroundColor = (items) => {
          const colorOrder = {
            '': 0,           // 白色（无背景色）
            null: 0,
            undefined: 0,
            'light-green': 1, // 绿色
            'light-blue': 2,  // 蓝色
            'light-purple': 3, // 紫色
            'light-gold': 4   // 金色
          }
          return items.sort((a, b) => {
            const orderA = colorOrder[a.background_color] ?? 0
            const orderB = colorOrder[b.background_color] ?? 0
            return orderA - orderB
          })
        }

        // 过滤需要存仓库的道具：status=1 且 is_warehouse=1
        warehouseItems.value = sortByBackgroundColor(allItems.filter(item =>
            item.status === 1 && item.is_warehouse === 1
        ))

        // 过滤需要删除的垃圾道具：status=1 且 is_delete=1
        deleteItems.value = sortByBackgroundColor(allItems.filter(item =>
            item.status === 1 && item.is_delete === 1
        ))

        // 过滤需要使用的道具：status=1 且 is_use=1
        useItems.value = sortByBackgroundColor(allItems.filter(item =>
            item.status === 1 && item.is_use === 1
        ))

        // 如果是新建配置，默认选中所有需要存仓库的道具和需要使用的道具
        // 注意：编辑模式下，如果配置中没有这些字段，会在onMounted中处理
        if (!isEditMode.value) {
          // 默认选中所有需要存仓库的道具
          configContent.value.warehouseItems = warehouseItems.value.map(item => item.id)
          // 默认选中所有需要使用的道具
          configContent.value.useItems = useItems.value.map(item => item.id)
        }

        // 强制触发响应式更新
        useItems.value = [...useItems.value]
      } catch (error) {
        console.error('加载道具数据失败:', error)
        ElMessage.error('加载道具数据失败: ' + (error.response?.data?.message || error.message))
      }
    }

    // 切换仓库道具选择
    const toggleWarehouseItem = (itemId) => {
      const index = configContent.value.warehouseItems.indexOf(itemId)
      if (index > -1) {
        configContent.value.warehouseItems.splice(index, 1)
      } else {
        configContent.value.warehouseItems.push(itemId)
      }
    }

    // 切换删除道具选择
    const toggleDeleteItem = (itemId) => {
      const index = configContent.value.deleteItems.indexOf(itemId)
      if (index > -1) {
        configContent.value.deleteItems.splice(index, 1)
      } else {
        configContent.value.deleteItems.push(itemId)
      }
    }

    // 切换使用道具选择
    const toggleUseItem = (itemId) => {
      if (!configContent.value.useItems) {
        configContent.value.useItems = []
      }
      const index = configContent.value.useItems.indexOf(itemId)
      if (index > -1) {
        configContent.value.useItems.splice(index, 1)
      } else {
        configContent.value.useItems.push(itemId)
      }
    }

    // 计算属性：判断是否全选
    const isAllWarehouseSelected = computed(() => {
      if (warehouseItems.value.length === 0) return false
      return warehouseItems.value.every(item => configContent.value.warehouseItems.includes(item.id))
    })

    const isAllDeleteSelected = computed(() => {
      if (deleteItems.value.length === 0) return false
      return deleteItems.value.every(item => configContent.value.deleteItems.includes(item.id))
    })

    const isAllUseSelected = computed(() => {
      if (useItems.value.length === 0) return false
      if (!configContent.value.useItems) return false
      return useItems.value.every(item => configContent.value.useItems.includes(item.id))
    })

    // 全选/取消全选方法
    const toggleSelectAllWarehouse = () => {
      if (isAllWarehouseSelected.value) {
        // 取消全选
        configContent.value.warehouseItems = []
      } else {
        // 全选
        configContent.value.warehouseItems = warehouseItems.value.map(item => item.id)
      }
    }

    const toggleSelectAllDelete = () => {
      if (isAllDeleteSelected.value) {
        // 取消全选
        configContent.value.deleteItems = []
      } else {
        // 全选
        configContent.value.deleteItems = deleteItems.value.map(item => item.id)
      }
    }

    const toggleSelectAllUse = () => {
      if (!configContent.value.useItems) {
        configContent.value.useItems = []
      }
      if (isAllUseSelected.value) {
        // 取消全选
        configContent.value.useItems = []
      } else {
        // 全选
        configContent.value.useItems = useItems.value.map(item => item.id)
      }
    }

    // 处理图片加载错误
    const handleImageError = (event) => {
      event.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB4PSIzIiB5PSIzIiB3aWR0aD0iMTgiIGhlaWdodD0iMTgiIHJ4PSIyIiByeT0iMiIgZmlsbD0iI2YzZjRmNiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTAiIGZpbGw9IiM5Y2EzYWYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj7plJnor689L3RleHQ+PC9zdmc+'
    }

    // 获取道具背景色CSS类
    const getItemBackgroundColorClass = (backgroundColor) => {
      if (!backgroundColor) return ''
      return `bg-${backgroundColor}`
    }

    // 加载配置数据（编辑模式）
    const loadConfigData = async () => {
      if (!isEditMode.value) return

      loading.value = true
      try {
        const response = await getConfig(props.id)
        const config = response.data

        // 检查配置数据是否存在
        if (!config || !response || !response.data) {
          ElMessage.error('配置不存在或加载失败')
          router.push('/dashboard/scripts')
          return
        }

        // 填充表单，使用可选链和默认值确保安全
        configForm.value.name = config?.name || ''
        configForm.value.description = config?.description || ''

        // 判断是否为默认配置
        isDefaultConfig.value = config?.description && config.description.includes('系统为新用户自动创建')

        // 判断是否为共享配置（不是自己创建的）
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}')
        isSharedConfig.value = config?.isShared === 1 && config?.createdBy !== currentUser.id

        // 填充配置内容
        if (config?.content) {
          fillConfigContent(config.content)
        }

        // 加载活动制作配置
        if (config.activityCraftSelections) {
          activityCraftSelections.value = config.activityCraftSelections
        } else if (config.id) {
          await loadActivityCraftConfig(config.id)
        }

      } catch (error) {
        console.error('加载配置失败:', error)
        ElMessage.error('加载配置失败: ' + (error.response?.data?.message || error.message))
        router.push('/dashboard/scripts')
      } finally {
        loading.value = false
      }
    }

    // 时间格式化辅助函数：将小时转换为 HH:00 格式
    const formatTimeForDisplay = (time) => {
      if (!time) return '09:00'
      // 如果时间不包含冒号，说明只有小时，添加 :00
      return time.includes(':') ? time : `${time.padStart(2, '0')}:00`
    }

    // 填充配置内容到表单
    const fillConfigContent = (content) => {
      // 设置加载标志，避免 watch 监听器在加载时清空数组
      isLoadingConfig.value = true

      // 角色模式
      if (content.角色模式) configContent.value.characterMode = content.角色模式

      // 账号大区
      if (content.账号大区) configContent.value.accountRegion = content.账号大区

      // 主角色位置
      if (content.主角色位置) configContent.value.mainCharacterPosition = content.主角色位置

      // 选区（占坑模式）
      if (content.选区) configContent.value.placeholderZone = content.选区

      // 创建角色
      if (content.创建角色) {
        Object.assign(configContent.value.createCharacter, {
          enabled: content.创建角色.启用 ?? false,
          profession: content.创建角色.职业 ?? '奶妈'
        })
      }

      // 游戏更新
      if (content.游戏更新) configContent.value.gameUpdate = content.游戏更新
      if (content.自动更新 !== undefined) configContent.value.autoUpdate = content.自动更新

      // 代理设置
      if (content.代理类型) configContent.value.proxyType = content.代理类型
      if (content.启用代理 !== undefined) configContent.value.enableProxy = content.启用代理

      // 主线任务
      if (content.主线任务) {
        if (content.主线任务.启用 !== undefined) configContent.value.mainQuest.enabled = content.主线任务.启用
        if (content.主线任务.类型) {
          configContent.value.mainQuest.type = content.主线任务.类型
        } else {
          // 向后兼容：如果没有类型字段，默认使用等级模式
          configContent.value.mainQuest.type = 'level'
        }
        if (content.主线任务.等级) configContent.value.mainQuest.level = content.主线任务.等级
        if (content.主线任务.大章节) configContent.value.mainQuest.chapterMajor = content.主线任务.大章节
        if (content.主线任务.小章) configContent.value.mainQuest.chapterMinor = content.主线任务.小章
        // 向后兼容：如果存在旧的章节开始/结束字段，转换为新字段
        if (content.主线任务.章节开始 && !content.主线任务.大章节) {
          configContent.value.mainQuest.chapterMajor = content.主线任务.章节开始
        }
        if (content.主线任务.章节结束 && !content.主线任务.小章) {
          configContent.value.mainQuest.chapterMinor = content.主线任务.章节结束
        }
        if (content.主线任务.死亡限制) configContent.value.mainQuest.deathLimit = content.主线任务.死亡限制
        if (content.主线任务.冷却小时) configContent.value.mainQuest.cooldownHours = content.主线任务.冷却小时
      }

      // 每日课题区域
      if (content.每日课题区域) {
        configContent.value.dailyQuest.enabled = content.每日课题区域.启用 ?? false
        if (content.每日课题区域.每日重复挑战) {
          configContent.value.dailyQuest.repeatChallenge.enabled = content.每日课题区域.每日重复挑战.启用 ?? false
          configContent.value.dailyQuest.repeatChallenge.type = content.每日课题区域.每日重复挑战.类型 ?? '野外'
          configContent.value.dailyQuest.repeatChallenge.count = content.每日课题区域.每日重复挑战.次数 ?? '10次'
        }
        if (content.每日课题区域.每日采集任务 !== undefined) {
          configContent.value.dailyQuest.collectionTask = content.每日课题区域.每日采集任务
        }
      }

      // 故事&成就&黑色女武神
      if (content.故事成就黑色女武神) {
        Object.assign(configContent.value.storyAchievement, {
          exchangeTask: content.故事成就黑色女武神.交易所任务 ?? false,
          ultimateTask: content.故事成就黑色女武神.大招任务 ?? false,
          utgardShadow: content.故事成就黑色女武神.乌坦加德之影 ?? false,
          worldTreeDisir: content.故事成就黑色女武神.世界树迪西尔 ?? false,
          blueBird: content.故事成就黑色女武神.蓝鸟 ?? false,
          purpleBird: content.故事成就黑色女武神.紫鸟 ?? false,
          sigrenTrial: content.故事成就黑色女武神.希格露恩试炼 ?? false
        })
      }

      // 系统设置
      if (content.系统设置) {
        Object.assign(configContent.value.systemSettings, {
          autoStart: content.系统设置.开机自启动 ?? false,
          ailinSystem: content.系统设置.艾琳系统 ?? false,
          randomDelayStart: content.系统设置.随机延时启动 ?? false,
          scheduledScreenshot: content.系统设置.定时截屏 ?? true,
          autoUpdateScript: content.系统设置.自动更新脚本 ?? false,
          killProxyAndGameOnStart: content.系统设置.启动脚本杀代理和游戏 ?? false,
          killProxyAndGameOnUpdate: content.系统设置.更新脚本杀代理和游戏 ?? false,
          openProxyBeforeStart: content.系统设置.每次启动游戏前都开一次代理 ?? false,
          android11InputMethod: content.系统设置.安卓11输入法 ?? false,
          autoRestartOnIdle: content.系统设置.系统画面长时间不动自动重启游戏 ?? false,
          loginType: content.系统设置.登录选项 ?? null,
          googleAccountType: content.系统设置.谷歌账号类型 ?? null,
          "谷歌登录界面选择": content.系统设置.谷歌登录界面选择 ?? "直接登录",
          countdownSeconds: content.系统设置.读秒倒计时 ?? 20,
          gameEnterFailStopThreshold: content.系统设置.连续进入游戏失败关闭脚本次数 ?? 0
        })
      }

      // 游戏商城（独立配置）
      if (content.游戏商城) {
        Object.assign(configContent.value.gameShop, {
          autoBuyMana: content.游戏商城.自动商场买蓝 ?? false,
          weaponStone: content.游戏商城.武器强化石 ?? false,
          clothesStone: content.游戏商城.服饰强化石 ?? false,
          accessoryStone: content.游戏商城.饰品强化石 ?? false,
          hammerStone: content.游戏商城.镐锤强化石 ?? false,
          weaponStoneBox: content.游戏商城.武器强化石箱子 ?? false,
          clothesStoneBox: content.游戏商城.服饰强化石箱子 ?? false,
          accessoryStoneBox: content.游戏商城.饰品强化石箱子 ?? false,
          hammerStoneBox: content.游戏商城.镐锤强化石箱子 ?? false,
          valkyrieDraw: content.游戏商城.瓦尔基里抽卡 ?? false,
          dixierDraw: content.游戏商城.迪西尔抽卡 ?? false,
          restPoints: content.游戏商城.休息点数 ?? false,
          restPointItems: {
            amberPocket: content.游戏商城.休息点数道具?.琥珀口袋 ?? false,
            sealEnhanceStone: content.游戏商城.休息点数道具?.印章强化石 ?? false,
            manaRegenPotion: content.游戏商城.休息点数道具?.法力再生药水 ?? false,
            enhanceStoneBox: content.游戏商城.休息点数道具?.强化石选择箱子 ?? false
          }
        })
        // 荣誉商人配置
        if (content.游戏商城.荣誉商人) {
          Object.assign(configContent.value.gameShop.honorMerchant, {
            enabled: content.游戏商城.荣誉商人.荣誉商人 ?? false,
            heroSoul: content.游戏商城.荣誉商人.英雄灵魂 ?? false,
            duskStone: content.游戏商城.荣誉商人.黄昏石 ?? false,
            victorySealPack: content.游戏商城.荣誉商人.胜利印章礼包 ?? false,
            sealEnhanceStone: content.游戏商城.荣誉商人.印章强化石 ?? false,
            gloriousEnhanceBox: content.游戏商城.荣誉商人.光耀的强化石选择箱子 ?? false,
            darkEnhanceBox: content.游戏商城.荣誉商人.黑暗强化石选择箱子 ?? false,
            patternBox: content.游戏商城.荣誉商人.图案选择箱子 ?? false,
            guildSkillBookBox: content.游戏商城.荣誉商人.公会技能书箱子 ?? false,
            midMaterialBox: content.游戏商城.荣誉商人.中级制作材料选择箱子 ?? false,
            lowMaterialBox: content.游戏商城.荣誉商人.低级制作材料选择箱子 ?? false,
            manaRegenPotion: content.游戏商城.荣誉商人.法力再生药水 ?? false,
            swiftMead: content.游戏商城.荣誉商人.迅速蜜酒 ?? false
          })
        }
      }

      // 副本
      if (content.副本) {
        if (content.副本.女武神的殿堂西侧) {
          const floor = content.副本.女武神的殿堂西侧.层数
          let defaultFloorLevel = '25'
          if (floor === '2') {
            defaultFloorLevel = '35'
          } else if (floor === '3') {
            defaultFloorLevel = '45'
          } else if (floor === '4') {
            defaultFloorLevel = '55'
          } else if (floor === '5') {
            defaultFloorLevel = '65'
          }
          // 先设置floorLevel，再设置floor，避免watch触发时覆盖已设置的等级
          const savedFloorLevel = content.副本.女武神的殿堂西侧.等级 ?? defaultFloorLevel
          Object.assign(configContent.value.dungeons.valkyrie, {
            enabled: content.副本.女武神的殿堂西侧.启用,
            floorLevel: savedFloorLevel,
            floor: floor,
            difficulty: content.副本.女武神的殿堂西侧.难度,
            difficultyType: content.副本.女武神的殿堂西侧.难度类型 ?? '普通',
            type: content.副本.女武神的殿堂西侧.类型,
            mapCircle: content.副本.女武神的殿堂西侧.地图绕圈打 ?? false,
            failLimit: content.副本.女武神的殿堂西侧.失败限制 ?? 3,
            failCooldownHours: content.副本.女武神的殿堂西侧.失败冷却小时 ?? 6,
            timeLimit: {
              enabled: content.副本.女武神的殿堂西侧.时间限制?.启用 ?? false,
              startTime: formatTimeForDisplay(content.副本.女武神的殿堂西侧.时间限制?.开始时间),
              endTime: formatTimeForDisplay(content.副本.女武神的殿堂西侧.时间限制?.结束时间)
            }
          })
        }
        if (content.副本.西格伦的迷宫西侧) {
          Object.assign(configContent.value.dungeons.sigren, {
            enabled: content.副本.西格伦的迷宫西侧.启用,
            floor: content.副本.西格伦的迷宫西侧.层数,
            difficulty: content.副本.西格伦的迷宫西侧.难度,
            position: content.副本.西格伦的迷宫西侧.位置,
            failLimit: content.副本.西格伦的迷宫西侧.失败限制 ?? 3,
            failCooldownHours: content.副本.西格伦的迷宫西侧.失败冷却小时 ?? 6,
            timeLimit: {
              enabled: content.副本.西格伦的迷宫西侧.时间限制?.启用 ?? false,
              startTime: formatTimeForDisplay(content.副本.西格伦的迷宫西侧.时间限制?.开始时间),
              endTime: formatTimeForDisplay(content.副本.西格伦的迷宫西侧.时间限制?.结束时间),
              weekdays: content.副本.西格伦的迷宫西侧.时间限制?.周几 ?? [1, 2, 3, 4, 5, 6, 7]
            }
          })
          // 加载混沌神殿定点坐标
          const sigrenPoints = content.副本.西格伦的迷宫西侧.定点坐标
          if (sigrenPoints) {
            if (Array.isArray(sigrenPoints)) {
              configContent.value.dungeons.sigren.customPoints = sigrenPoints.map(p => ({ x: Number(p.x), y: Number(p.y) })).slice(0, 50)
            } else if (typeof sigrenPoints === 'object' && sigrenPoints.x !== undefined && sigrenPoints.y !== undefined) {
              configContent.value.dungeons.sigren.customPoints = [{ x: Number(sigrenPoints.x), y: Number(sigrenPoints.y) }]
            }
          } else {
            configContent.value.dungeons.sigren.customPoints = []
          }
        }
        if (content.副本.波尔克房间西侧) {
          Object.assign(configContent.value.dungeons.polk, {
            enabled: content.副本.波尔克房间西侧.启用,
            floor: content.副本.波尔克房间西侧.层数,
            difficulty: content.副本.波尔克房间西侧.难度,
            position: content.副本.波尔克房间西侧.位置,
            type: content.副本.波尔克房间西侧.类型,
            failLimit: content.副本.波尔克房间西侧.失败限制 ?? 3,
            failCooldownHours: content.副本.波尔克房间西侧.失败冷却小时 ?? 6,
            timeLimit: {
              enabled: content.副本.波尔克房间西侧.时间限制?.启用 ?? false,
              startTime: formatTimeForDisplay(content.副本.波尔克房间西侧.时间限制?.开始时间),
              endTime: formatTimeForDisplay(content.副本.波尔克房间西侧.时间限制?.结束时间),
              weekdays: content.副本.波尔克房间西侧.时间限制?.周几 ?? [1, 2, 3, 4, 5, 6, 7]
            }
          })
          // 加载弗尔克范格定点坐标
          const polkPoints = content.副本.波尔克房间西侧.定点坐标
          if (polkPoints) {
            if (Array.isArray(polkPoints)) {
              configContent.value.dungeons.polk.customPoints = polkPoints.map(p => ({ x: Number(p.x), y: Number(p.y) })).slice(0, 50)
            } else if (typeof polkPoints === 'object' && polkPoints.x !== undefined && polkPoints.y !== undefined) {
              configContent.value.dungeons.polk.customPoints = [{ x: Number(polkPoints.x), y: Number(polkPoints.y) }]
            }
          } else {
            configContent.value.dungeons.polk.customPoints = []
          }
        }
        if (content.副本.黄金殿堂) {
          Object.assign(configContent.value.dungeons.goldenHall, {
            enabled: content.副本.黄金殿堂.启用 ?? false,
            floor: content.副本.黄金殿堂.层数 ?? '1',
            difficulty: content.副本.黄金殿堂.难度 ?? '普通'
          })
          // 加载黄金殿堂定点坐标
          const goldenHallPoints = content.副本.黄金殿堂.定点坐标
          if (goldenHallPoints) {
            if (Array.isArray(goldenHallPoints)) {
              configContent.value.dungeons.goldenHall.customPoints = goldenHallPoints.map(p => ({ x: Number(p.x), y: Number(p.y) })).slice(0, 50)
            } else if (typeof goldenHallPoints === 'object' && goldenHallPoints.x !== undefined && goldenHallPoints.y !== undefined) {
              configContent.value.dungeons.goldenHall.customPoints = [{ x: Number(goldenHallPoints.x), y: Number(goldenHallPoints.y) }]
            }
          } else {
            configContent.value.dungeons.goldenHall.customPoints = []
          }
        }
        if (content.副本.赛季英灵殿) {
          const floor = content.副本.赛季英灵殿.层数
          let defaultFloorLevel = '25'
          if (floor === '2') {
            defaultFloorLevel = '35'
          } else if (floor === '3') {
            defaultFloorLevel = '45'
          } else if (floor === '4') {
            defaultFloorLevel = '55'
          } else if (floor === '5') {
            defaultFloorLevel = '65'
          }
          // 先设置floorLevel，再设置floor，避免watch触发时覆盖已设置的等级
          const savedFloorLevel = content.副本.赛季英灵殿.等级 ?? defaultFloorLevel
          Object.assign(configContent.value.dungeons.seasonalValkyrie, {
            enabled: content.副本.赛季英灵殿.启用,
            floorLevel: savedFloorLevel,
            floor: floor,
            difficultyType: content.副本.赛季英灵殿.难度类型 ?? '普通',
            type: content.副本.赛季英灵殿.类型 ?? '成长屋',
            mapCircle: content.副本.赛季英灵殿.地图绕圈打 ?? false,
            failLimit: content.副本.赛季英灵殿.失败限制 ?? 3,
            failCooldownHours: content.副本.赛季英灵殿.失败冷却小时 ?? 6
          })
        }
      }

      // 野外
      if (content.野外) {
        // 先加载定点坐标（避免 watch 清空）
        const pointsAny = content.野外.定点坐标 ?? content.野外.迷宫定点坐标
        if (pointsAny) {
          const pts = pointsAny
          if (Array.isArray(pts)) {
            configContent.value.outdoor.customPoints = pts.map(p => ({ x: Number(p.x), y: Number(p.y) })).slice(0, 50)
          } else if (typeof pts === 'object' && pts.x !== undefined && pts.y !== undefined) {
            // 兼容旧版本单点
            configContent.value.outdoor.customPoints = [{ x: Number(pts.x), y: Number(pts.y) }]
          }
        } else {
          // 如果没有定点坐标，清空数组
          configContent.value.outdoor.customPoints = []
        }
        // 再加载 type 和 location（此时定点坐标已加载，watch 不会清空）
        if (content.野外.类型) configContent.value.outdoor.type = content.野外.类型
        if (content.野外.位置) configContent.value.outdoor.location = content.野外.位置
        if (content.野外.定点挂机) configContent.value.outdoor.position = content.野外.定点挂机
        if (content.野外.自动开箱 !== undefined) configContent.value.outdoor.autoChest = content.野外.自动开箱
        if (content.野外.刷银币 !== undefined) configContent.value.outdoor.autoSilverFarming = content.野外.刷银币
        if (content.野外.精锐地图 !== undefined) configContent.value.outdoor.eliteMap = content.野外.精锐地图
        // 普通地图时间限制
        if (content.野外.时间限制) {
          Object.assign(configContent.value.outdoor.timeLimit, {
            enabled: content.野外.时间限制.启用 ?? false,
            startTime: formatTimeForDisplay(content.野外.时间限制.开始时间),
            endTime: formatTimeForDisplay(content.野外.时间限制.结束时间)
          })
        }
        // 挖琥珀配置
        if (content.野外.挖琥珀) {
          Object.assign(configContent.value.outdoor.amber, {
            enabled: content.野外.挖琥珀.启用 ?? false,
            location: content.野外.挖琥珀.位置 ?? '世界树一层',
            channel: content.野外.挖琥珀.更换频道 ?? '与挂机设置相同',
            timeLimit: {
              enabled: content.野外.挖琥珀.时间限制?.启用 ?? false,
              startTime: formatTimeForDisplay(content.野外.挖琥珀.时间限制?.开始时间),
              endTime: formatTimeForDisplay(content.野外.挖琥珀.时间限制?.结束时间)
            }
          })
          // 加载挖琥珀定点坐标
          const amberPoints = content.野外.挖琥珀.定点坐标
          if (amberPoints) {
            if (Array.isArray(amberPoints)) {
              configContent.value.outdoor.amber.customPoints = amberPoints.map(p => ({ x: Number(p.x), y: Number(p.y) })).slice(0, 50)
            } else if (typeof amberPoints === 'object' && amberPoints.x !== undefined && amberPoints.y !== undefined) {
              configContent.value.outdoor.amber.customPoints = [{ x: Number(amberPoints.x), y: Number(amberPoints.y) }]
            }
          } else {
            configContent.value.outdoor.amber.customPoints = []
          }
        }
        // 激战地配置
        if (content.野外.激战地) {
          Object.assign(configContent.value.outdoor.battleground, {
            enabled: content.野外.激战地.启用 ?? false,
            location: content.野外.激战地.位置 ?? '诸神黄昏岔路',
            position: content.野外.激战地.定点挂机 ?? '随机',
            deathCount: content.野外.激战地.死亡次数 ?? 3,
            timeLimit: {
              enabled: content.野外.激战地.时间限制?.启用 ?? false,
              startTime: formatTimeForDisplay(content.野外.激战地.时间限制?.开始时间),
              endTime: formatTimeForDisplay(content.野外.激战地.时间限制?.结束时间)
            }
          })
          // 加载激战地定点坐标
          const bgPoints = content.野外.激战地.定点坐标
          if (bgPoints) {
            if (Array.isArray(bgPoints)) {
              configContent.value.outdoor.battleground.customPoints = bgPoints.map(p => ({ x: Number(p.x), y: Number(p.y) })).slice(0, 50)
            } else if (typeof bgPoints === 'object' && bgPoints.x !== undefined && bgPoints.y !== undefined) {
              configContent.value.outdoor.battleground.customPoints = [{ x: Number(bgPoints.x), y: Number(bgPoints.y) }]
            }
          } else {
            configContent.value.outdoor.battleground.customPoints = []
          }
        }
        if (content.野外.离线挂机) {
          Object.assign(configContent.value.outdoor.offlineHang, {
            enabled: content.野外.离线挂机.启用 ?? false,
            location: content.野外.离线挂机.位置 ?? '洞穴巨人墓地1层',
            timeLimit: {
              enabled: content.野外.离线挂机.时间限制?.启用 ?? false,
              startTime: formatTimeForDisplay(content.野外.离线挂机.时间限制?.开始时间),
              endTime: formatTimeForDisplay(content.野外.离线挂机.时间限制?.结束时间)
            }
          })
        } else {
          Object.assign(configContent.value.outdoor.offlineHang, {
            enabled: false,
            location: '洞穴巨人墓地1层',
            timeLimit: {
              enabled: false,
              startTime: '09:00',
              endTime: '18:00'
            }
          })
        }
      }

      // 挂机设置
      if (content.挂机设置) {
        Object.assign(configContent.value.hangSettings, {
          autoBuyPotion: content.挂机设置.自动买药,
          powerSaveMode: content.挂机设置.省电模式,
          healthThreshold: content.挂机设置.血量阈值,
          potionInterval: content.挂机设置.买药间隔 ?? 4,
          nightMode: content.挂机设置.夜晚模式 ?? false,
          hangRange: content.挂机设置.挂机范围 ?? 50,
          collectRange: content.挂机设置.采集范围 ?? 50,
          autoUseConsumables: content.挂机设置.自动使用所有消耗品 ?? false,
          changeChannel: content.挂机设置.更换频道 ?? false,
          staminaPotion: content.挂机设置.体力药水 ?? 500,
          autoUseStaminaPotion: content.挂机设置.自动使用体力药水 ?? 80,
          moveScroll: content.挂机设置.移动卷轴 ?? 50,
          returnScroll: content.挂机设置.回城卷轴 ?? 50,
          archerReturnOriginEnabled: content.挂机设置.弓箭手回原点启用 ?? false,
          archerReturnOriginInterval: content.挂机设置.弓箭手回原点间隔 ?? 200,
          channel: (() => {
            const channelValue = content.挂机设置.频道 ?? '5'
            if (typeof channelValue === 'string' && channelValue.startsWith('频道')) {
              // 提取"频道5"中的数字部分
              const match = channelValue.match(/频道(\d+)/)
              return match ? match[1] : '5'
            }
            // 如果是"随机"，直接返回
            if (channelValue === '随机') {
              return '随机'
            }
            // 兼容旧格式（直接是"5"）
            return channelValue.toString()
          })()
        })
      }

      // 间隔时间
      if (content.间隔时间) {
        Object.assign(configContent.value.hangSettings, {
          decomposeInterval: content.间隔时间.分解装备间隔 ?? 60,
          cleanBagInterval: content.间隔时间.清理背包间隔 ?? 360,
          storageInterval: content.间隔时间.仓库存储间隔 ?? 360,
          nornTreasureInterval: content.间隔时间.诺恩宝物间隔 ?? 60,
          redeemCodeInterval: content.间隔时间.兑换码间隔 ?? 1440
        })
      } else {
        // 向后兼容：如果没有间隔时间区域，尝试从挂机设置中读取
        if (content.挂机设置) {
          Object.assign(configContent.value.hangSettings, {
            decomposeInterval: content.挂机设置.分解装备间隔 ?? 60,
            cleanBagInterval: content.挂机设置.清理背包间隔 ?? 360,
            storageInterval: content.挂机设置.仓库存储间隔 ?? 360,
            nornTreasureInterval: content.挂机设置.诺恩宝物间隔 ?? 60,
            redeemCodeInterval: content.挂机设置.兑换码间隔 ?? 1440
          })
        }
      }

      // 其他需求
      if (content.其他需求) {
        Object.assign(configContent.value.otherRequirements, {
          commission: content.其他需求.委托 ?? true,
          commissionDeathLimit: content.其他需求.委托死亡限制 ?? 1,
          commissionCooldownHours: content.其他需求.委托冷却小时 ?? 12,
          sideQuest: content.其他需求.支线 ?? true,
          sideQuestDeathLimit: content.其他需求.挑战死亡限制 ?? 1,
          sideQuestCooldownHours: content.其他需求.挑战冷却小时 ?? 12,
          commissionFailHours: content.其他需求.委托失败小时 ?? 3,
          deathRecoverExp: content.其他需求.死亡恢复经验 ?? true,
          autoDecomposeEquip: content.其他需求.自动分解装备 ?? true,
          decomposeWhite: content.其他需求.分解白装 ?? true,
          decomposeGreen: content.其他需求.分解绿装 ?? true,
          decomposeBlue: content.其他需求.分解蓝装 ?? false,
          decomposeEnhanced: content.其他需求.分解强化 ?? true,
          decomposeIdentified: content.其他需求.分解鉴定 ?? true,
          decomposeSkillBook: content.其他需求.分解技能书 ?? false,
          autoEnhance: content.其他需求.自动强化 ?? true,
          autoValkyrieCompose: content.其他需求.自动女武神合成 ?? true,
          autoPetCompose: content.其他需求.自动宠物合成 ?? true,
          autoEquipValkyrie: content.其他需求.自动穿戴女武神卡 ?? true,
          autoEquipPet: content.其他需求.自动穿戴宠物 ?? true,
          autoAmberBox: content.其他需求.自动琥珀封箱 ?? false,
          amberBoxPriceThreshold: content.其他需求.琥珀封箱价格阈值 ?? 120,
          amberBoxPriceThresholdEnabled: content.其他需求.琥珀封箱价格阈值启用 ?? false,
          autoEquipBox: content.其他需求.自动开装备箱子 ?? false,
          autoBuySkill: content.其他需求.自动购买技能 ?? false,
          autoBuySkillMaxSilver: content.其他需求.自动购买技能银币上限 ?? 100000,
          autoWarehouse: content.其他需求.自动仓库 ?? false,
          warehouseInfiniteCleanup: content.其他需求.空间不足无限清理 ?? false,
          warehouseRedScreenAlert: content.其他需求.空间不足红屏提醒 ?? false,
          autoUltimate: content.其他需求.自动释放大招 ?? false,
          silverRestoreEnabled: content.其他需求.银币恢复启用 ?? false,
          silverRestore: content.其他需求.银币恢复 ?? 20000,
          potentialUpgradeEnabled: content.其他需求.潜能升级启用 ?? false,
          potentialUpgradeSkill: content.其他需求.潜能升级技能 ?? 6,
          dixierUpgrade: content.其他需求.迪西尔升级 ?? false,
          valkyrieUpgrade: content.其他需求.瓦尔基里升级 ?? false,
          autoSkillUpgrade: content.其他需求.自动技能升级 ?? false,
          autoArtifact: content.其他需求.自动神器 ?? false,
          autoSmartCraftBlue: content.其他需求.自动智能制作蓝装 ?? false,
          autoEquipAdvanced: content.其他需求.自动穿戴高级装备 ?? false,
          autoIdentifyEquipment: content.其他需求.自动鉴定装备 ?? false,
          identifyEquipmentType: content.其他需求.鉴定装备类型 ?? '法术系',
          identifyEquipmentLevel: content.其他需求.鉴定装备级别 ?? '随机',
          storyCardUpgradeEnabled: content.其他需求.剧情卡组提升启用 ?? false,
          storyCardUpgradeLevel: content.其他需求.剧情卡组提升等级 ?? 1,
          redeemCodeEnabled: content.其他需求.兑换码 ?? false,
          redeemCodes: content.其他需求.兑换码列表 ?? '',
          useBuiltInRedeemCodes: content.其他需求.脚本内置兑换码 ?? false,
          seasonInstruction: content.其他需求.赛季指令 ?? false,
          blessingTower: content.其他需求.祝福之塔 ?? false,
          autoClaimMail: content.其他需求.自动领邮件 ?? true
        })
      }

      // 工会
      if (content.工会) {
        Object.assign(configContent.value.guild, {
          autoJoin: content.工会.自动加入公会 ?? true,
          techContribute24: content.工会['24次科技捐献'] ?? content.工会.自动贡献 ?? true, // 向后兼容
          silverContribute: content.工会.银币捐献 ?? false,
          amberContribute: content.工会.琥珀捐献 ?? false,
          collectGuildChest: content.工会.领取工会箱子 ?? false,
          guildHelp: content.工会.工会帮助 ?? false,
          guildSkill: content.工会.公会技能 ?? '公会发展',
          collaborationItemType: content.工会.协作道具 ?? '仅银币',
          skillCollaborationInterval: content.工会.技能协作间隔 ?? 6
        })
      }


      // 交易所
      if (content.交易所) {
        Object.assign(configContent.value.tradingPost, {
          autoSell: content.交易所.自动出售,
          enablePriceDifference: content.交易所.启用价格差 ?? false,
          priceDifference: content.交易所.价格差 ?? 1,
          delistHours: content.交易所.下架超过小时 ?? 5,
          priceProtection: Array.isArray(content.交易所.价格保护)
              ? content.交易所.价格保护
              : (content.交易所.价格保护 ? [content.交易所.价格保护] : [{ itemName: '琥珀箱子：50,000', minPrice: 300 }])
        })
        // 确保priceProtection是数组格式
        if (!Array.isArray(configContent.value.tradingPost.priceProtection)) {
          configContent.value.tradingPost.priceProtection = [{ itemName: '琥珀箱子：50,000', minPrice: 300 }]
        }
        // 确保琥珀箱子始终存在
        const hasAmberBox = configContent.value.tradingPost.priceProtection.some(item => item.itemName === '琥珀箱子：50,000')
        if (!hasAmberBox) {
          configContent.value.tradingPost.priceProtection.unshift({ itemName: '琥珀箱子：50,000', minPrice: 300 })
        }
        // 强化可交易装备
        if (content.交易所.强化可交易装备) {
          Object.assign(configContent.value.tradingPost.equipmentEnhancement, {
            enabled: content.交易所.强化可交易装备.是否开启强化装备 ?? false,
            defaultCount: content.交易所.强化可交易装备.强化装备默认个数 ?? 5,
            autoOpenStoneBox: content.交易所.强化可交易装备.是否智能开强化石箱子 ?? false,
            weaponStone: content.交易所.强化可交易装备.武器强化石 ?? true,
            armorStone: content.交易所.强化可交易装备.防具强化石 ?? true,
            accessoryStone: content.交易所.强化可交易装备.饰品强化石 ?? true,
            lifeEquipmentStone: content.交易所.强化可交易装备.生活装备强化石 ?? false
          })
          if (content.交易所.强化可交易装备.装备品质) {
            if (content.交易所.强化可交易装备.装备品质.白) {
              Object.assign(configContent.value.tradingPost.equipmentEnhancement.equipmentQuality.white, {
                enabled: content.交易所.强化可交易装备.装备品质.白.启用 ?? true,
                level: content.交易所.强化可交易装备.装备品质.白.等级 ?? 6
              })
            }
            if (content.交易所.强化可交易装备.装备品质.绿) {
              Object.assign(configContent.value.tradingPost.equipmentEnhancement.equipmentQuality.green, {
                enabled: content.交易所.强化可交易装备.装备品质.绿.启用 ?? true,
                level: content.交易所.强化可交易装备.装备品质.绿.等级 ?? 6
              })
            }
          }
        }
        // 装备自定义价格
        configContent.value.tradingPost.equipmentCustomPricesEnabled = content.交易所.是否上架自定义装备价格 ?? false
        if (content.交易所.装备自定义价格 && Array.isArray(content.交易所.装备自定义价格)) {
          configContent.value.tradingPost.equipmentCustomPrices = content.交易所.装备自定义价格.map(item => ({
            level: item.level ?? 6,
            whitePrice: item.whitePrice ?? 10,
            greenPrice: item.greenPrice ?? 15
          }))
        } else {
          configContent.value.tradingPost.equipmentCustomPrices = []
        }
      }

      // 区域交易所
      if (content.区域交易所) {
        Object.assign(configContent.value.regionalExchange, {
          autoSell: content.区域交易所.自动出售 ?? false,
          exchangeTypeEnabled: content.区域交易所.交易类型启用 ?? false,
          exchangeType: content.区域交易所.交易类型 ?? '钻石',
          itemType: content.区域交易所.普通道具 ?? false,
          equipment: content.区域交易所.装备 ?? false,
          enablePriceDifference: content.区域交易所.启用价格差 ?? false,
          priceDifference: content.区域交易所.价格差 ?? 1,
          delistHours: content.区域交易所.下架超过小时 ?? 5
        })
      } else if (content.交易所) {
        // 向后兼容：如果旧配置中有区域交易所字段，迁移到新的区域交易所配置
        if (content.交易所.区域交易所 !== undefined) {
          Object.assign(configContent.value.regionalExchange, {
            autoSell: content.交易所.区域交易所 ?? false,
            exchangeTypeEnabled: content.交易所.区域交易所类型 !== undefined,
            exchangeType: content.交易所.区域交易所类型 ?? '钻石',
            itemType: content.交易所.区域交易所普通道具 ?? false,
            equipment: content.交易所.区域交易所装备 ?? false,
            enablePriceDifference: content.交易所.启用价格差 ?? false,
            priceDifference: content.交易所.价格差 ?? 1,
            delistHours: content.交易所.下架超过小时 ?? 5
          })
        }
      }

      // 活动/制作
      if (content.活动制作) {
        Object.assign(configContent.value.activityCraft, {
          enabled: content.活动制作.活动制作 ?? false,
          synthesizeBlue: content.活动制作.合成蓝 ?? false,
          nornTreasure: content.活动制作.诺恩宝物 ?? false,
          useTradableTreasure: content.活动制作.使用可交易宝物 ?? false,
          tradableTreasureNormal: content.活动制作.可交易宝物普通 ?? false,
          tradableTreasureAdvanced: content.活动制作.可交易宝物高级 ?? false,
          antiqueCraft: content.活动制作.古董制作 ?? false,
          scrollCraft: content.活动制作.制作卷轴 ?? false,
          foodCraft: content.活动制作.制作食物 ?? false
        })
      }

      // 背包
      if (content.背包) {
        Object.assign(configContent.value.backpack, {
          slotsEnabled: content.背包.背包开格子 ?? false,
          slots: content.背包.背包格子数 ?? 100
        })
      }

      // 收藏区域
      if (content.收藏区域) {
        Object.assign(configContent.value.collection, {
          enabled: content.收藏区域.收藏开关 ?? false,
          maxPrice: content.收藏区域.最高价格 ?? 0.5
        })
        if (content.收藏区域.装备品质) {
          Object.assign(configContent.value.collection.equipmentQuality, {
            white: content.收藏区域.装备品质.白 ?? true,
            green: content.收藏区域.装备品质.绿 ?? true,
            blue: content.收藏区域.装备品质.蓝 ?? false
          })
        }
      }

      // BOSS区域
      if (content.BOSS区域) {
        // 处理远征5人配置（向后兼容）
        if (typeof content.BOSS区域.远征5人 === 'boolean') {
          configContent.value.bossArea.expedition5.enabled = content.BOSS区域.远征5人
          configContent.value.bossArea.expedition5.level = 'auto'
          configContent.value.bossArea.expedition5.createRoom = false
        } else if (content.BOSS区域.远征5人) {
          Object.assign(configContent.value.bossArea.expedition5, {
            enabled: content.BOSS区域.远征5人.启用 ?? false,
            level: content.BOSS区域.远征5人.等级 ?? 'auto',
            createRoom: content.BOSS区域.远征5人.没有队伍就开房间 ?? false
          })
        }

        // 处理远征15人配置（向后兼容）
        if (typeof content.BOSS区域.远征15人 === 'boolean') {
          configContent.value.bossArea.expedition15.enabled = content.BOSS区域.远征15人
          configContent.value.bossArea.expedition15.level = 'auto'
          configContent.value.bossArea.expedition15.createRoom = false
        } else if (content.BOSS区域.远征15人) {
          Object.assign(configContent.value.bossArea.expedition15, {
            enabled: content.BOSS区域.远征15人.启用 ?? false,
            level: content.BOSS区域.远征15人.等级 ?? 'auto',
            createRoom: content.BOSS区域.远征15人.没有队伍就开房间 ?? false
          })
        }

        // 处理赛季5人配置（向后兼容）
        if (typeof content.BOSS区域.赛季5人 === 'boolean') {
          configContent.value.bossArea.expeditionSeasonal5.enabled = content.BOSS区域.赛季5人
          configContent.value.bossArea.expeditionSeasonal5.level = '28'
          configContent.value.bossArea.expeditionSeasonal5.createRoom = false
        } else if (content.BOSS区域.赛季5人) {
          Object.assign(configContent.value.bossArea.expeditionSeasonal5, {
            enabled: content.BOSS区域.赛季5人.启用 ?? false,
            level: content.BOSS区域.赛季5人.等级 ?? '28',
            createRoom: content.BOSS区域.赛季5人.没有队伍就开房间 ?? false
          })
        }

        Object.assign(configContent.value.bossArea, {
          worldBoss: content.BOSS区域.世界boss ?? false,
          timeLimit: {
            enabled: content.BOSS区域.时间限制?.启用 ?? false,
            startTime: formatTimeForDisplay(content.BOSS区域.时间限制?.开始时间),
            endTime: formatTimeForDisplay(content.BOSS区域.时间限制?.结束时间)
          }
        })
      }

      // 道具选择
      if (content.需要存仓库的道具) {
        configContent.value.warehouseItems = content.需要存仓库的道具
      }
      if (content.内置存仓 !== undefined) {
        configContent.value.builtInWarehouse = content.内置存仓
      }
      if (content.需要删除的垃圾道具) {
        configContent.value.deleteItems = content.需要删除的垃圾道具
      }
      if (content.内置删除 !== undefined) {
        configContent.value.builtInDelete = content.内置删除
      }
      if (content.需要使用的道具) {
        configContent.value.useItems = content.需要使用的道具
      }
      if (content.特殊道具使用) {
        configContent.value.growthSummonTicketBox = content.特殊道具使用.成长召唤券选择箱子 ?? '不使用'
        configContent.value.advancedUpgradeCrystalBox = content.特殊道具使用.高级升阶结晶石选择箱子 ?? '不使用'
        configContent.value.advancedPatraCardBox = content.特殊道具使用.高级帕特拉卡牌选择箱子 ?? '不使用'
        configContent.value.treasureIslandSummonTicketBox = content.特殊道具使用.宝物岛召唤券选择箱子 ?? '不使用'
        configContent.value.maniSummonTicketBox = content.特殊道具使用.玛尼召唤券选择箱子 ?? '不使用'
        configContent.value.treasureIslandRareSummonTicketBox = content.特殊道具使用.宝物岛稀有召唤券选择箱子 ?? '不使用'
        configContent.value.collaborationSummonTicketBox = content.特殊道具使用.协作召唤券选择箱子 ?? '不使用'
        configContent.value.oathSummonTicket11TimesBox = content.特殊道具使用.誓言召唤券11次选择箱子 ?? '不使用'
      } else {
        // 如果没有特殊道具使用配置，使用默认值
        configContent.value.growthSummonTicketBox = '不使用'
        configContent.value.advancedUpgradeCrystalBox = '不使用'
        configContent.value.advancedPatraCardBox = '不使用'
        configContent.value.treasureIslandSummonTicketBox = '不使用'
        configContent.value.maniSummonTicketBox = '不使用'
        configContent.value.treasureIslandRareSummonTicketBox = '不使用'
        configContent.value.collaborationSummonTicketBox = '不使用'
        configContent.value.oathSummonTicket11TimesBox = '不使用'
      }

      // 自动喊话
      if (content.自动喊话) {
        Object.assign(configContent.value.autoShout, {
          enabled: content.自动喊话.启用 ?? false,
          content: content.自动喊话.喊话内容 ?? '',
          interval: content.自动喊话.间隔时间 ?? 60
        })
      }

      // 额外功能（兼容旧版 离线箱子.不断开启）
      {
        const ex = content.额外功能
        const legacyChest = content.离线箱子
        configContent.value.extraFeatures.keepOpeningOfflineChest =
            ex?.不断开离线箱子 ?? legacyChest?.不断开启 ?? false
        configContent.value.extraFeatures.autoCraftBlueBook =
            ex?.自动做蓝书 ?? content.其他需求?.自动做蓝书 ?? false
        configContent.value.extraFeatures.autoCraftRedBook =
            ex?.自动做红书 ?? false
      }

      // 兼容旧配置格式
      if (content.自定义删除) {
        configContent.value.deleteItems = content.自定义删除
      }

      // 确保琥珀箱子始终存在（在所有配置加载完成后）
      if (configContent.value.tradingPost && Array.isArray(configContent.value.tradingPost.priceProtection)) {
        const hasAmberBox = configContent.value.tradingPost.priceProtection.some(item => item.itemName === '琥珀箱子：50,000')
        if (!hasAmberBox) {
          configContent.value.tradingPost.priceProtection.unshift({ itemName: '琥珀箱子：50,000', minPrice: 300 })
        }
      }

      // 延迟重置加载标志，确保所有响应式更新完成
      nextTick(() => {
        setTimeout(() => {
          isLoadingConfig.value = false
        }, 100)
      })
    }

    // 保存配置
    const saveConfig = async () => {
      if (!configForm.value.name.trim()) {
        ElMessage.error('请输入配置名称')
        return
      }

      // 活动制作总开关由选中状态兜底同步
      configContent.value.activityCraft.enabled = isAnyActivityCraftSelected.value

      // 验证所有定点挂机功能必须至少设置1个点

      // 1. 挖矿开箱至少需要1个定点
      if (configContent.value.outdoor.type === 'chest' && configContent.value.outdoor.location) {
        const points = configContent.value.outdoor.customPoints || []
        if (points.length < 1) {
          ElMessage.error('挖矿开箱至少需要设置1个定点')
          return
        }
      }

      // 2. 野外挂机/迷宫挂机如果使用了定点功能，必须至少设置1个定点
      const type = configContent.value.outdoor.type
      const loc = configContent.value.outdoor.location || ''
      if (type && loc) {
        // 检查是否支持定点功能
        const sparta = type === 'maze' && ['斯巴达的迷宫一层', '斯巴达的迷宫二层', '斯巴达的迷宫三层'].some(k => loc.includes(k))
        const kara = type === 'maze' && ['卡拉的迷宫一层', '卡拉的迷宫二层', '卡拉的迷宫三层'].some(k => loc.includes(k))
        const arbit = type === 'maze' && loc.includes('阿尔比特迷宫1层')
        const troll = type === 'hang' && (loc === '巨魔遗址下层')
        const trollUpper = type === 'hang' && (loc === '巨魔遗址上层')
        const utgard = type === 'hang' && (loc === '乌坦加尔德')
        const tomb1 = type === 'hang' && (loc === '洞穴巨人墓地1层')
        const tomb2 = type === 'hang' && (loc === '洞穴巨人墓地2层')
        const fortress1 = type === 'hang' && (loc === '乌特加德要塞1层')
        const fortress2 = type === 'hang' && (loc === '乌特加德要塞2层')
        const fortress3 = type === 'hang' && (loc === '乌特加德要塞3层')
        const worldTree1 = type === 'hang' && (loc === '世界树一层')
        const worldTree2 = type === 'hang' && (loc === '世界树二层')
        const worldTree3 = type === 'hang' && (loc === '世界树三层')
        const grasil = type === 'hang' && (loc === '格拉西尔森林')
        const eiden = type === 'hang' && (loc === '伊顿的峡谷')
        const supportsPoints = sparta || kara || arbit || troll || trollUpper || utgard || tomb1 || tomb2 || fortress1 || fortress2 || fortress3 || worldTree1 || worldTree2 || worldTree3 || grasil || eiden || (type === 'chest')

        if (supportsPoints) {
          const points = configContent.value.outdoor.customPoints || []
          if (points.length < 1) {
            ElMessage.error('该地图支持定点挂机，至少需要设置1个定点坐标')
            return
          }
        }
      }

      // 3. 挖琥珀如果启用，必须至少设置1个定点
      if (configContent.value.outdoor.amber.enabled) {
        const amberLoc = configContent.value.outdoor.amber.location || ''
        if (['世界树一层', '世界树二层', '世界树三层', '世界树四层'].includes(amberLoc)) {
          const amberPoints = configContent.value.outdoor.amber.customPoints || []
          if (amberPoints.length < 1) {
            ElMessage.error('挖琥珀至少需要设置1个定点坐标')
            return
          }
        }
      }

      // 4. 混沌神殿如果启用，必须至少设置1个定点
      if (configContent.value.dungeons.sigren.enabled) {
        const sigrenFloor = configContent.value.dungeons.sigren.floor || ''
        if (['1', '2', '3', '4', '5'].includes(sigrenFloor)) {
          const sigrenPoints = configContent.value.dungeons.sigren.customPoints || []
          if (sigrenPoints.length < 1) {
            ElMessage.error('周本-混沌神殿至少需要设置1个定点坐标')
            return
          }
        }
      }

      // 5. 弗尔克范格如果启用，必须至少设置1个定点
      if (configContent.value.dungeons.polk.enabled) {
        const polkFloor = configContent.value.dungeons.polk.floor || ''
        if (['1', '2', '3', '5'].includes(polkFloor)) {
          const polkPoints = configContent.value.dungeons.polk.customPoints || []
          if (polkPoints.length < 1) {
            ElMessage.error('周本-弗尔克范格至少需要设置1个定点坐标')
            return
          }
        }
      }

      // 6. 黄金殿堂如果启用，必须至少设置1个定点
      if (configContent.value.dungeons.goldenHall.enabled) {
        const goldenHallFloor = configContent.value.dungeons.goldenHall.floor || ''
        if (['1', '2', '3'].includes(goldenHallFloor)) {
          const goldenHallPoints = configContent.value.dungeons.goldenHall.customPoints || []
          if (goldenHallPoints.length < 1) {
            ElMessage.error('黄金殿堂至少需要设置1个定点坐标')
            return
          }
        }
      }

      // 7. 激战地如果启用，必须至少设置1个定点
      if (configContent.value.outdoor.battleground.enabled) {
        const bgPoints = configContent.value.outdoor.battleground.customPoints || []
        if (bgPoints.length < 1) {
          ElMessage.error('激战地至少需要设置1个定点坐标')
          return
        }
      }

      // 验证自动喊话配置
      if (configContent.value.autoShout.enabled) {
        const content = configContent.value.autoShout.content?.trim()
        if (!content) {
          ElMessage.error('启用自动喊话时，喊话内容不能为空')
          return
        }
      }

      saving.value = true
      try {

        const configData = {
          name: configForm.value.name,
          description: configForm.value.description,
          game: '尤弥尔传奇国际服',
          content: {
            角色模式: configContent.value.characterMode,
            账号大区: configContent.value.accountRegion,
            主角色位置: configContent.value.mainCharacterPosition,
            选区: configContent.value.placeholderZone,
            创建角色: {
              启用: configContent.value.createCharacter.enabled,
              职业: configContent.value.createCharacter.profession
            },
            游戏更新: configContent.value.gameUpdate,
            自动更新: configContent.value.autoUpdate,
            代理类型: configContent.value.proxyType,
            启用代理: configContent.value.enableProxy,
            主线任务: {
              启用: configContent.value.mainQuest.enabled,
              类型: configContent.value.mainQuest.type,
              等级: configContent.value.mainQuest.level,
              大章节: configContent.value.mainQuest.chapterMajor,
              小章: configContent.value.mainQuest.chapterMinor,
              死亡限制: configContent.value.mainQuest.deathLimit,
              冷却小时: configContent.value.mainQuest.cooldownHours
            },
            每日课题区域: {
              启用: configContent.value.dailyQuest.enabled,
              每日重复挑战: {
                启用: configContent.value.dailyQuest.repeatChallenge.enabled,
                次数: configContent.value.dailyQuest.repeatChallenge.count,
                类型: configContent.value.dailyQuest.repeatChallenge.type
              },
              每日采集任务: configContent.value.dailyQuest.collectionTask
            },
            故事成就黑色女武神: {
              交易所任务: configContent.value.storyAchievement.exchangeTask,
              大招任务: configContent.value.storyAchievement.ultimateTask,
              乌坦加德之影: configContent.value.storyAchievement.utgardShadow,
              世界树迪西尔: configContent.value.storyAchievement.worldTreeDisir,
              蓝鸟: configContent.value.storyAchievement.blueBird,
              红鸟: configContent.value.storyAchievement.purpleBird,
              希格露恩试炼: configContent.value.storyAchievement.sigrenTrial
            },
            系统设置: {
              开机自启动: configContent.value.systemSettings.autoStart,
              艾琳系统: configContent.value.systemSettings.ailinSystem,
              随机延时启动: configContent.value.systemSettings.randomDelayStart,
              定时截屏: configContent.value.systemSettings.scheduledScreenshot,
              自动更新脚本: configContent.value.systemSettings.autoUpdateScript,
              启动脚本杀代理和游戏: configContent.value.systemSettings.killProxyAndGameOnStart,
              更新脚本杀代理和游戏: configContent.value.systemSettings.killProxyAndGameOnUpdate,
              每次启动游戏前都开一次代理: configContent.value.systemSettings.openProxyBeforeStart,
              安卓11输入法: configContent.value.systemSettings.android11InputMethod,
              系统画面长时间不动自动重启游戏: configContent.value.systemSettings.autoRestartOnIdle,
              登录选项: configContent.value.systemSettings.loginType,
              谷歌账号类型: configContent.value.systemSettings.googleAccountType,
              谷歌登录界面选择: configContent.value.systemSettings['谷歌登录界面选择'],
              读秒倒计时: configContent.value.systemSettings.countdownSeconds,
              连续进入游戏失败关闭脚本次数: configContent.value.systemSettings.gameEnterFailStopThreshold
            },
            游戏商城: {
              自动商场买蓝: configContent.value.gameShop.autoBuyMana,
              武器强化石: configContent.value.gameShop.weaponStone,
              服饰强化石: configContent.value.gameShop.clothesStone,
              饰品强化石: configContent.value.gameShop.accessoryStone,
              镐锤强化石: configContent.value.gameShop.hammerStone,
              武器强化石箱子: configContent.value.gameShop.weaponStoneBox,
              服饰强化石箱子: configContent.value.gameShop.clothesStoneBox,
              饰品强化石箱子: configContent.value.gameShop.accessoryStoneBox,
              镐锤强化石箱子: configContent.value.gameShop.hammerStoneBox,
              瓦尔基里抽卡: configContent.value.gameShop.valkyrieDraw,
              迪西尔抽卡: configContent.value.gameShop.dixierDraw,
              休息点数: configContent.value.gameShop.restPoints,
              休息点数道具: {
                琥珀口袋: configContent.value.gameShop.restPointItems.amberPocket,
                印章强化石: configContent.value.gameShop.restPointItems.sealEnhanceStone,
                法力再生药水: configContent.value.gameShop.restPointItems.manaRegenPotion,
                强化石选择箱子: configContent.value.gameShop.restPointItems.enhanceStoneBox
              },
              荣誉商人: {
                荣誉商人: configContent.value.gameShop.honorMerchant.enabled,
                英雄灵魂: configContent.value.gameShop.honorMerchant.heroSoul,
                黄昏石: configContent.value.gameShop.honorMerchant.duskStone,
                胜利印章礼包: configContent.value.gameShop.honorMerchant.victorySealPack,
                印章强化石: configContent.value.gameShop.honorMerchant.sealEnhanceStone,
                光耀的强化石选择箱子: configContent.value.gameShop.honorMerchant.gloriousEnhanceBox,
                黑暗强化石选择箱子: configContent.value.gameShop.honorMerchant.darkEnhanceBox,
                图案选择箱子: configContent.value.gameShop.honorMerchant.patternBox,
                公会技能书箱子: configContent.value.gameShop.honorMerchant.guildSkillBookBox,
                中级制作材料选择箱子: configContent.value.gameShop.honorMerchant.midMaterialBox,
                低级制作材料选择箱子: configContent.value.gameShop.honorMerchant.lowMaterialBox,
                法力再生药水: configContent.value.gameShop.honorMerchant.manaRegenPotion,
                迅速蜜酒: configContent.value.gameShop.honorMerchant.swiftMead
              }
            },
            副本: {
              女武神的殿堂西侧: {
                启用: configContent.value.dungeons.valkyrie.enabled,
                层数: configContent.value.dungeons.valkyrie.floor,
                等级: configContent.value.dungeons.valkyrie.floorLevel,
                难度: configContent.value.dungeons.valkyrie.difficulty,
                难度类型: configContent.value.dungeons.valkyrie.difficultyType,
                类型: configContent.value.dungeons.valkyrie.type,
                地图绕圈打: configContent.value.dungeons.valkyrie.mapCircle,
                失败限制: configContent.value.dungeons.valkyrie.failLimit,
                失败冷却小时: configContent.value.dungeons.valkyrie.failCooldownHours,
                时间限制: {
                  启用: configContent.value.dungeons.valkyrie.timeLimit.enabled,
                  开始时间: configContent.value.dungeons.valkyrie.timeLimit.startTime.split(':')[0],
                  结束时间: configContent.value.dungeons.valkyrie.timeLimit.endTime.split(':')[0]
                }
              },
              西格伦的迷宫西侧: {
                启用: configContent.value.dungeons.sigren.enabled,
                层数: configContent.value.dungeons.sigren.floor,
                难度: configContent.value.dungeons.sigren.difficulty,
                位置: configContent.value.dungeons.sigren.position,
                定点坐标: configContent.value.dungeons.sigren.customPoints && configContent.value.dungeons.sigren.customPoints.length > 0
                    ? configContent.value.dungeons.sigren.customPoints
                    : undefined,
                失败限制: configContent.value.dungeons.sigren.failLimit,
                失败冷却小时: configContent.value.dungeons.sigren.failCooldownHours,
                时间限制: {
                  启用: configContent.value.dungeons.sigren.timeLimit.enabled,
                  开始时间: configContent.value.dungeons.sigren.timeLimit.startTime.split(':')[0],
                  结束时间: configContent.value.dungeons.sigren.timeLimit.endTime.split(':')[0],
                  周几: configContent.value.dungeons.sigren.timeLimit.weekdays || [1, 2, 3, 4, 5, 6, 7]
                }
              },
              波尔克房间西侧: {
                启用: configContent.value.dungeons.polk.enabled,
                层数: configContent.value.dungeons.polk.floor,
                难度: configContent.value.dungeons.polk.difficulty,
                位置: configContent.value.dungeons.polk.position,
                类型: configContent.value.dungeons.polk.type,
                定点坐标: configContent.value.dungeons.polk.customPoints && configContent.value.dungeons.polk.customPoints.length > 0
                    ? configContent.value.dungeons.polk.customPoints
                    : undefined,
                失败限制: configContent.value.dungeons.polk.failLimit,
                失败冷却小时: configContent.value.dungeons.polk.failCooldownHours,
                时间限制: {
                  启用: configContent.value.dungeons.polk.timeLimit.enabled,
                  开始时间: configContent.value.dungeons.polk.timeLimit.startTime.split(':')[0],
                  结束时间: configContent.value.dungeons.polk.timeLimit.endTime.split(':')[0],
                  周几: configContent.value.dungeons.polk.timeLimit.weekdays || [1, 2, 3, 4, 5, 6, 7]
                }
              },
              黄金殿堂: {
                启用: configContent.value.dungeons.goldenHall.enabled,
                层数: configContent.value.dungeons.goldenHall.floor,
                难度: configContent.value.dungeons.goldenHall.difficulty,
                定点坐标: configContent.value.dungeons.goldenHall.customPoints && configContent.value.dungeons.goldenHall.customPoints.length > 0
                    ? configContent.value.dungeons.goldenHall.customPoints
                    : undefined
              },
              赛季英灵殿: {
                启用: configContent.value.dungeons.seasonalValkyrie.enabled,
                层数: configContent.value.dungeons.seasonalValkyrie.floor,
                等级: configContent.value.dungeons.seasonalValkyrie.floorLevel,
                难度类型: configContent.value.dungeons.seasonalValkyrie.difficultyType,
                类型: configContent.value.dungeons.seasonalValkyrie.type,
                地图绕圈打: configContent.value.dungeons.seasonalValkyrie.mapCircle,
                失败限制: configContent.value.dungeons.seasonalValkyrie.failLimit,
                失败冷却小时: configContent.value.dungeons.seasonalValkyrie.failCooldownHours
              }
            },
            野外: (() => {
              const points = Array.isArray(configContent.value.outdoor.customPoints)
                  ? configContent.value.outdoor.customPoints
                  : []
              const outdoorData = {
                类型: configContent.value.outdoor.type,
                位置: configContent.value.outdoor.location,
                定点挂机: configContent.value.outdoor.position,
                自动开箱: configContent.value.outdoor.autoChest,
                刷银币: configContent.value.outdoor.autoSilverFarming,
                精锐地图: configContent.value.outdoor.eliteMap,
                时间限制: {
                  启用: configContent.value.outdoor.timeLimit.enabled,
                  开始时间: configContent.value.outdoor.timeLimit.startTime.split(':')[0],
                  结束时间: configContent.value.outdoor.timeLimit.endTime.split(':')[0]
                },
                挖琥珀: {
                  启用: configContent.value.outdoor.amber.enabled,
                  位置: configContent.value.outdoor.amber.location,
                  更换频道: configContent.value.outdoor.amber.channel,
                  定点坐标: configContent.value.outdoor.amber.customPoints && configContent.value.outdoor.amber.customPoints.length > 0
                      ? configContent.value.outdoor.amber.customPoints
                      : undefined,
                  时间限制: {
                    启用: configContent.value.outdoor.amber.timeLimit.enabled,
                    开始时间: configContent.value.outdoor.amber.timeLimit.startTime.split(':')[0],
                    结束时间: configContent.value.outdoor.amber.timeLimit.endTime.split(':')[0]
                  }
                },
                激战地: {
                  启用: configContent.value.outdoor.battleground.enabled,
                  位置: configContent.value.outdoor.battleground.location,
                  定点坐标: configContent.value.outdoor.battleground.customPoints && configContent.value.outdoor.battleground.customPoints.length > 0
                      ? configContent.value.outdoor.battleground.customPoints
                      : undefined,
                  死亡次数: configContent.value.outdoor.battleground.deathCount,
                  时间限制: {
                    启用: configContent.value.outdoor.battleground.timeLimit.enabled,
                    开始时间: configContent.value.outdoor.battleground.timeLimit.startTime.split(':')[0],
                    结束时间: configContent.value.outdoor.battleground.timeLimit.endTime.split(':')[0]
                  }
                },
                离线挂机: {
                  启用: configContent.value.outdoor.offlineHang.enabled,
                  位置: configContent.value.outdoor.offlineHang.location,
                  时间限制: {
                    启用: configContent.value.outdoor.offlineHang.timeLimit.enabled,
                    开始时间: configContent.value.outdoor.offlineHang.timeLimit.startTime.split(':')[0],
                    结束时间: configContent.value.outdoor.offlineHang.timeLimit.endTime.split(':')[0]
                  }
                }
              }
              // 如果有定点坐标，添加到配置中
              if (points.length > 0) {
                outdoorData.定点坐标 = points.map(p => ({x: Number(p.x) || 0, y: Number(p.y) || 0}))
              }
              return outdoorData
            })(),
            挂机设置: {
              自动买药: configContent.value.hangSettings.autoBuyPotion,
              省电模式: configContent.value.hangSettings.powerSaveMode,
              血量阈值: configContent.value.hangSettings.healthThreshold,
              买药间隔: configContent.value.hangSettings.potionInterval,
              夜晚模式: configContent.value.hangSettings.nightMode,
              挂机范围: configContent.value.hangSettings.hangRange,
              采集范围: configContent.value.hangSettings.collectRange,
              自动使用所有消耗品: configContent.value.hangSettings.autoUseConsumables,
              更换频道: configContent.value.hangSettings.changeChannel,
              频道: (() => {
                const channel = configContent.value.hangSettings.channel
                if (/^\d+$/.test(channel)) {
                  return `频道${channel}`
                }
                return channel
              })(),
              体力药水: configContent.value.hangSettings.staminaPotion,
              自动使用体力药水: configContent.value.hangSettings.autoUseStaminaPotion,
              移动卷轴: configContent.value.hangSettings.moveScroll,
              回城卷轴: configContent.value.hangSettings.returnScroll,
              弓箭手回原点启用: configContent.value.hangSettings.archerReturnOriginEnabled,
              弓箭手回原点间隔: configContent.value.hangSettings.archerReturnOriginInterval
            },
            间隔时间: {
              分解装备间隔: configContent.value.hangSettings.decomposeInterval,
              清理背包间隔: configContent.value.hangSettings.cleanBagInterval,
              仓库存储间隔: configContent.value.hangSettings.storageInterval,
              诺恩宝物间隔: configContent.value.hangSettings.nornTreasureInterval,
              兑换码间隔: configContent.value.hangSettings.redeemCodeInterval
            },
            其他需求: {
              委托: configContent.value.otherRequirements.commission,
              委托死亡限制: configContent.value.otherRequirements.commissionDeathLimit,
              委托冷却小时: configContent.value.otherRequirements.commissionCooldownHours,
              支线: configContent.value.otherRequirements.sideQuest,
              挑战死亡限制: configContent.value.otherRequirements.sideQuestDeathLimit,
              挑战冷却小时: configContent.value.otherRequirements.sideQuestCooldownHours,
              委托失败小时: configContent.value.otherRequirements.commissionFailHours,
              死亡恢复经验: configContent.value.otherRequirements.deathRecoverExp,
              自动分解装备: configContent.value.otherRequirements.autoDecomposeEquip,
              分解白装: configContent.value.otherRequirements.decomposeWhite,
              分解绿装: configContent.value.otherRequirements.decomposeGreen,
              分解蓝装: configContent.value.otherRequirements.decomposeBlue,
              分解强化: configContent.value.otherRequirements.decomposeEnhanced,
              分解鉴定: configContent.value.otherRequirements.decomposeIdentified,
              分解技能书: configContent.value.otherRequirements.decomposeSkillBook,
              自动强化: configContent.value.otherRequirements.autoEnhance,
              自动女武神合成: configContent.value.otherRequirements.autoValkyrieCompose,
              自动宠物合成: configContent.value.otherRequirements.autoPetCompose,
              自动穿戴女武神卡: configContent.value.otherRequirements.autoEquipValkyrie,
              自动穿戴宠物: configContent.value.otherRequirements.autoEquipPet,
              自动琥珀封箱: configContent.value.otherRequirements.autoAmberBox,
              琥珀封箱价格阈值: configContent.value.otherRequirements.amberBoxPriceThreshold,
              琥珀封箱价格阈值启用: configContent.value.otherRequirements.amberBoxPriceThresholdEnabled,
              自动开装备箱子: configContent.value.otherRequirements.autoEquipBox,
              自动购买技能: configContent.value.otherRequirements.autoBuySkill,
              自动购买技能银币上限: configContent.value.otherRequirements.autoBuySkillMaxSilver,
              自动仓库: configContent.value.otherRequirements.autoWarehouse,
              空间不足无限清理: configContent.value.otherRequirements.warehouseInfiniteCleanup,
              空间不足红屏提醒: configContent.value.otherRequirements.warehouseRedScreenAlert,
              自动释放大招: configContent.value.otherRequirements.autoUltimate,
              银币恢复: configContent.value.otherRequirements.silverRestore,
              银币恢复启用: configContent.value.otherRequirements.silverRestoreEnabled,
              潜能升级启用: configContent.value.otherRequirements.potentialUpgradeEnabled,
              潜能升级技能: configContent.value.otherRequirements.potentialUpgradeSkill,
              迪西尔升级: configContent.value.otherRequirements.dixierUpgrade,
              瓦尔基里升级: configContent.value.otherRequirements.valkyrieUpgrade,
              自动技能升级: configContent.value.otherRequirements.autoSkillUpgrade,
              自动神器: configContent.value.otherRequirements.autoArtifact,
              自动智能制作蓝装: configContent.value.otherRequirements.autoSmartCraftBlue,
              自动穿戴高级装备: configContent.value.otherRequirements.autoEquipAdvanced,
              自动鉴定装备: configContent.value.otherRequirements.autoIdentifyEquipment,
              鉴定装备类型: configContent.value.otherRequirements.identifyEquipmentType,
              鉴定装备级别: configContent.value.otherRequirements.identifyEquipmentLevel,
              剧情卡组提升启用: configContent.value.otherRequirements.storyCardUpgradeEnabled,
              剧情卡组提升等级: configContent.value.otherRequirements.storyCardUpgradeLevel,
              兑换码: configContent.value.otherRequirements.redeemCodeEnabled,
              兑换码列表: configContent.value.otherRequirements.redeemCodes,
              脚本内置兑换码: configContent.value.otherRequirements.useBuiltInRedeemCodes,
              赛季指令: configContent.value.otherRequirements.seasonInstruction,
              祝福之塔: configContent.value.otherRequirements.blessingTower,
              自动领邮件: configContent.value.otherRequirements.autoClaimMail
            },
            工会: {
              自动加入公会: configContent.value.guild.autoJoin,
              '24次科技捐献': configContent.value.guild.techContribute24,
              银币捐献: configContent.value.guild.silverContribute,
              琥珀捐献: configContent.value.guild.amberContribute,
              领取工会箱子: configContent.value.guild.collectGuildChest,
              工会帮助: configContent.value.guild.guildHelp,
              公会技能: configContent.value.guild.guildSkill,
              协作道具: configContent.value.guild.collaborationItemType,
              技能协作间隔: configContent.value.guild.skillCollaborationInterval
            },
            交易所: {
              自动出售: configContent.value.tradingPost.autoSell,
              启用价格差: configContent.value.tradingPost.enablePriceDifference,
              价格差: configContent.value.tradingPost.priceDifference,
              下架超过小时: configContent.value.tradingPost.delistHours,
              价格保护: Array.isArray(configContent.value.tradingPost.priceProtection)
                  ? configContent.value.tradingPost.priceProtection.filter(item => item && item.itemName && item.itemName.trim() !== '')
                  : [{itemName: '琥珀箱子：50,000', minPrice: 300}],
              强化可交易装备: {
                是否开启强化装备: configContent.value.tradingPost.equipmentEnhancement.enabled,
                装备品质: {
                  白: {
                    启用: configContent.value.tradingPost.equipmentEnhancement.equipmentQuality.white.enabled,
                    等级: configContent.value.tradingPost.equipmentEnhancement.equipmentQuality.white.level
                  },
                  绿: {
                    启用: configContent.value.tradingPost.equipmentEnhancement.equipmentQuality.green.enabled,
                    等级: configContent.value.tradingPost.equipmentEnhancement.equipmentQuality.green.level
                  }
                },
                强化装备默认个数: configContent.value.tradingPost.equipmentEnhancement.defaultCount,
                是否智能开强化石箱子: configContent.value.tradingPost.equipmentEnhancement.autoOpenStoneBox,
                武器强化石: configContent.value.tradingPost.equipmentEnhancement.weaponStone ?? false,
                防具强化石: configContent.value.tradingPost.equipmentEnhancement.armorStone ?? false,
                饰品强化石: configContent.value.tradingPost.equipmentEnhancement.accessoryStone ?? false,
                生活装备强化石: configContent.value.tradingPost.equipmentEnhancement.lifeEquipmentStone ?? false
              },
              是否上架自定义装备价格: configContent.value.tradingPost.equipmentCustomPricesEnabled ?? false,
              装备自定义价格: Array.isArray(configContent.value.tradingPost.equipmentCustomPrices)
                  ? configContent.value.tradingPost.equipmentCustomPrices.filter(item => item && item.level && item.level > 0)
                  : []
            },
            区域交易所: {
              自动出售: configContent.value.regionalExchange.autoSell,
              交易类型启用: configContent.value.regionalExchange.exchangeTypeEnabled,
              交易类型: configContent.value.regionalExchange.exchangeType,
              普通道具: configContent.value.regionalExchange.itemType,
              装备: configContent.value.regionalExchange.equipment,
              启用价格差: configContent.value.regionalExchange.enablePriceDifference,
              价格差: configContent.value.regionalExchange.priceDifference,
              下架超过小时: configContent.value.regionalExchange.delistHours
            },
            活动制作: {
              活动制作: configContent.value.activityCraft.enabled,
              合成蓝: configContent.value.activityCraft.synthesizeBlue,
              诺恩宝物: configContent.value.activityCraft.nornTreasure,
              使用可交易宝物: configContent.value.activityCraft.useTradableTreasure,
              可交易宝物普通: configContent.value.activityCraft.tradableTreasureNormal,
              可交易宝物高级: configContent.value.activityCraft.tradableTreasureAdvanced,
              古董制作: configContent.value.activityCraft.antiqueCraft,
              制作卷轴: configContent.value.activityCraft.scrollCraft,
              制作食物: configContent.value.activityCraft.foodCraft
            },
            背包: {
              背包开格子: configContent.value.backpack.slotsEnabled,
              背包格子数: configContent.value.backpack.slots
            },
            收藏区域: {
              收藏开关: configContent.value.collection.enabled,
              最高价格: configContent.value.collection.maxPrice,
              装备品质: {
                白: configContent.value.collection.equipmentQuality.white,
                绿: configContent.value.collection.equipmentQuality.green,
                蓝: configContent.value.collection.equipmentQuality.blue
              }
            },
            BOSS区域: {
              远征5人: {
                启用: configContent.value.bossArea.expedition5.enabled,
                等级: configContent.value.bossArea.expedition5.level,
                没有队伍就开房间: configContent.value.bossArea.expedition5.createRoom
              },
              远征15人: {
                启用: configContent.value.bossArea.expedition15.enabled,
                等级: configContent.value.bossArea.expedition15.level,
                没有队伍就开房间: configContent.value.bossArea.expedition15.createRoom
              },
              赛季5人: {
                启用: configContent.value.bossArea.expeditionSeasonal5.enabled,
                等级: configContent.value.bossArea.expeditionSeasonal5.level,
                没有队伍就开房间: configContent.value.bossArea.expeditionSeasonal5.createRoom
              },
              世界boss: configContent.value.bossArea.worldBoss,
              时间限制: {
                启用: configContent.value.bossArea.timeLimit.enabled,
                开始时间: configContent.value.bossArea.timeLimit.startTime.split(':')[0],
                结束时间: configContent.value.bossArea.timeLimit.endTime.split(':')[0]
              }
            },
            需要存仓库的道具: configContent.value.warehouseItems,
            内置存仓: configContent.value.builtInWarehouse,
            需要删除的垃圾道具: configContent.value.deleteItems,
            内置删除: configContent.value.builtInDelete,
            需要使用的道具: configContent.value.useItems || [],
            特殊道具使用: {
              成长召唤券选择箱子: configContent.value.growthSummonTicketBox,
              高级升阶结晶石选择箱子: configContent.value.advancedUpgradeCrystalBox,
              高级帕特拉卡牌选择箱子: configContent.value.advancedPatraCardBox,
              宝物岛召唤券选择箱子: configContent.value.treasureIslandSummonTicketBox,
              玛尼召唤券选择箱子: configContent.value.maniSummonTicketBox,
              宝物岛稀有召唤券选择箱子: configContent.value.treasureIslandRareSummonTicketBox,
              协作召唤券选择箱子: configContent.value.collaborationSummonTicketBox,
              誓言召唤券11次选择箱子: configContent.value.oathSummonTicket11TimesBox
            },
            自动喊话: {
              启用: configContent.value.autoShout.enabled,
              喊话内容: configContent.value.autoShout.content,
              间隔时间: configContent.value.autoShout.interval
            },
            额外功能: {
              不断开离线箱子: configContent.value.extraFeatures.keepOpeningOfflineChest,
              自动做蓝书: configContent.value.extraFeatures.autoCraftBlueBook,
              自动做红书: configContent.value.extraFeatures.autoCraftRedBook
            },
            离线箱子: {
              不断开启: configContent.value.extraFeatures.keepOpeningOfflineChest
            }
          }
        }


        let configId
        if (isEditMode.value) {
          // 更新配置
          const updateResponse = await updateConfig(props.id, configData)
          configId = updateResponse.data.id
          ElMessage.success('配置更新成功')
        } else {
          // 创建配置
          const createResponse = await createConfig(configData)
          configId = createResponse.data.id
          ElMessage.success('配置创建成功')
        }

        // 保存活动制作配置
        if (configId) {
          await saveActivityCraftConfig(configId)
        }

        router.push('/dashboard/scripts')
      } catch (error) {
        console.error('保存配置失败:', error)
        ElMessage.error('保存配置失败: ' + (error.response?.data?.message || error.message))
      } finally {
        saving.value = false
      }
    }


    // 加载活动制作模板树
    const loadActivityCraftTree = async () => {
      const game = '尤弥尔传奇国际服' // 根据实际配置获取游戏名称
      activityCraftLoading.value = true
      try {
        const response = await getActivityCraftTemplateTree(game)
        activityCraftTree.value = response.data || []
      } catch (error) {
        console.error('加载活动制作模板失败:', error)
        // 不显示错误消息，因为可能还没有模板
      } finally {
        activityCraftLoading.value = false
      }
    }

    // 加载活动制作配置
    const loadActivityCraftConfig = async (configId) => {
      try {
        const response = await getConfigActivityCraft(configId)
        activityCraftSelections.value = response.data || {}
      } catch (error) {
        console.error('加载活动制作配置失败:', error)
        activityCraftSelections.value = {}
      }
    }

    // 保存活动制作配置
    const saveActivityCraftConfig = async (configId) => {
      try {
        // 转换selections格式：将数字键转换为字符串键
        const selections = {}
        for (const [key, value] of Object.entries(activityCraftSelections.value)) {
          selections[key] = value
        }
        await saveConfigActivityCraft(configId, selections)
      } catch (error) {
        console.error('保存活动制作配置失败:', error)
        // 不阻止配置保存，只记录错误
      }
    }

    // 检查活动是否启用
    const isActivityEnabled = (activityId) => {
      return activityCraftSelections.value[activityId] === 1
    }

    // 切换活动启用状态
    const toggleActivity = (activityId, enabled) => {
      const status = enabled ? 1 : 0
      activityCraftSelections.value[activityId] = status
      
      // 联动处理所有子项（包括二级菜单和三级商品）
      const activity = activityCraftTree.value.find(a => a.id === activityId)
      if (activity && activity.children) {
        activity.children.forEach(child => {
          // 处理二级节点 (可能是商品或菜单)
          activityCraftSelections.value[child.id] = status
          
          // 如果是菜单，处理其下的三级商品
          if (child.children && child.children.length > 0) {
            child.children.forEach(grandChild => {
              activityCraftSelections.value[grandChild.id] = status
            })
          }
        })
      }
    }

    // 检查商品是否启用
    const isProductEnabled = (productId) => {
      return activityCraftSelections.value[productId] === 1
    }

    // 切换商品启用状态
    const toggleProduct = (productId, enabled) => {
      activityCraftSelections.value[productId] = enabled ? 1 : 0
      
      // 如果选中了子商品，自动选中所属的一级活动
      if (enabled) {
        for (const activity of activityCraftTree.value) {
          // 检查是否是直接子项 (level 2)
          const isDirectChild = activity.children && activity.children.some(c => c.id === productId)
          
          // 检查是否在二级菜单内 (level 3)
          let isInMenu = false
          if (activity.children) {
            for (const menu of activity.children) {
              if (menu.children && menu.children.some(c => c.id === productId)) {
                isInMenu = true
                break
              }
            }
          }

          if (isDirectChild || isInMenu) {
            activityCraftSelections.value[activity.id] = 1
            break
          }
        }
      }
    }

    // 获取活动下的“菜单”（有子节点的 level=2）
    const getMenus = (activity) => {
      if (!activity.children) return []
      return activity.children.filter(child => child.children && child.children.length > 0)
    }

    // 获取活动下的“直接商品”（没有子节点的 level=2）
    const getDirectProducts = (activity) => {
      if (!activity.children) return []
      return activity.children.filter(child => !child.children || child.children.length === 0)
    }

    onMounted(async () => {
      // 先加载配置数据（编辑模式），再加载道具数据
      // 这样可以确保在编辑模式下，如果配置中没有需要使用的道具字段，可以默认选中所有道具
      await loadConfigData()
      await loadGameItems()
      await loadActivityCraftTree()

      // 如果是编辑模式且配置中没有需要使用的道具字段，默认选中所有道具
      if (isEditMode.value && (!configContent.value.useItems || configContent.value.useItems.length === 0)) {
        configContent.value.useItems = useItems.value.map(item => item.id)
      }
      // 如果是编辑模式且配置中没有需要存仓库的道具字段，默认选中所有道具
      if (isEditMode.value && (!configContent.value.warehouseItems || configContent.value.warehouseItems.length === 0)) {
        configContent.value.warehouseItems = warehouseItems.value.map(item => item.id)
      }
    })

    return {
      configForm,
      configContent,
      saving,
      loading,
      activeTab,
      useManualInput,
      isEditMode,
      isViewMode,
      isDefaultConfig,
      isSharedConfig,
      outdoorOptions,
      offlineHangOptions,
      warehouseItems,
      deleteItems,
      useItems,
      onOutdoorTypeChange,
      toggleWarehouseItem,
      toggleDeleteItem,
      toggleUseItem,
      // 全选相关
      isAllWarehouseSelected,
      isAllDeleteSelected,
      isAllUseSelected,
      toggleSelectAllWarehouse,
      toggleSelectAllDelete,
      toggleSelectAllUse,
      handleImageError,
      getItemBackgroundColorClass,
      saveConfig,
      // 定点坐标选择
      showPointButton,
      showAmberPointButton,
      hasCustomPoints,
      hasAmberCustomPoints,
      pointDialogVisible,
      amberPointDialogVisible,
      customPointsDisplay,
      mapCanvasRef,
      amberMapCanvasRef,
      openPointPicker,
      openAmberPointPicker,
      openPolkPointPicker,
      openSigrenPointPicker,
      openGoldenHallPointPicker,
      openBattlegroundPointPicker,
      onMapClick,
      savePoint,
      clearAllPoints,
      clearAllAmberPoints,
      clearAllPolkPoints,
      clearAllSigrenPoints,
      clearAllGoldenHallPoints,
      clearAllBattlegroundPoints,
      closePointDialog,
      getClearButtonText,
      hasCurrentCustomPoints,
      addPriceProtection,
      removePriceProtection,
      addEquipmentCustomPrice,
      removeEquipmentCustomPrice,
      checkLevelDuplicate,
      validateEquipmentLevel,
      mapImageUrl,
      mapImageLoaded,
      amberMapImageLoaded,
      polkMapImageLoaded,
      sigrenMapImageLoaded,
      goldenHallMapImageLoaded,
      battlegroundMapImageLoaded,
      pointPickerMode,
      showPolkPointButton,
      showSigrenPointButton,
      showGoldenHallPointButton,
      hasPolkCustomPoints,
      hasSigrenCustomPoints,
      hasGoldenHallCustomPoints,
      hasBattlegroundCustomPoints,
      polkPointDialogVisible,
      sigrenPointDialogVisible,
      goldenHallPointDialogVisible,
      battlegroundPointDialogVisible,
      polkMapCanvasRef,
      sigrenMapCanvasRef,
      goldenHallMapCanvasRef,
      battlegroundMapCanvasRef,
      copyPointsToClipboard,
      importPoints,
      importPointsText,
      removePoint,
      hoveredPointIndex,
      // 活动制作相关
      activityCraftTree,
      activityCraftLoading,
      activeActivityCollapseItems,
      onActivityCraftEnabledChange,
      isActivityEnabled,
      toggleActivity,
      isProductEnabled,
      toggleProduct,
      getMenus,
      getDirectProducts
    }
  }
}
</script>

<style scoped>
.config-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  background: #fafbfc;
}

/* Reduced header padding for more compact layout */
.config-header {
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border-bottom: 1px solid #e2e8f0;
  padding: 8px 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.header-left {
  flex: 1;
}

.config-header h2 {
  margin: 0 0 8px 0;
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
}

.header-notice {
  max-width: 400px;
  height: 80%;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #64748b;
  border-radius: 6px;
  padding: 8px;
  box-shadow: 0 2px 8px rgba(100, 116, 139, 0.1);
  display: flex;
  align-items: center;
}

.notice-content-compact h4 {
  margin: 0 0 4px 0;
  font-size: 13px;
  font-weight: 600;
  color: #1d4ed8;
}

.notice-content-compact p {
  margin: 1px 0;
  font-size: 11px;
  color: #1e40af;
  line-height: 1.3;
}

.notice-content-compact strong {
  color: #1d4ed8;
  font-weight: 600;
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 12px;
}

.config-name-group,
.config-description-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.config-name-group label,
.config-description-group label {
  font-size: 14px;
  color: #475569;
  font-weight: 500;
}

.config-name-input,
.config-description-input {
  padding: 6px 10px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  background: white;
  transition: border-color 0.2s ease;
  width: 200px;
}

.config-description-input {
  width: 300px;
}

.config-name-input:focus,
.config-description-input:focus {
  outline: none;
  border-color: #64748b;
  box-shadow: 0 0 0 3px rgba(100, 116, 139, 0.1);
}

.save-btn {
  background: #475569;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.save-btn:hover:not(:disabled) {
  background: #334155;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.save-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.shared-config-notice {
  color: #ff9500;
  font-size: 14px;
  font-weight: 500;
  padding: 10px 20px;
  background: rgba(255, 149, 0, 0.1);
  border-radius: 6px;
  display: inline-block;
}

/* Simplified loading overlay */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(248, 250, 252, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(2px);
}

.loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.loading-spinner .spinner {
  width: 32px;
  height: 32px;
  border: 2px solid #e2e8f0;
  border-top: 2px solid #64748b;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-spinner p {
  color: #475569;
  font-size: 14px;
  margin: 0;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.config-name-input:disabled {
  background: #f8fafc;
  color: #64748b;
  cursor: not-allowed;
}

.config-content {
  flex: 1;
  padding: 12px;
  background: #fafbfc;
  overflow-x: auto;
  overflow-y: auto;
}

/* Tab样式 */
.tab-container {
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.tab-nav {
  display: flex;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  justify-content: flex-start;
  gap: 0;
}

.tab-button {
  width: 100px;
  padding: 12px 16px;
  border: none;
  background: transparent;
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 3px solid transparent;
}

.tab-button:hover {
  background: #f1f5f9;
  color: #475569;
}

.tab-button.active {
  background: #ffffff;
  color: #475569;
  border-bottom-color: #475569;
}

/* 教程文本链接样式 */
.tutorial-text-link {
  color: #3b82f6;
  text-decoration: none;
  font-size: 12px;
  font-weight: 500;
  transition: color 0.2s ease;
}

.tutorial-text-link:hover {
  color: #1d4ed8;
  text-decoration: underline;
}

.tab-content {
  background: #ffffff;
}

.tab-pane {
  padding: 16px;
  min-height: auto;
  min-width: 1200px; /* 设置最小宽度以确保内容完整显示 */
}

.items-config-container {
  width: 100%;
  margin: 0;
}


/* Reduced margins and padding for more compact sections */
.grouped-section {
  margin-bottom: 8px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.grouped-content {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 8px;
  height: 100%;
  align-content: start;
}

.right-sections-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: 100%;
}

.compact-section {
  padding: 0;
  background: transparent;
}

.bordered-section {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px;
  background: #ffffff;
}

/* 野外挂机子区域样式 */
.outdoor-subsection {
  margin-bottom: 12px;
  padding: 6px 0;
}

.outdoor-subsection:not(:last-child) {
  border-bottom: 1px solid #f1f5f9;
  padding-bottom: 12px;
}

.subsection-title {
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  margin: 0 0 8px 0;
  padding-left: 4px;
  border-left: 3px solid #64748b;
}

/* 挖琥珀内联提示样式 */
.inline-notice {
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
}

/* 激战地提示样式 */
.battleground-notice {
  margin-bottom: 8px;
}

.notice-content {
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 4px;
  padding: 6px 8px;
  display: flex;
  align-items: center;
}

.notice-text {
  font-size: 12px;
  color: #dc2626;
  line-height: 1.4;
}

.compact-title {
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
}

/* Reduced note padding and margin */
.compact-note {
  font-size: 13px;
  color: #92400e;
  margin-bottom: 12px;
  padding: 8px;
  background: #fef3c7;
  border-radius: 6px;
  border-left: 3px solid #d97706;
}

.form-section {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.section-title {
  margin: 0 0 12px 0;
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  padding-bottom: 6px;
  border-bottom: 1px solid #e2e8f0;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.section-header .section-title {
  margin: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.select-all-btn {
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.select-all-btn:hover:not(:disabled) {
  background: #2563eb;
  transform: translateY(-1px);
}

.select-all-btn:active:not(:disabled) {
  background: #1d4ed8;
  transform: translateY(0);
}

.select-all-btn:disabled {
  background: #94a3b8;
  cursor: not-allowed;
  transform: none;
}

/* 时间设置样式 */
.time-settings {
  margin-top: 12px;
  margin-bottom: 12px;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e9ecef;
}

.time-range {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  flex-wrap: wrap;
}

.time-separator {
  color: #666;
  font-size: 12px;
  white-space: nowrap;
  font-weight: 500;
}

/* 时间选择下拉框样式 */
.time-range .el-select {
  flex-shrink: 0;
}

.time-range .el-select .el-select__wrapper {
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  border: 1px solid #ddd;
}

.time-range .el-select .el-select__wrapper:hover {
  border-color: #409EFF;
}

/* BOSS时间提示样式 */
.boss-time-notice {
  margin-top: 8px;
  padding: 8px;
  background: #e8f4fd;
  border: 1px solid #b3d8ff;
  border-radius: 4px;
}

.boss-time-notice .notice-content {
  display: flex;
  align-items: flex-start;
  gap: 6px;
}

.boss-time-notice .notice-text {
  font-size: 12px;
  line-height: 1.4;
  color: #1976d2;
}

/* 夜晚模式提示样式 */
.night-mode-notice {
  margin-left: 8px;
  display: inline-block;
}

.night-mode-notice .notice-text {
  font-size: 11px;
  color: #666;
  font-style: italic;
}

/* 远征设置样式 */
.expedition-setting {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
}

.expedition-setting .el-checkbox {
  width: 60px;
  flex-shrink: 0;
}

.expedition-setting .el-select {
  flex-shrink: 0;
}

/* 5人远征行间距调整 */
.expedition-5-row {
  margin-top: 8px;
}

/* 远征内联选项样式 */
.expedition-inline-option {
  margin-left: 6px;
  font-size: 12px;
}

.expedition-inline-option.is-disabled {
  opacity: 0.5;
}

/* 移动端时间设置优化 */
@media (max-width: 768px) {
  .time-range {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }

  .time-range .el-select {
    width: 60px !important;
  }

  .night-mode-notice {
    margin-left: 0;
    margin-top: 4px;
    display: block;
  }

  .expedition-setting {
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .expedition-setting .el-select {
    width: 100px !important;
  }

  .expedition-inline-option {
    margin-left: 4px !important;
  }
}

.section-note {
  font-size: 13px;
  color: #92400e;
  margin-bottom: 12px;
  background: #fef3c7;
  padding: 8px;
  border-radius: 6px;
  border-left: 3px solid #d97706;
}

.main-content-grid {
  display: grid;
  grid-template-columns: 1fr 1.5fr 1fr;
  gap: 8px;
  margin-bottom: 8px;
  align-items: stretch;
  min-width: 1100px; /* 确保网格布局有足够空间 */
}

.main-content-grid > .form-section {
  display: flex;
  flex-direction: column;
}

.full-width {
  grid-column: 1 / -1;
}

.form-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.checkbox-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px 12px;
  margin-bottom: 8px;
}

.checkbox-compact {
  margin-bottom: 8px;
}

.checkbox-row {
  display: flex;
  gap: 20px;
  margin-bottom: 6px;
  align-items: center;
  justify-content: flex-start;
}

.shop-rows .checkbox-row {
  gap: 16px;
}

.shop-rows .checkbox-row:has(.el-checkbox:nth-child(2)) {
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: start;
}

.shop-rows .checkbox-row:has(.el-checkbox:nth-child(2)) :deep(.el-checkbox) {
  width: 100%;
}

.checkbox-row:last-child {
  margin-bottom: 0;
}

.checkbox-grid-aligned {
  display: grid;
  grid-template-columns: 150px 150px 150px;
  gap: 6px 20px;
  margin-bottom: 8px;
  justify-content: start;
}

.decompose-section {
  margin-bottom: 12px;
}

.section-divider {
  margin-top: 12px;
  margin-bottom: 12px;
  padding-top: 12px;
  padding-bottom: 12px;
  border-top: 1px solid #e2e8f0;
}

.section-divider:first-child {
  margin-top: 0;
  padding-top: 0;
  border-top: none;
}

.decompose-options {
  display: flex;
  gap: 16px;
  margin-left: 20px;
  margin-top: 8px;
  padding: 8px;
  background: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e9ecef;
}

.checkbox-grid-2x2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 12px;
  margin-bottom: 8px;
}

.checkbox-grid-aligned2 {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  column-gap: 8px;
  row-gap: 4px;
  margin-bottom: 8px;
  max-width: 100%;
}

.single-line {
  flex-wrap: nowrap;
}

.form-row label {
  font-size: 14px;
  color: #475569;
  white-space: nowrap;
  font-weight: 500;
}

.form-label {
  font-size: 14px;
  color: #475569;
  white-space: nowrap;
}

.form-hint {
  font-size: 13px;
  color: #94a3b8;
  white-space: nowrap;
  font-style: italic;
}

.small-text {
  font-size: 13px;
  color: #64748b;
}

/* Simple number input */
.simple-number-input {
  width: 50px;
  height: 24px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  text-align: center;
  font-size: 12px;
  color: #1e293b;
  background: #ffffff;
  outline: none;
  transition: border-color 0.2s ease;
}

.simple-number-input:focus {
  border-color: #64748b;
  box-shadow: 0 0 0 2px rgba(100, 116, 139, 0.1);
}

.simple-number-input:hover {
  border-color: #64748b;
}

.simple-text-input {
  width: 180px;
  height: 32px;
  padding: 0 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 12px;
  color: #1e293b;
  background: #ffffff;
  outline: none;
  transition: border-color 0.2s ease;
  box-sizing: border-box;
}

.simple-text-input:focus {
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.1);
}

.simple-text-input:hover {
  border-color: #409eff;
}

.dungeon-item {
  margin-bottom: 12px;
  padding: 12px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
}

/* 赛季功能区域背景色 - 淡青绿色 */
.seasonal-dungeon-item {
  background: #e6f7f5;
  border-color: #b8e6d9;
}

.seasonal-expedition-row {
  background: #e6f7f5;
  padding: 8px;
  border-radius: 6px;
  border: 1px solid #b8e6d9;
}

.dungeon-settings {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
  flex-wrap: wrap;
}

.dungeon-settings label {
  font-size: 13px;
  color: #475569;
  white-space: nowrap;
  font-weight: 500;
}

.inventory-section {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e2e8f0;
}

.item-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
  gap: 12px;
  margin-top: 12px;
}

/* 道具容器 */
.item-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.item-container:hover .item-icon {
  border-color: #64748b;
}

.item-container.selected .item-icon {
  border-color: #64748b;
  background: #f1f5f9;
}

/* 选中状态下保持背景色和边框色 */
.item-container.selected .item-icon.bg-light-green {
  background: linear-gradient(135deg, #c8e6c9 0%, #a5d6a7 100%);
  border-color: #66bb6a;
}

.item-container.selected .item-icon.bg-light-blue {
  background: linear-gradient(135deg, #bbdefb 0%, #90caf9 100%);
  border-color: #42a5f5;
}

.item-container.selected .item-icon.bg-light-purple {
  background: linear-gradient(135deg, #e1bee7 0%, #ce93d8 100%);
  border-color: #ab47bc;
}

.item-container.selected .item-icon.bg-light-gold {
  background: linear-gradient(135deg, #ffe082 0%, #ffd54f 100%);
  border-color: #ffb300;
}

/* Enhanced item icons */
.item-icon {
  width: 70px;
  height: 70px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.2s ease;
  background: #ffffff;
  position: relative;
  margin-bottom: 6px;
}

.item-icon.bg-light-green {
  background: linear-gradient(135deg, #e8f5e8 0%, #c8e6c9 100%);
  border-color: #81c784;
}

.item-icon.bg-light-blue {
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%);
  border-color: #64b5f6;
}

.item-icon.bg-light-purple {
  background: linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%);
  border-color: #ba68c8;
}

.item-icon.bg-light-gold {
  background: linear-gradient(135deg, #fff9e6 0%, #ffe082 100%);
  border-color: #ffc107;
}

.item-icon img {
  width: 40px;
  height: 40px;
  object-fit: contain;
}

/* 道具名称 */
.item-name {
  font-size: 12px;
  color: #4b5563;
  text-align: center;
  max-width: 85px;
  line-height: 1.2;
  word-wrap: break-word;
  word-break: break-all;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
}

.item-checkbox {
  position: absolute;
  bottom: 4px;
  right: 4px;
}

.empty-items {
  text-align: center;
  color: #6b7280;
  font-size: 14px;
  margin-top: 20px;
  padding: 20px;
  border: 1px dashed #d1d5db;
  border-radius: 8px;
  background: #f9fafb;
}

/* 地图选择器样式 */
.map-picker-wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.map-canvas {
  position: relative;
  width: 100%;
  /* keep 16:9 by limiting height relative to width in dialog */
  aspect-ratio: 16 / 9;
  background: #0b1220;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  overflow: hidden;
}

.map-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  user-select: none;
  -webkit-user-drag: none;
}

.map-marker {
  position: absolute;
  width: 10px;
  height: 10px;
  margin-left: -5px; /* center dot on point */
  margin-top: -5px;
  background: #ef4444;
  border: 2px solid #ffffff;
  border-radius: 50%;
  box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.map-marker.map-marker-hovered {
  background: #409eff !important;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.6) !important;
  transform: scale(1.3);
  z-index: 10;
}

.map-marker-numbered {
  width: 16px;
  height: 16px;
  margin-left: -8px;
  margin-top: -8px;
  background: #ef4444;
  border: 2px solid #ffffff;
  border-radius: 50%;
  box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.4);
  transition: all 0.2s ease;
}

.map-marker-hovered {
  background: #409eff !important;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.6) !important;
  transform: scale(1.3);
  z-index: 10;
}

.marker-number {
  color: #ffffff;
  font-size: 10px;
  font-weight: bold;
  line-height: 1;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
  user-select: none;
}

.map-info {
  font-size: 12px;
  color: #475569;
}

.map-info .hint {
  color: #64748b;
  margin-top: 4px;
}

/* 平板设备适配 */
@media (max-width: 1024px) and (min-width: 769px) {
  .main-content-grid {
    grid-template-columns: 1fr 1fr;
    min-width: auto;
  }

  .tab-pane {
    min-width: auto;
  }
}

@media (max-width: 768px) {
  .config-content {
    padding: 8px;
  }

  .tab-pane {
    padding: 12px;
    min-width: auto; /* 移动端移除最小宽度限制 */
    width: 100%;
  }

  .main-content-grid {
    grid-template-columns: 1fr;
    gap: 16px;
    min-width: auto; /* 移动端移除最小宽度限制 */
  }

  .grouped-content {
    grid-template-columns: 1fr;
  }

  .config-header {
    padding: 12px;
  }

  .header-content {
    flex-direction: column;
    gap: 16px;
  }

  .header-left {
    width: 100%;
  }

  .config-header h2 {
    font-size: 16px;
    margin-bottom: 12px;
  }

  .header-notice {
    max-width: none;
    width: 100%;
  }

  .header-controls {
    width: 100%;
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .config-name-group,
  .config-description-group {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    width: 100%;
  }

  .config-name-group label,
  .config-description-group label {
    font-size: 13px;
    white-space: nowrap;
  }

  .config-name-input,
  .config-description-input {
    width: 100%;
    box-sizing: border-box;
  }

  .save-btn {
    width: 100%;
    padding: 12px 20px;
    font-size: 15px;
  }

  .shared-config-notice {
    width: 100%;
    text-align: center;
    padding: 12px 16px;
    font-size: 13px;
    line-height: 1.4;
    box-sizing: border-box;
  }

  .anti-ban-content {
    padding: 10px 12px;
  }

  .anti-ban-text {
    font-size: 11px;
  }
}

/* 超小屏幕优化 */
@media (max-width: 480px) {
  .config-header {
    padding: 10px;
  }

  .config-header h2 {
    font-size: 15px;
  }

  .config-name-group label,
  .config-description-group label {
    font-size: 12px;
  }

  .config-name-input,
  .config-description-input {
    font-size: 13px;
    padding: 8px 10px;
  }

  .save-btn {
    font-size: 14px;
    padding: 10px 16px;
  }

  .shared-config-notice {
    font-size: 12px;
    padding: 10px 12px;
  }
}

/* 防封策略说明样式 */
.anti-ban-notice {
  margin: 12px 0 !important;
  padding: 0;
}

.anti-ban-content {
  display: flex;
  align-items: flex-start;
  padding: 12px 16px;
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(16, 185, 129, 0.02) 100%);
  border: 1px solid rgba(16, 185, 129, 0.2);
  border-radius: 8px;
  margin: 0;
}

.anti-ban-text {
  font-size: 12px;
  line-height: 1.5;
  color: #374151;
  flex: 1;
}

.anti-ban-text strong {
  color: #10b981;
  font-weight: 600;
}

/* 功能特色标签样式 */
.feature-badge {
  display: inline-block;
  font-size: 10px;
  font-weight: 500;
  color: #10b981;
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.2);
  border-radius: 4px;
  padding: 2px 6px;
  margin-left: 8px;
  vertical-align: middle;
}

/* 设置标签样式 */
.setting-label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  margin-right: 8px;
}

/* 谷歌登录提示样式 */
.google-login-tip {
  margin-top: 8px;
  padding: 0;
}

.tip-content {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 8px 12px;
  background: rgba(59, 130, 246, 0.05);
  border: 1px solid rgba(59, 130, 246, 0.15);
  border-radius: 6px;
  font-size: 12px;
  line-height: 1.4;
}

.tip-icon {
  flex-shrink: 0;
  font-size: 14px;
  margin-top: 1px;
}

.tip-text {
  color: #4b5563;
  flex: 1;
}

/* 紧凑型活动制作面板样式 */
.compact-craft-panel {
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
  margin-top: 10px;
}

.craft-rows {
  padding: 5px 0;
}

.craft-row {
  display: flex;
  align-items: flex-start;
  padding: 8px 12px;
  border-bottom: 1px dashed #ebeef5;
}

.craft-row:last-child {
  border-bottom: none;
}

.row-label {
  flex: 0 0 100px;
  padding-top: 2px;
}

.activity-tag {
  font-size: 12px;
  padding: 2px 8px;
  background: #f0f2f5;
  color: #909399;
  border-radius: 4px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;
  display: inline-block;
}

.activity-tag.is-active {
  background: #e6f7ff;
  color: #1890ff;
  border: 1px solid #91d5ff;
}

.row-content {
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding-left: 15px;
}

.product-tag {
  font-size: 12px;
  padding: 3px 10px;
  border: 1px solid #dcdfe6;
  border-radius: 3px;
  cursor: pointer;
  background: #fff;
  color: #606266;
  transition: all 0.2s;
  user-select: none;
}

.product-tag:hover {
  border-color: #409eff;
  color: #409eff;
}

.product-tag.is-checked {
  background: #409eff;
  border-color: #409eff;
  color: #fff;
}

.product-tag.has-menu {
  border-style: dotted;
}

.menu-prefix {
  opacity: 0.7;
  margin-right: 2px;
}
</style>
